import type { SVGProps, JSX } from 'react';
import { forwardRef } from 'react';
import styled from 'styled-components';

import { colors } from '../../theme/colors';

export type IconSize = 8 | 12 | 14 | 16 | 18 | 20 | 24 | 28 | 32 | 40;

interface Props {
  color?: string; // eslint-disable-line react/no-unused-prop-types
  display?: string; // eslint-disable-line react/no-unused-prop-types
  size?: IconSize; // eslint-disable-line react/no-unused-prop-types
  rotate?: number; // eslint-disable-line react/no-unused-prop-types
  backgroundColor?: string; // eslint-disable-line react/no-unused-prop-types
  foregroundColor?: string; // eslint-disable-line react/no-unused-prop-types
  primaryColor?: string; // eslint-disable-line react/no-unused-prop-types
  secondaryColor?: string; // eslint-disable-line react/no-unused-prop-types
  isFullLogo?: boolean; // eslint-disable-line react/no-unused-prop-types
  isActive?: boolean; // eslint-disable-line react/no-unused-prop-types
  testId?: string; // eslint-disable-line react/no-unused-prop-types
  disabled?: boolean; // eslint-disable-line react/no-unused-prop-types
  height?: number; // eslint-disable-line react/no-unused-prop-types
  className?: string; // eslint-disable-line react/no-unused-prop-types
}

interface WithSvgProps {
  strokeWidth?: number;
}

const StyledSVG = styled.svg<{ display?: string }>`
  display: ${({ display }) => display || 'block'};
`;

/* eslint-disable  max-len */

export function IconBack({ color = colors.brand[400], size = 32 }: Props): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 22 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4.5 10L0.5 6L4.5 2" stroke={color} strokeLinecap="round" strokeLinejoin="round" />
      <path d="M0.5 6H9.5" stroke={color} strokeLinecap="round" strokeLinejoin="round" />
      <path d="M11.5 11.5V0.5" stroke={color} strokeLinecap="round" strokeLinejoin="round" />
    </StyledSVG>
  );
}

export function IconRegenerate({ color = colors.steel['400'], size = 20 }: Props): JSX.Element {
  return (
    <StyledSVG
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size * 1.111}
      viewBox="0 0 18 20"
      fill="none"
      data-testid="icon-regenerate"
    >
      <path
        d="M17 18.0001V13.0001H12M1 2.00008V7.00008H6M16.938 9.00008C16.7287 7.33748 16.0026 5.78257 14.8623 4.55472C13.7219 3.32686 12.2248 2.48804 10.5822 2.1566C8.93957 1.82516 7.23434 2.01783 5.70709 2.70743C4.17983 3.39702 2.90764 4.54874 2.07 6.00008M1.062 11.0001C1.27133 12.6627 1.99739 14.2176 3.13774 15.4454C4.2781 16.6733 5.7752 17.5121 7.41782 17.8436C9.06043 18.175 10.7657 17.9823 12.2929 17.2927C13.8202 16.6031 15.0924 15.4514 15.93 14.0001"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </StyledSVG>
  );
}

export function IconRegenerateOff({ color = colors.steel['300'], size = 20 }: Props): JSX.Element {
  return (
    <StyledSVG
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size * 1.111}
      viewBox="0 0 18 20"
      fill="none"
      data-testid="icon-regenerate-off"
    >
      <path
        d="M17 18.0001V13.0001H12M1 2.00008V7.00008H6M16.938 9.00008C16.7287 7.33748 16.0026 5.78257 14.8623 4.55472C13.7219 3.32686 12.2248 2.48804 10.5822 2.1566C8.93957 1.82516 7.23434 2.01783 5.70709 2.70743C4.17983 3.39702 2.90764 4.54874 2.07 6.00008M1.062 11.0001C1.27133 12.6627 1.99739 14.2176 3.13774 15.4454C4.2781 16.6733 5.7752 17.5121 7.41782 17.8436C9.06043 18.175 10.7657 17.9823 12.2929 17.2927C13.8202 16.6031 15.0924 15.4514 15.93 14.0001"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line x1="1" y1="19" x2="17" y2="1" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </StyledSVG>
  );
}

export function IconArrow({ color, size = 24, rotate = 0 }: Props): JSX.Element {
  return (
    <StyledSVG
      width={size}
      height={size}
      viewBox="0 0 22 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ transform: `rotate(${rotate}deg)` }}
    >
      <path
        d="M0.293006 8.293L8.00001 0.585999L9.41401 2L3.41401 8H21C21.553 8 22 8.448 22 9C22 9.552 21.553 10 21 10H3.41401L9.41401 16L8.00001 17.414L0.293006 9.707C-0.0979941 9.316 -0.0979941 8.684 0.293006 8.293Z"
        fill={color || '#333333'}
      />
    </StyledSVG>
  );
}

export function IconExternalArrow({ color }: Props): JSX.Element {
  return (
    <StyledSVG width="12" height="12" viewBox="0 0 11 11" fill="none">
      <path
        d="M10 1L1 10M10 1H3.99971M10 1L9.9997 7"
        stroke={color || '#262D46'}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </StyledSVG>
  );
}

export function IconSwitch({ color = colors.brand[400], size = 16 }: Props): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M1 5.5H12.6L11.3 6.8C10.9 7.2 10.9 7.8 11.3 8.2C11.5 8.4 11.7 8.5 12 8.5C12.3 8.5 12.5 8.4 12.7 8.2L15.7 5.2C16.1 4.8 16.1 4.2 15.7 3.8L12.7 0.8C12.3 0.4 11.7 0.4 11.3 0.8C10.9 1.2 10.9 1.8 11.3 2.2L12.6 3.5H1C0.4 3.5 0 3.9 0 4.5C0 5.1 0.4 5.5 1 5.5Z"
        fill={color}
      />
      <path
        d="M15 11.5H3.4L4.7 10.2C5.1 9.8 5.1 9.2 4.7 8.8C4.3 8.4 3.7 8.4 3.3 8.8L0.3 11.8C-0.1 12.2 -0.1 12.8 0.3 13.2L3.3 16.2C3.5 16.4 3.7 16.5 4 16.5C4.3 16.5 4.5 16.4 4.7 16.2C5.1 15.8 5.1 15.2 4.7 14.8L3.4 13.5H15C15.6 13.5 16 13.1 16 12.5C16 11.9 15.6 11.5 15 11.5Z"
        fill={color}
      />
    </StyledSVG>
  );
}

export function IconNewfrontLogo({ isFullLogo, color = '#db0e63', height = 20 }: Props): JSX.Element {
  if (isFullLogo) {
    return (
      <StyledSVG xmlns="http://www.w3.org/2000/svg" width="143px" height="58px" viewBox="0 0 143 58">
        <path
          d="m54.5 7.8-2.9 10.3-3.1-10.3h-3.2l-3.1 10.3-2.9-10.3h-3.4l4.7 15h3.2l3.1-10 3.2 10h3.1l4.7-15zM25.1 7.9c-5.7 1.6-6.9 8.2-4.2 12 1.6 2.4 4.1 3.3 6.9 3.3 2.5 0 4.7-.8 6.3-2.8.1-.1.2-.3.3-.5l-2.7-1.6s-.3.4-.7.8c-1.1.9-2.4 1.2-3.8 1-2.3-.2-3.7-1.4-4.2-3.5h12.1v-1.4c0-4.5-4.1-8.9-10-7.3zM23 14.2c0-1.9 2-3.6 4.4-3.7 2.3-.1 4.3 1.6 4.4 3.7H23zM113.9 13.2c-.1-3.2-2-5.3-5-5.6-1.8-.2-3.4.2-4.7 1.5-.2.2-.3.4-.6.7v-2h-3.2v14.9h3.2v-6.6c0-.7 0-1.4.1-2.1.1-2.1 1.4-3.4 3.4-3.5 1.7-.2 3.1.7 3.6 2.2.1.5.2 1 .2 1.5v8.5h3.2v-7.2c-.1-.9-.1-1.5-.2-2.3zM67.4 1.6c-2.4.2-4.1 1.7-4.6 4.1-.2.7-.2 1.4-.2 2.2h-2.3V11h2.3v11.8h3.2V10.9h3.5V7.8h-3.5c-.1-.8 0-1.6.5-2.2.7-1 1.9-1 3-1V1.5c-.7 0-1.4 0-1.9.1zM75.7 10.1c-.1.1-.1.2-.1.2V7.8h-3.2v14.9h3.3V14.8c0-2 1.2-3.3 3.1-3.7.5-.1 1-.1 1.4-.2V7.6c-2 0-3.5.8-4.5 2.5zM123.7 19.7c-.7-.1-1.1-.5-1.2-1.2 0-.2-.1-.4-.1-.6v-7h3.7V7.8h-3.7V3.5l-3.2.9v3.4h-2.8V11h2.8v6.7c0 .9 0 1.2.1 1.8.2 1.7 1.2 2.7 2.9 3.1.5.1 1.3.2 1.6.2h2.7v-3c-.7 0-2.1 0-2.8-.1zM16.2 1.5h-3.3v14.8L2.6 1.5H-.1v21.3h3.3v-15l10.3 15h2.7zM81.4 15.5c0-4.4 3.4-7.9 7.8-7.9S97 11 97 15.4s-3.4 7.9-7.7 7.9c-4.4 0-7.8-3.4-7.9-7.8zm3.2-.1c0 2.7 2 4.7 4.6 4.8 2.6 0 4.6-2 4.6-4.7s-2-4.7-4.6-4.7c-2.6-.1-4.6 2-4.6 4.6zM120.2 28.1c-2.7 0-5.1 1.4-6.5 3.6l2.1 2.8c.6-1.9 2.4-3.2 4.4-3.2 2.6 0 4.6 2.1 4.6 4.6 0 2.6-2.1 4.6-4.6 4.6-1.6 0-3-.8-3.9-2.1l-5.5-7.3c-1.4-1.8-3.6-3-6.1-3-2.7 0-5.1 1.4-6.5 3.6l2.1 2.8c.6-1.9 2.4-3.2 4.4-3.2 1.6 0 3 .8 3.8 2 0 0 3.9 5.2 5.3 7.1 1.4 1.9 3.7 3.2 6.3 3.2 4.3 0 7.7-3.5 7.7-7.7s-3.3-7.8-7.6-7.8z"
          fill={color}
        />
        <path
          d="M104.8 40.4c-1.6 0-2.9-.8-3.8-2l-5.3-7C94.3 29.3 92 28 89.3 28c-4.3 0-7.7 3.5-7.7 7.7s3.5 7.7 7.7 7.7c2.7 0 5.1-1.4 6.5-3.6L93.7 37v.1c-.6 1.9-2.4 3.2-4.4 3.2-2.6 0-4.6-2.1-4.6-4.6 0-2.6 2.1-4.6 4.6-4.6 1.5 0 2.8.7 3.6 1.8l5.4 7.2c1.6 2.1 3.7 3.4 6.4 3.4s5.1-1.4 6.5-3.6l-2.1-2.8v.1c-.5 1.9-2.3 3.2-4.3 3.2z"
          fill={color}
        />
      </StyledSVG>
    );
  }

  return (
    <StyledSVG xmlns="http://www.w3.org/2000/svg" viewBox="100 35.942 60 17.278" width={height * 5} height={height}>
      <path
        d="M 116.067 39.813 C 114.284 39.813 112.718 40.746 111.82 42.155 L 113.174 43.99 C 113.174 43.99 113.177 43.981 113.178 43.976 C 113.568 42.741 114.714 41.847 116.067 41.847 C 117.741 41.847 119.098 43.216 119.098 44.905 C 119.098 46.593 117.741 47.962 116.067 47.962 C 115.017 47.962 114.091 47.423 113.547 46.605 L 109.962 41.789 C 109.039 40.587 107.595 39.813 105.972 39.813 C 104.184 39.813 102.615 40.751 101.718 42.165 L 103.079 43.988 C 103.079 43.988 103.082 43.98 103.083 43.976 C 103.473 42.741 104.619 41.847 105.972 41.847 C 107.018 41.847 107.94 42.382 108.485 43.195 C 108.485 43.195 111.057 46.646 111.969 47.878 C 112.882 49.109 114.379 49.997 116.067 49.997 C 118.855 49.997 121.114 47.717 121.114 44.905 C 121.114 42.093 118.855 39.813 116.067 39.813"
        id="Fill-10"
        fill={color}
      />
      <path
        d="M 106.298 48.12 C 105.277 48.12 104.375 47.594 103.825 46.79 L 100.393 42.044 C 99.486 40.641 97.945 39.717 96.197 39.717 C 93.408 39.717 91.147 42.068 91.147 44.967 C 91.147 47.866 93.408 50.217 96.197 50.217 C 97.985 50.217 99.555 49.251 100.452 47.794 L 99.09 45.916 C 99.09 45.916 99.084 45.936 99.081 45.946 C 98.685 47.207 97.544 48.12 96.197 48.12 C 94.523 48.12 93.164 46.708 93.164 44.967 C 93.164 43.226 94.523 41.814 96.197 41.814 C 97.164 41.814 98.025 42.285 98.58 43.018 C 98.58 43.018 101.129 46.53 102.099 47.883 C 103.117 49.301 104.547 50.217 106.298 50.217 C 108.083 50.217 109.65 49.254 110.548 47.802 L 109.193 45.909 C 109.193 45.909 109.186 45.933 109.182 45.945 C 108.786 47.207 107.645 48.12 106.298 48.12"
        id="Fill-11"
        fill={color}
      />
    </StyledSVG>
  );
}

export function NewfrontLogoPunch(): JSX.Element {
  return (
    <StyledSVG xmlns="http://www.w3.org/2000/svg" viewBox="0 0 259 48" width="259px" fill="none" height="48px">
      <path
        d="M1.72186 11.46H6.3407L25.1014 29.5176V11.46H30.1855V36.54H25.5307L6.80594 18.4824V36.54H1.72186V11.46Z"
        fill="#F028B8"
      />
      <path
        d="M34.8355 11.46H59.0017V15.7224H39.9196V21.8688H59.0017V26.1312H39.9196V32.2416H59.0017V36.54H34.8355V11.46Z"
        fill="#F028B8"
      />
      <path
        d="M61.9418 11.46H67.4551L74.1148 29.9472L80.4147 11.46H85.3933L91.6572 29.9472L98.3529 11.46H103.866L94.5926 36.54H88.8658L82.9232 18.876L76.9086 36.54H71.2154L61.9418 11.46Z"
        fill="#F028B8"
      />
      <path
        d="M197.569 11.46H202.188L220.946 29.5176V11.46H226.03V36.54H221.378L202.653 18.4824V36.54H197.569V11.46Z"
        fill="#F028B8"
      />
      <path d="M241.059 15.7224H229.961V11.46H257.278V15.7224H246.144V36.54H241.059V15.7224Z" fill="#F028B8" />
      <path
        d="M156.491 26.0232C159.945 25.4304 162.573 22.4208 162.573 18.7944C162.573 14.7432 159.29 11.4576 155.242 11.4576H135.622V36.5376H140.635V26.1288H151.263L158.278 36.5376H163.575L156.489 26.0208L156.491 26.0232ZM140.673 21.8664L140.637 15.7224H154.731C156.426 15.7224 157.801 17.0976 157.801 18.7944C157.801 20.4912 156.426 21.8664 154.731 21.8664H140.673Z"
        fill="#F028B8"
      />
      <path
        d="M106.806 11.46H130.975V15.7224H111.89V21.8688H130.975V26.1312H111.89V36.54H106.806V11.46Z"
        fill="#F028B8"
      />
      <path
        d="M165.223 24C165.223 16.3152 171.086 11.2176 179.95 11.2176C188.813 11.2176 194.677 16.3176 194.677 24C194.677 31.6824 188.78 36.7824 179.95 36.7824C171.12 36.7824 165.223 31.6824 165.223 24ZM189.722 24C189.722 18.6912 185.813 15.3744 179.95 15.3744C174.086 15.3744 170.177 18.6912 170.177 24C170.177 29.3088 174.086 32.592 179.95 32.592C185.813 32.592 189.722 29.3088 189.722 24Z"
        fill="#F028B8"
      />
    </StyledSVG>
  );
}

export function IconAviationMarineLogo({ color = '#231E60', height = 100 }: Props): JSX.Element {
  return (
    <StyledSVG
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 982.7 834.6"
      width={(982.7 / 834.6) * height}
      height={height}
    >
      <g>
        <g>
          <path
            fill={color}
            className="st0"
            d="M537.7,729.1L537.7,729.1c0-3.2,2-6.3,4.6-8c5.5-3.5,11.6-1.3,14.9,3.9c3.3,5.2,2.4,11.4-3,14.8
			c-2.8,1.7-6.2,2.2-9.1,0.9l-0.1,0l8.6,13.6l-1.8,1.1l-18-28.5l1.8-1.1L537.7,729.1z M541.5,735.3c2.6,4.2,7.5,5.6,11.8,2.8
			c4.2-2.6,4.8-8,2.2-12c-2.6-4.1-7.7-6.2-12.1-3.5C538.8,725.6,538.7,731,541.5,735.3z"
          />
          <path
            fill={color}
            className="st0"
            d="M579.9,710.5c3.4,5.3,1.8,12-3.5,15.3c-5.2,3.3-12,1.6-15.2-3.4c-3.3-5.2-1.8-12,3.4-15.3
			C569.9,703.8,576.6,705.2,579.9,710.5z M562.9,721.3c2.6,4.1,8.1,5.7,12.3,3c4.4-2.8,5.7-8.3,3-12.6c-2.8-4.4-8.3-5.6-12.7-2.8
			C561.2,711.5,560.3,717.1,562.9,721.3z"
          />
          <path
            fill={color}
            className="st0"
            d="M594.7,709.3l-3.6-19.7l16.3,11.7l-3.1-18.7l1.8-1.1l3.8,24.1L593.8,694l3.5,19.5l-20-13.8l1.8-1.1
			L594.7,709.3z"
          />
          <path
            fill={color}
            className="st0"
            d="M616.6,687.6c2.8,4.6,7.8,5.7,12,3c2.8-1.8,4-4.9,3.8-8.1l2-0.1c0.3,4.1-1.4,7.7-5,9.9
			c-5.3,3.4-11.4,1.2-14.6-3.9c-3.2-5-2.4-11.5,2.8-14.8c5.4-3.4,11.4-1.1,14.6,4.1L616.6,687.6z M629.5,677.2
			c-2.5-3-7.1-4.3-10.8-1.9c-3.7,2.3-4.9,7.1-3,10.7L629.5,677.2z"
          />
          <path
            fill={color}
            className="st0"
            d="M635.5,666.2L635.5,666.2c-0.3-2.1,0.3-4,2.2-5.2c0.7-0.4,1.4-0.7,2.2-0.8l0,2.1c-0.6,0-1,0.1-1.5,0.4
			c-3.9,2.5-0.6,7.2,1.1,9.9l5.8,9.2l-1.7,1.1l-11.3-18l1.8-1.1L635.5,666.2z"
          />
          <path
            fill={color}
            className="st0"
            d="M650.4,666.3c2.8,4.6,7.8,5.7,12,3c2.8-1.8,4-4.9,3.8-8.1l2-0.1c0.3,4.1-1.4,7.7-5,9.9
			c-5.3,3.4-11.4,1.2-14.6-3.9c-3.2-5-2.4-11.5,2.8-14.8c5.4-3.4,11.4-1.1,14.6,4.1L650.4,666.3z M663.2,655.9
			c-2.5-3-7.1-4.3-10.8-1.9c-3.7,2.3-4.9,7.1-3,10.7L663.2,655.9z"
          />
          <path
            fill={color}
            className="st0"
            d="M689.4,649.6L689.4,649.6c0,3.2-2,6.3-4.6,8c-5.4,3.4-11.5,1.4-14.9-3.9c-3.3-5.2-2.5-11.4,3-14.8
			c2.8-1.7,6.2-2.2,9.1-0.9l0.1,0l-10.9-17.2l1.8-1.1l20.2,32.1l-1.8,1.1L689.4,649.6z M685.6,643.4c-2.6-4.2-7.5-5.6-11.9-2.9
			c-4.1,2.6-4.6,8.1-2.2,12c2.6,4.1,7.7,6.1,12,3.4C688.2,653,688.4,647.7,685.6,643.4z"
          />
          <path
            fill={color}
            className="st0"
            d="M694.1,606.3l1.8-1.1l10.9,17.2l0.1,0c0.1-3.2,2-6.1,4.7-7.9c5.4-3.4,11.3-1.5,14.6,3.7
			c3.3,5.2,2.6,11.6-2.9,15.1c-2.6,1.7-6.4,2.2-9.1,0.7l-0.1,0l2,3.2l-1.8,1.1L694.1,606.3z M724.3,619.3c-2.5-3.9-7.6-5.8-11.8-3.2
			c-4.5,2.9-5,7.9-2.3,12.2c2.6,4.2,7.6,6.1,12,3.4C726.6,628.8,726.9,623.4,724.3,619.3z"
          />
          <path
            fill={color}
            className="st0"
            d="M723,607.7l1.8-1.2l17.9,11.4l-3.4-20.6l1.8-1.1l5.9,36.1l-1.8,1.1l-2.1-12.9L723,607.7z"
          />
          <path
            fill={color}
            className="st0"
            d="M753.5,578.4l6.3-4l1.6,2.5c0.8-3.6,2.6-6.3,5.4-8c3.2-2,6.3-2.6,9.3-1.7c2.6,0.7,5.1,3,7.6,6.9l10.6,16.8
			l-6.3,4l-9.6-15.3c-1.7-2.7-3.3-4.3-4.6-4.9c-1.4-0.6-3.1-0.3-5,0.9c-2.1,1.3-3.1,3-3.2,4.9c0,1.9,1.1,4.6,3.2,8l8.3,13.2l-6.3,4
			L753.5,578.4z"
          />
          <path
            fill={color}
            className="st0"
            d="M818.3,558.7l-19.6,12.4c1.6,2.1,3.4,3.5,5.6,4c2.1,0.5,4.2,0.1,6.2-1.2c1.6-1,2.6-2.2,3.2-3.6
			c0.5-1.4,0.8-3.5,0.8-6.3l7.2-0.4c0.1,1.9,0,3.7-0.3,5.3c-0.3,1.6-0.7,3-1.4,4.3c-0.6,1.3-1.5,2.5-2.5,3.6c-1,1.1-2.3,2-3.7,2.9
			c-4.1,2.6-8.2,3.3-12.3,2.3c-4.1-1.1-7.6-3.8-10.3-8.2c-2.7-4.3-3.7-8.6-3-12.8c0.8-4.2,3.1-7.5,7.1-10c4-2.5,8-3.3,12-2.2
			c3.9,1.1,7.3,3.9,10.2,8.4L818.3,558.7z M808.6,557.6c-3-2.8-6.2-3.2-9.6-1c-0.8,0.5-1.4,1.1-1.9,1.7s-0.9,1.4-1.2,2.2
			c-0.3,0.8-0.4,1.6-0.4,2.5c0,0.9,0.1,1.8,0.4,2.7L808.6,557.6z"
          />
          <path
            fill={color}
            className="st0"
            d="M817.1,538.3l17,11.1l-3.7-23l19.2,13.2l-2.8-20.1l7.1-4.5l4.2,38.4l-18.7-12.8l3.4,22.5L810,542.8
			L817.1,538.3z"
          />
          <path
            fill={color}
            className="st0"
            d="M869.6,513.4l13.5,21.5l-6.3,4l-13.5-21.5l-2.2,1.4l-3.7-5.9l2.2-1.4l-6.7-10.6c-2.2-3.4-3.1-6.3-2.8-8.4
			c0.4-3,2.2-5.5,5.3-7.5c1.1-0.7,2.7-1.3,4.9-1.7l4.1,6.5l-0.9,0.1c-1.8,0.2-3.1,0.5-4,1.1c-1.1,0.7-1.6,1.6-1.5,2.6
			c0.1,1,0.9,2.7,2.3,4.9l5.7,9l6.9-4.3l3.7,5.9L869.6,513.4z"
          />
          <path
            fill={color}
            className="st0"
            d="M875.3,501.6l6.3-4l1.5,2.4c0.4-1.9,0.9-3.4,1.5-4.4c0.6-1,1.6-2,2.8-2.8c1.7-1.1,3.8-1.6,6.3-1.7l0.8,7.6
			c-1.7-0.1-3.2,0.2-4.3,0.9c-3.4,2.2-3.5,5.9-0.2,11.1l8.9,14.2l-6.3,4L875.3,501.6z"
          />
          <path
            fill={color}
            className="st0"
            d="M902.3,503.4c-2.5-4-3.2-8.2-2.1-12.8c1.1-4.6,3.7-8.1,7.7-10.7c4.1-2.6,8.4-3.4,13-2.4
			c4.6,1,8.1,3.6,10.7,7.6c2.6,4.1,3.3,8.4,2.3,13c-1.1,4.5-3.7,8.1-7.9,10.7c-4.1,2.6-8.4,3.3-13,2.3
			C908.6,510.2,905,507.6,902.3,503.4z M908.9,499.4c1.7,2.7,3.8,4.4,6.3,5.1c2.5,0.7,5,0.2,7.5-1.3c2.5-1.6,3.9-3.6,4.4-6.1
			c0.5-2.5-0.1-5.1-1.8-7.8c-1.7-2.7-3.8-4.3-6.2-5c-2.5-0.7-5-0.2-7.5,1.3c-2.4,1.5-3.9,3.6-4.3,6.1
			C906.7,494.3,907.2,496.9,908.9,499.4z"
          />
          <path
            fill={color}
            className="st0"
            d="M928.5,468l6.3-4l1.6,2.5c0.8-3.6,2.6-6.3,5.4-8c3.2-2,6.3-2.6,9.3-1.7c2.6,0.7,5.1,3,7.6,6.9l10.6,16.8
			l-6.3,4l-9.6-15.3c-1.7-2.7-3.2-4.3-4.6-4.9c-1.4-0.6-3.1-0.3-5,0.9c-2.1,1.3-3.1,3-3.2,4.9c0,1.9,1.1,4.6,3.2,8l8.3,13.2l-6.3,4
			L928.5,468z"
          />
          <path
            fill={color}
            className="st0"
            d="M969.2,450.6l13.5,21.5l-6.3,4l-13.5-21.5l-2.7,1.7l-3.7-5.9l2.7-1.7l-6.3-10l6.3-4l6.3,10l4.9-3.1l3.7,5.9
			L969.2,450.6z"
          />
        </g>

        <rect
          fill={color}
          x="519.2"
          y="261.1"
          transform="matrix(0.8503 -0.5263 0.5263 0.8503 -65.1399 321.3755)"
          className="st0"
          width="26.5"
          height="28.1"
        />

        <rect
          fill={color}
          x="179.7"
          y="228.8"
          transform="matrix(0.8503 -0.5263 0.5263 0.8503 -98.9375 137.8835)"
          className="st0"
          width="26.5"
          height="28.1"
        />

        <rect
          fill={color}
          x="404.4"
          y="87.1"
          transform="matrix(0.8503 -0.5263 0.5263 0.8503 9.2827 234.925)"
          className="st0"
          width="26.5"
          height="28.1"
        />
        <g>
          <path
            fill={color}
            className="st0"
            d="M55,387l22.1-14l62.2,101.3l-22.1,14l-6.5-10.6c-3.5,14.7-10.5,25.4-20.9,32c-13.2,8.4-27.2,10.2-42,5.5
			c-14.9-4.9-27.1-15.1-36.6-30.6C1.9,469.4-1.6,454,0.7,438.5C3,423,10.6,411.2,23.4,403c11.1-7,24-8.5,38.8-4.3L55,387z
			 M33.7,470.3c6,9.7,13.3,16.1,21.9,19.1c8.8,3,17,2.1,24.7-2.7c8.1-5.2,12.9-12.3,14.3-21.5c1.2-9.3-1.1-18.8-6.9-28.3
			c-5.8-9.5-13.2-15.7-22-18.7c-8.7-2.8-17.1-1.7-25.1,3.4c-7.6,4.8-12,11.8-13.4,21.2C25.9,452,28.1,461.3,33.7,470.3z"
          />
          <path
            fill={color}
            className="st0"
            d="M118.5,346.8l59.6,41.3l-10.4-72.4l24.7-15.7l17,139.6L93.8,362.4L118.5,346.8z"
          />
          <path fill={color} className="st0" d="M229.3,276.6l62.3,101.3l-22,13.9l-62.3-101.2L229.3,276.6z" />
          <path
            fill={color}
            className="st0"
            d="M327.4,214.4l22.1-14l62.3,101.3l-22.1,14l-6.5-10.6c-3.5,14.8-10.5,25.4-20.9,32c-13.2,8.3-27.2,10.2-42,5.5
			c-14.9-4.9-27.1-15.1-36.6-30.6c-9.3-15.2-12.8-30.5-10.5-46.1s9.9-27.3,22.8-35.5c11.1-7,24-8.5,38.8-4.4L327.4,214.4z
			 M306.1,297.8c6,9.7,13.3,16.1,21.9,19.1c8.8,3,17,2.1,24.7-2.8c8.2-5.2,12.9-12.3,14.3-21.5c1.2-9.4-1.1-18.8-6.9-28.3
			c-5.8-9.5-13.2-15.7-22-18.7c-8.7-2.9-17.1-1.7-25.1,3.3c-7.6,4.8-12,11.9-13.4,21.2C298.4,279.5,300.5,288.8,306.1,297.8z"
          />
          <path
            fill={color}
            className="st0"
            d="M413.4,190.3l48.8,79.4l-22,13.9l-48.8-79.4l-9.4,5.9l-13.4-21.8l9.4-5.9l-22.8-37.1l22-13.9l22.8,37.1
			l17.1-10.9l13.4,21.8L413.4,190.3z"
          />
          <path fill={color} className="st0" d="M454.1,134.2l62.2,101.3l-22,13.9l-62.2-101.3L454.1,134.2z" />
          <path
            fill={color}
            className="st0"
            d="M508.1,169.3c-9-14.6-11.7-30.2-8.2-46.7c3.5-16.5,12.4-29.2,26.5-38.2c14.2-9,29.4-11.5,45.7-7.4
			c16.1,4.1,28.8,13.7,38.1,28.8c9.3,15.2,12.2,31,8.7,47.5c-3.7,16.5-12.7,29.2-27.1,38.4c-14.3,9-29.4,11.4-45.5,7.1
			C530.4,194.7,517.7,184.8,508.1,169.3z M530.9,155.5c6.2,10.1,13.7,16.5,22.4,19.2c8.9,2.7,17.6,1.3,26.1-4.1
			c8.6-5.4,13.7-12.8,15.2-21.9c1.5-9.2-0.7-18.7-6.8-28.5c-6-9.8-13.5-16.1-22.2-18.7c-8.9-2.7-17.6-1.3-26.1,4.1
			c-8.4,5.3-13.4,12.6-15,21.9C523,136.7,525.1,146.1,530.9,155.5z"
          />
          <path
            fill={color}
            className="st0"
            d="M602.4,40.3l22.1-14l5.7,9.3c2.7-13,8.9-22.5,18.5-28.6c11.1-7,22-8.8,32.7-5.3c9.2,2.9,18.2,11.5,27,25.8
			l38.2,62.1l-22.1,14L689.8,47c-6.1-10-11.7-16-16.6-18.2c-4.9-2.3-10.7-1.3-17.5,3c-7.3,4.6-10.9,10.5-10.8,17.5
			c0.1,7,4,16.7,11.8,29.4l30,48.9l-22.1,14L602.4,40.3z"
          />
        </g>
        <g>
          <path
            fill={color}
            className="st0"
            d="M189.8,546.9l22-13.9l5.7,9.4c1.3-7.4,3-12.9,5-16.4c2.1-3.8,5.6-7.2,10.4-10.2c10.8-6.8,22.3-7.2,34.7-1.2
			c0.8-14.4,7.1-25.3,18.9-32.7c21.5-13.6,40.7-6.5,57.7,21.1l38.5,62.6l-22.1,14l-34.6-56.2c-6-9.7-11.3-15.9-16-18.5
			c-4.8-2.6-9.8-2.3-15,1c-6.1,3.8-9,9.1-8.8,15.7c0.2,6.6,3.6,15.3,10.2,26l32.1,52.3l-22.1,14l-34.4-55.9
			c-11.1-18-21.5-23.9-31.3-17.7c-6.2,3.9-9.2,9.2-9,16c0.2,6.7,3.5,15.4,10.1,26l32.1,52.3l-22,13.9L189.8,546.9z"
          />
          <path
            fill={color}
            className="st0"
            d="M418.5,402.1l22.1-14l62.3,101.3l-22.1,14l-6.5-10.6c-3.5,14.8-10.5,25.4-20.9,32.1
			c-13.2,8.3-27.2,10.2-42,5.5c-14.9-4.9-27.1-15.1-36.6-30.6c-9.3-15.2-12.8-30.5-10.5-46.1c2.3-15.5,9.9-27.3,22.8-35.5
			c11.1-7,24-8.5,38.8-4.3L418.5,402.1z M397.2,485.5c6,9.7,13.3,16.1,21.9,19.1c8.8,3,17,2.1,24.7-2.8c8.2-5.2,12.9-12.3,14.3-21.5
			c1.2-9.3-1.1-18.8-6.9-28.3c-5.8-9.5-13.2-15.8-22-18.7c-8.7-2.8-17.1-1.7-25.1,3.4c-7.6,4.8-12,11.8-13.4,21.2
			C389.4,467.2,391.6,476.4,397.2,485.5z"
          />
          <path
            fill={color}
            className="st0"
            d="M468.4,370.5l22-13.9l5.6,9.1c1.3-7.1,3-12.4,5.1-16.1c2.1-3.7,5.4-7,9.7-9.8c5.8-3.7,13.1-5.5,21.9-5.5
			l3.1,27.8c-6.1-0.5-11.2,0.4-15.2,3c-12,7.6-12.1,21-0.2,40.3l32.3,52.5l-22,13.9L468.4,370.5z"
          />
          <path fill={color} className="st0" d="M568.4,307.2l62.2,101.3l-22,13.9l-62.2-101.3L568.4,307.2z" />
          <path
            fill={color}
            className="st0"
            d="M596.2,289.6l22.1-14l5.8,9.4c2.7-13,8.9-22.5,18.5-28.6c11.1-7,22-8.8,32.8-5.3c9.2,2.9,18.2,11.5,27,25.8
			l38.2,62.1l-22.1,14l-34.8-56.5c-6.1-10-11.7-16.1-16.6-18.2c-4.9-2.3-10.8-1.3-17.5,3c-7.3,4.6-10.9,10.5-10.8,17.5
			c0.1,6.9,4,16.7,11.8,29.4l30,48.9l-22.1,14L596.2,289.6z"
          />
          <path
            fill={color}
            className="st0"
            d="M827.3,221.2l-68.3,43.2c5.7,8,12.3,12.9,19.8,15c7.5,2,14.7,0.8,21.8-3.7c5.5-3.5,9.2-7.7,11-12.8
			c1.8-5,2.7-12.6,2.6-23l25.4-0.8c0.3,7,0,13.4-0.9,19.1c-0.9,5.7-2.5,11-4.6,15.6c-2.2,4.7-5,9-8.6,12.7
			c-3.6,3.8-7.8,7.3-12.8,10.4c-14.2,9-28.6,11.4-43.2,7.1c-14.6-4.3-26.9-14.5-36.8-30.7c-9.8-16-13.6-31.6-11.4-46.8
			c2.4-15.1,10.5-27,24.4-35.8c14-8.9,28-11.1,42-6.8c13.9,4.3,26,14.8,36.2,31.5L827.3,221.2z M793,216.4
			c-10.7-10.5-22-12.1-33.7-4.7c-2.7,1.7-4.9,3.7-6.7,6.1c-1.8,2.4-3.2,5-4.1,7.8c-0.9,2.8-1.4,5.9-1.3,9c0,3.2,0.6,6.4,1.7,9.8
			L793,216.4z"
          />
        </g>
        <g>
          <path
            fill={color}
            className="st0"
            d="M273.6,678.4c-0.9-1.5-1.2-3.2-0.9-4.9c0.4-1.8,1.3-3.1,2.7-4.1c1.5-0.9,3.1-1.2,4.8-0.7c1.7,0.4,3.1,1.4,4,3
			c1,1.6,1.3,3.3,0.9,5c-0.3,1.8-1.2,3.1-2.7,4.1c-1.5,0.9-3.1,1.2-4.8,0.7C275.9,681,274.6,680,273.6,678.4z M292.7,688.2
			l23.2,37.8l-8.2,5.2l-23.2-37.8L292.7,688.2z"
          />
          <path
            fill={color}
            className="st0"
            d="M303.1,681.6l8.3-5.2l2.1,3.5c1-4.9,3.3-8.4,6.9-10.7c4.1-2.6,8.2-3.3,12.2-2c3.4,1.1,6.8,4.3,10.1,9.6
			L357,700l-8.2,5.2l-13-21.1c-2.3-3.7-4.4-6-6.2-6.8c-1.8-0.9-4-0.5-6.5,1.1c-2.7,1.7-4.1,3.9-4,6.5c0,2.6,1.5,6.3,4.4,11
			l11.2,18.2l-8.3,5.2L303.1,681.6z"
          />
          <path
            fill={color}
            className="st0"
            d="M368.7,648.9l-4.4,8.1c-2.5-1.6-4.5-2-6.1-1c-0.8,0.5-1.2,1.2-1.4,2c-0.2,0.9,0,1.7,0.4,2.5
			c0.9,1.5,3.4,1.9,7.4,1.3c5.6-0.8,9.7-0.8,12.2-0.1c2.6,0.7,4.7,2.5,6.4,5.4c2.2,3.6,2.9,7.5,1.8,11.6c-1,3.9-3.3,6.9-6.8,9.1
			c-6,3.8-12.1,3.3-18.4-1.3l4.9-7.9c2.1,1.2,3.5,1.9,4.4,2c1.6,0.4,3.1,0.1,4.5-0.8c2.8-1.7,3.3-4,1.7-6.7c-1-1.6-2.9-2.3-5.9-2.3
			c-1.1,0.1-2.2,0.2-3.3,0.3c-1.1,0.1-2.2,0.2-3.3,0.3c-3.2,0.3-5.6,0.1-7.3-0.5c-2.2-0.8-4.1-2.4-5.6-4.9c-2-3.3-2.6-6.7-1.8-10.2
			c0.8-3.5,2.8-6.3,5.9-8.2C358.5,645,363.4,645.4,368.7,648.9z"
          />
          <path
            fill={color}
            className="st0"
            d="M382.7,631.2l13.3,21.7c3.8,6.2,8.1,7.9,12.7,5c4.6-2.9,5-7.5,1.2-13.8l-13.3-21.7l8.2-5.2l13.5,21.9
			c1.9,3,3.1,5.9,3.8,8.5c0.5,2.4,0.4,4.9-0.3,7.6c-1.2,4.3-3.9,7.8-8.2,10.6c-4.3,2.7-8.6,3.7-12.9,2.8c-2.7-0.5-5-1.5-6.9-3
			c-1.8-1.3-3.7-3.7-5.8-7.2l-13.5-21.9L382.7,631.2z"
          />
          <path
            fill={color}
            className="st0"
            d="M415.2,610.6l8.2-5.2l2.1,3.4c0.5-2.6,1.1-4.6,1.9-6c0.8-1.4,2-2.6,3.6-3.6c2.2-1.4,4.9-2.1,8.2-2.1l1.2,10.4
			c-2.3-0.2-4.2,0.2-5.7,1.1c-4.5,2.8-4.5,7.8-0.1,15l12.1,19.6l-8.2,5.2L415.2,610.6z"
          />
          <path
            fill={color}
            className="st0"
            d="M468.9,576.6l8.3-5.2l23.2,37.8l-8.2,5.2l-2.4-4c-1.3,5.5-3.9,9.5-7.8,11.9c-4.9,3.1-10.1,3.8-15.7,2.1
			c-5.6-1.8-10.1-5.6-13.7-11.4c-3.5-5.7-4.8-11.4-3.9-17.2c0.9-5.8,3.7-10.2,8.5-13.2c4.1-2.6,9-3.2,14.5-1.6L468.9,576.6z
			 M460.9,607.7c2.2,3.6,5,6,8.2,7.1c3.3,1.1,6.4,0.8,9.2-1c3-1.9,4.8-4.6,5.3-8c0.4-3.5-0.4-7-2.6-10.6c-2.2-3.5-4.9-5.9-8.2-7
			c-3.3-1.1-6.4-0.7-9.4,1.3c-2.8,1.8-4.5,4.4-5,7.9C458,600.9,458.8,604.3,460.9,607.7z"
          />
          <path
            fill={color}
            className="st0"
            d="M487.5,564.8l8.2-5.2l2.1,3.5c1-4.9,3.3-8.4,6.9-10.7c4.1-2.6,8.2-3.3,12.2-2c3.5,1.1,6.8,4.3,10.1,9.6
			l14.3,23.2l-8.3,5.2l-13-21.1c-2.3-3.7-4.4-6-6.2-6.8c-1.8-0.9-4-0.5-6.5,1.1c-2.7,1.7-4.1,3.9-4.1,6.5c0,2.6,1.5,6.3,4.4,11
			l11.2,18.2l-8.2,5.2L487.5,564.8z"
          />
          <path
            fill={color}
            className="st0"
            d="M557.2,522.9l7.1,11.6c-3.4-1.2-6.1-1.9-8.1-1.8c-2,0-4,0.6-6,1.9c-3.1,1.9-5,4.7-5.6,8.3
			c-0.6,3.6,0.1,7.1,2.3,10.6c2.2,3.5,4.9,5.8,8.3,6.9c3.4,1,6.7,0.5,9.7-1.4c2-1.2,3.4-2.8,4.4-4.7c0.9-1.8,1.6-4.6,2-8.4l7.1,11.5
			c-2.2,4-5,7-8.3,9.1c-5.5,3.5-11.3,4.5-17.3,3.1c-6-1.4-10.8-5-14.3-10.6c-3.5-5.7-4.5-11.6-3.2-17.9c1.4-6.3,4.8-11.1,10.2-14.6
			C549.1,524.3,553,523.1,557.2,522.9z"
          />
          <path
            fill={color}
            className="st0"
            d="M609.4,516.7l-25.5,16.1c2.1,3,4.6,4.8,7.4,5.6c2.8,0.8,5.5,0.3,8.1-1.4c2.1-1.3,3.4-2.9,4.1-4.7
			c0.7-1.9,1-4.8,1-8.6l9.5-0.3c0.1,2.6,0,5-0.3,7.2c-0.3,2.1-0.9,4.1-1.7,5.8c-0.8,1.7-1.9,3.4-3.2,4.8c-1.3,1.4-2.9,2.7-4.8,3.9
			c-5.3,3.4-10.7,4.3-16.1,2.7c-5.5-1.6-10-5.4-13.8-11.5c-3.7-6-5.1-11.8-4.2-17.5c0.9-5.6,3.9-10.1,9.1-13.4
			c5.2-3.3,10.5-4.2,15.7-2.5c5.2,1.6,9.7,5.5,13.5,11.7L609.4,516.7z M596.6,514.9c-4-3.9-8.2-4.5-12.6-1.7c-1,0.6-1.8,1.4-2.5,2.3
			c-0.7,0.9-1.2,1.8-1.5,2.9c-0.3,1.1-0.5,2.2-0.5,3.4c0,1.2,0.2,2.4,0.6,3.7L596.6,514.9z"
          />
          <path
            fill={color}
            className="st0"
            d="M653.2,468.8l-4.4,8.1c-2.5-1.6-4.5-2-6.1-1c-0.8,0.5-1.2,1.2-1.4,2s0,1.7,0.4,2.5c0.9,1.5,3.4,1.9,7.4,1.3
			c5.6-0.8,9.7-0.8,12.2-0.1c2.6,0.7,4.7,2.5,6.4,5.4c2.2,3.6,2.9,7.5,1.8,11.6c-1,3.9-3.3,6.9-6.8,9.1c-6,3.8-12.1,3.3-18.4-1.3
			l4.9-7.9c2.1,1.2,3.5,1.9,4.3,2c1.6,0.3,3.1,0.1,4.5-0.8c2.8-1.8,3.3-4,1.7-6.7c-1-1.6-2.9-2.3-5.9-2.3c-1.1,0.1-2.2,0.2-3.3,0.3
			c-1.1,0.1-2.2,0.2-3.3,0.3c-3.2,0.3-5.6,0.1-7.3-0.5c-2.2-0.8-4.1-2.4-5.6-4.9c-2-3.3-2.6-6.7-1.8-10.2c0.8-3.5,2.8-6.3,5.9-8.2
			C643,464.9,647.9,465.2,653.2,468.8z"
          />
          <path
            fill={color}
            className="st0"
            d="M704.2,456.7l-25.5,16.1c2.1,3,4.6,4.8,7.4,5.6c2.8,0.7,5.5,0.3,8.1-1.4c2.1-1.3,3.4-2.9,4.1-4.7
			c0.7-1.9,1-4.7,1-8.6l9.5-0.3c0.1,2.6,0,5-0.3,7.1c-0.4,2.1-0.9,4.1-1.7,5.9c-0.8,1.7-1.9,3.3-3.2,4.7c-1.3,1.4-2.9,2.7-4.8,3.9
			c-5.3,3.4-10.7,4.2-16.1,2.6c-5.5-1.6-10-5.4-13.7-11.5c-3.7-6-5.1-11.8-4.3-17.5c0.9-5.6,3.9-10.1,9.1-13.4
			c5.2-3.3,10.4-4.2,15.7-2.5c5.2,1.6,9.7,5.5,13.5,11.8L704.2,456.7z M691.4,454.9c-4-3.9-8.2-4.5-12.6-1.7c-1,0.6-1.8,1.4-2.5,2.3
			c-0.7,0.9-1.2,1.8-1.5,2.9c-0.4,1.1-0.5,2.2-0.5,3.4c0,1.2,0.2,2.4,0.6,3.7L691.4,454.9z"
          />
          <path
            fill={color}
            className="st0"
            d="M700.3,430l8.2-5.2l2.1,3.4c0.5-2.6,1.1-4.6,1.9-6c0.8-1.4,2-2.6,3.6-3.6c2.2-1.4,4.9-2.1,8.2-2.1l1.2,10.4
			c-2.3-0.2-4.2,0.2-5.7,1.1c-4.5,2.8-4.5,7.8-0.1,15l12.1,19.6l-8.2,5.2L700.3,430z"
          />
          <path
            fill={color}
            className="st0"
            d="M735.9,407.5l22.3,15.4l-3.9-27l9.2-5.9l6.4,52.1l-43.2-28.8L735.9,407.5z"
          />
          <path
            fill={color}
            className="st0"
            d="M758.1,371.5c-0.9-1.5-1.2-3.2-0.9-4.9c0.4-1.8,1.3-3.1,2.7-4c1.5-0.9,3.1-1.2,4.8-0.7c1.7,0.4,3.1,1.4,4,3
			s1.3,3.3,0.9,5c-0.3,1.8-1.3,3.1-2.7,4.1c-1.5,0.9-3.1,1.2-4.8,0.7C760.5,374.2,759.1,373.1,758.1,371.5z M777.3,381.3l23.2,37.8
			l-8.2,5.2L769,386.5L777.3,381.3z"
          />
          <path
            fill={color}
            className="st0"
            d="M816.4,358.8l7.1,11.6c-3.4-1.2-6.1-1.9-8.1-1.8c-2,0-4,0.6-6,1.8c-3.1,2-4.9,4.8-5.6,8.3
			c-0.6,3.6,0.1,7.1,2.3,10.6c2.2,3.6,4.9,5.8,8.3,6.9c3.4,1,6.7,0.5,9.7-1.4c2-1.2,3.4-2.8,4.4-4.7c0.9-1.8,1.6-4.6,2-8.4l7.1,11.5
			c-2.2,4-5,7-8.3,9.1c-5.5,3.5-11.3,4.5-17.3,3.1c-6-1.4-10.8-5-14.3-10.7c-3.5-5.6-4.5-11.6-3.2-17.8c1.4-6.3,4.8-11.1,10.2-14.6
			C808.3,360.2,812.1,359,816.4,358.8z"
          />
          <path
            fill={color}
            className="st0"
            d="M868.6,352.6l-25.5,16.1c2.1,3,4.6,4.8,7.4,5.6c2.8,0.8,5.5,0.3,8.1-1.4c2-1.3,3.4-2.9,4.1-4.8
			c0.7-1.9,1-4.7,1-8.6l9.5-0.3c0.1,2.6,0,5-0.3,7.1c-0.4,2.1-0.9,4.1-1.7,5.9c-0.8,1.7-1.9,3.4-3.2,4.8c-1.3,1.4-2.9,2.7-4.8,3.9
			c-5.3,3.4-10.7,4.2-16.1,2.7c-5.5-1.6-10-5.4-13.7-11.5c-3.7-6-5.1-11.8-4.3-17.5c0.9-5.6,3.9-10.1,9.1-13.4
			c5.2-3.3,10.5-4.2,15.7-2.5c5.2,1.6,9.7,5.5,13.5,11.7L868.6,352.6z M855.8,350.8c-4-3.9-8.2-4.5-12.6-1.7c-1,0.6-1.8,1.4-2.5,2.3
			c-0.7,0.9-1.2,1.8-1.5,2.9c-0.3,1.1-0.5,2.2-0.5,3.4c0,1.2,0.2,2.4,0.6,3.7L855.8,350.8z"
          />
          <path
            fill={color}
            className="st0"
            d="M889.3,319.3l-4.4,8.1c-2.5-1.7-4.5-2-6.1-1c-0.8,0.5-1.2,1.1-1.4,2c-0.2,0.9-0.1,1.7,0.4,2.5
			c0.9,1.5,3.4,1.9,7.4,1.3c5.6-0.8,9.6-0.8,12.2-0.1c2.5,0.7,4.7,2.5,6.4,5.4c2.2,3.6,2.9,7.5,1.8,11.6c-1,3.9-3.3,6.9-6.8,9.1
			c-6,3.8-12.1,3.4-18.4-1.3l4.9-7.9c2.1,1.2,3.5,1.9,4.3,2c1.6,0.4,3.1,0.1,4.5-0.8c2.8-1.8,3.3-4,1.7-6.7c-1-1.6-2.9-2.3-5.9-2.3
			c-1.1,0.1-2.2,0.2-3.3,0.3c-1.1,0.1-2.2,0.2-3.3,0.3c-3.2,0.3-5.6,0.1-7.3-0.5c-2.2-0.8-4.1-2.4-5.6-4.9c-2-3.3-2.6-6.7-1.8-10.2
			c0.9-3.5,2.8-6.3,5.9-8.2C879.1,315.3,884,315.7,889.3,319.3z"
          />
        </g>
        <g>
          <rect
            fill={color}
            x="166.5"
            y="603.1"
            transform="matrix(0.8459 -0.5333 0.5333 0.8459 -232.2782 408.1846)"
            className="st0"
            width="847.5"
            height="5.9"
          />
        </g>
      </g>
    </StyledSVG>
  );
}

export function IconArchivePaperCheck({ backgroundColor, foregroundColor }: Props): JSX.Element {
  return (
    <StyledSVG width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M29 7V22C29 22.2652 28.8946 22.5196 28.7071 22.7071C28.5196 22.8946 28.2652 23 28 23H21V26H11V23H4C3.73478 23 3.48043 22.8946 3.29289 22.7071C3.10536 22.5196 3 22.2652 3 22V7H1V27C1 28.0609 1.42143 29.0783 2.17157 29.8284C2.92172 30.5786 3.93913 31 5 31H27C28.0609 31 29.0783 30.5786 29.8284 29.8284C30.5786 29.0783 31 28.0609 31 27V7H29Z"
        fill={backgroundColor || '#D47239'}
      />
      <path
        d="M26 1H6C5.73478 1 5.48043 1.10536 5.29289 1.29289C5.10536 1.48043 5 1.73478 5 2V20C5 20.2652 5.10536 20.5196 5.29289 20.7071C5.48043 20.8946 5.73478 21 6 21H26C26.2652 21 26.5196 20.8946 26.7071 20.7071C26.8946 20.5196 27 20.2652 27 20V2C27 1.73478 26.8946 1.48043 26.7071 1.29289C26.5196 1.10536 26.2652 1 26 1ZM14.707 15.707C14.5195 15.8945 14.2652 15.9998 14 15.9998C13.7348 15.9998 13.4805 15.8945 13.293 15.707L9.586 12L11 10.586L14 13.586L21 6.586L22.414 8L14.707 15.707Z"
        fill={foregroundColor || '#FFAB7A'}
      />
    </StyledSVG>
  );
}

export function IconCaret({ color = colors.brand[400], rotate = 0, display, size }: Props): JSX.Element {
  return (
    <StyledSVG width={size ?? 9} viewBox="0 0 9 7" fill="none" display={display}>
      <path
        d="M8.07118 1.53553L4.53564 5.07107L1.00011 1.53553"
        style={{
          transform: `rotate(${rotate}deg)`,
          transformOrigin: 'center',
        }}
        stroke={color}
        strokeWidth="2"
      />
    </StyledSVG>
  );
}

export function IconCaretSolid({ color = colors.steel[400], rotate = 0 }: Props): JSX.Element {
  return (
    <StyledSVG width="8" height="7" viewBox="0 0 8 7" fill="none">
      <path
        d="M7.94135 0.855374C7.97571 0.805326 7.99575 0.746845 7.9993 0.686242C8.00286 0.625638 7.9898 0.565214 7.96153 0.51149C7.93326 0.457765 7.89086 0.412781 7.8389 0.381391C7.78694 0.350002 7.72739 0.333399 7.66668 0.333374L0.333346 0.333374C0.272614 0.333486 0.213066 0.350187 0.161134 0.381676C0.109202 0.413164 0.0668592 0.458242 0.0386796 0.512041C0.0132146 0.559612 -6.96182e-05 0.61275 1.33514e-05 0.666707C5.29289e-05 0.73408 0.0205073 0.799859 0.0586801 0.855374L3.72535 6.18871C3.75598 6.23326 3.79699 6.26969 3.84484 6.29486C3.89269 6.32003 3.94595 6.33318 4.00001 6.33318C4.05408 6.33318 4.10733 6.32003 4.15519 6.29486C4.20304 6.26969 4.24405 6.23326 4.27468 6.18871L7.94135 0.855374Z"
        fill={color}
        style={{
          transform: `rotate(${rotate}deg)`,
          transformOrigin: 'center',
        }}
      />
    </StyledSVG>
  );
}

export function IconIsoscelesCaretSolid({ color = colors.steel[400], rotate = 0 }: Props): JSX.Element {
  return (
    <StyledSVG width="10" height="5" viewBox="0 0 10 5" fill="none">
      <path
        d="M0.833252 0.333496L4.99992 4.50016L9.16658 0.333496H0.833252Z"
        fill={color}
        style={{
          transform: `rotate(${rotate}deg)`,
          transformOrigin: 'center center',
        }}
      />
    </StyledSVG>
  );
}

export function IconArrowDropUp({ color = 'currentColor', size = 24 }: Props): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7 14L12 9L17 14H7Z" fill={color} />
    </StyledSVG>
  );
}

export function IconArrowDropDown({ color = 'currentColor', size = 24 }: Props): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7 10L12 15L17 10H7Z" fill={color} />
    </StyledSVG>
  );
}

export function IconClock({ color, size = 16 }: Props): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 16 16">
      <path
        d="M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM8 14C4.7 14 2 11.3 2 8C2 4.7 4.7 2 8 2C11.3 2 14 4.7 14 8C14 11.3 11.3 14 8 14Z"
        fill={color || '#333333'}
      />
      <path d="M9 4H7V9H12V7H9V4Z" fill={color || '#333333'} />
    </StyledSVG>
  );
}

export function IconFileContent({ color }: Props): JSX.Element {
  return (
    <StyledSVG x="0px" y="0px" width="16px" height="16px" viewBox="0 0 16 16">
      <g transform="translate(0, 0)">
        <line
          fill="none"
          stroke={color || colors.brand[400]}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          x1="4.5"
          y1="11.5"
          x2="11.5"
          y2="11.5"
          data-color="color-2"
        />
        <line
          fill="none"
          stroke={color || colors.brand[400]}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          x1="4.5"
          y1="8.5"
          x2="11.5"
          y2="8.5"
          data-color="color-2"
        />
        <line
          fill="none"
          stroke={color || colors.brand[400]}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          x1="4.5"
          y1="5.5"
          x2="6.5"
          y2="5.5"
          data-color="color-2"
        />
        <polygon
          fill="none"
          stroke={color || colors.brand[400]}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          points="9.5,0.5
    1.5,0.5 1.5,15.5 14.5,15.5 14.5,5.5 "
        />
        <polyline
          fill="none"
          stroke={color || colors.brand[400]}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          points="9.5,0.5
    9.5,5.5 14.5,5.5 "
        />
        <path
          fill="none"
          stroke={color || colors.brand[400]}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          points="9.5,0.5
    1.5,0.5 1.5,15.5 14.5,15.5 14.5,5.5 "
        />
      </g>
    </StyledSVG>
  );
}

export function IconFileAdd({ color }: Props): JSX.Element {
  return (
    <StyledSVG x="0px" y="0px" width="16px" height="16px" viewBox="0 0 16 16">
      <g transform="translate(0, 0)">
        <polyline
          fill="none"
          stroke={color || colors.brand[400]}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          points="6.5,15.5
    0.5,15.5 0.5,0.5 13.5,0.5 13.5,5.5 "
        />
        <line
          fill="none"
          stroke={color || colors.brand[400]}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          x1="3.5"
          y1="3.5"
          x2="10.5"
          y2="3.5"
        />
        <line
          fill="none"
          stroke={color || colors.brand[400]}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          x1="3.5"
          y1="6.5"
          x2="7.5"
          y2="6.5"
        />
        <line
          fill="none"
          stroke={color || colors.brand[400]}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          x1="3.5"
          y1="9.5"
          x2="5.5"
          y2="9.5"
        />
        <line
          fill="none"
          stroke={color || colors.brand[400]}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          x1="3.5"
          y1="12.5"
          x2="5.5"
          y2="12.5"
        />
        <circle
          fill="none"
          stroke={color || colors.brand[400]}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          cx="11.5"
          cy="11.5"
          r="4"
          data-color="color-2"
        />
        <line
          fill="none"
          stroke={color || colors.brand[400]}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          x1="11.5"
          y1="9.5"
          x2="11.5"
          y2="13.5"
          data-color="color-2"
        />
        <line
          fill="none"
          stroke={color || colors.brand[400]}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          x1="9.5"
          y1="11.5"
          x2="13.5"
          y2="11.5"
          data-color="color-2"
        />
      </g>
    </StyledSVG>
  );
}

export function IconUnlink(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...props}>
      <path
        d="M12.39 9.06481L14 10.6748V9.06481H12.39ZM15 5.06481H11V6.9648H15C16.71 6.9648 18.1 8.3548 18.1 10.0648C18.1 11.3348 17.33 12.4348 16.23 12.9048L17.63 14.3048C18.3538 13.8587 18.9514 13.235 19.3662 12.4929C19.7811 11.7508 19.9992 10.915 20 10.0648C20 7.3048 17.76 5.06481 15 5.06481ZM0 2.3348L3.11 5.44481C2.19119 5.81865 1.40462 6.45781 0.850686 7.28069C0.296755 8.10357 0.000591825 9.07285 0 10.0648C0 12.8248 2.24 15.0648 5 15.0648H9V13.1648H5C3.29 13.1648 1.9 11.7748 1.9 10.0648C1.9 8.4748 3.11 7.16481 4.66 6.99481L6.73 9.06481H6V11.0648H8.73L11 13.3348V15.0648H12.73L16.74 19.0748L18.15 17.6648L1.41 0.924805L0 2.3348Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function IconTick({ color, size }: Props): JSX.Element {
  return (
    <StyledSVG width={size || 14} height={size || 9} viewBox="0 0 12 9" fill="none">
      <path
        d="M10.2929 0.293041L3.99992 6.58604L1.70692 4.29304C1.51832 4.11088 1.26571 4.01009 1.00352 4.01237C0.741321 4.01465 0.490508 4.11981 0.3051 4.30522C0.119692 4.49063 0.0145233 4.74144 0.0122448 5.00364C0.00996641 5.26584 0.110761 5.51844 0.292919 5.70704L3.29292 8.70704C3.48045 8.89451 3.73475 8.99983 3.99992 8.99983C4.26508 8.99983 4.51939 8.89451 4.70692 8.70704L11.7069 1.70704C11.8891 1.51844 11.9899 1.26584 11.9876 1.00364C11.9853 0.741443 11.8801 0.49063 11.6947 0.305222C11.5093 0.119814 11.2585 0.0146453 10.9963 0.0123669C10.7341 0.0100885 10.4815 0.110883 10.2929 0.293041Z"
        fill={color || 'white'}
      />
    </StyledSVG>
  );
}

export function IconDoubleTick({ color, size }: Props): JSX.Element {
  return (
    <StyledSVG width={size || 24} height={size || 16} viewBox="0 0 401 329" fill="none">
      <path
        d="M255.654495,47.857735
        C253.610413,46.216785 251.566345,44.575840 249.190552,42.668587
        C201.419891,102.108444 153.916168,161.216171 105.802582,221.082718
        C84.692802,199.868927 64.171631,179.246613 44.306877,159.283981
        C36.498695,167.164825 28.834900,174.899948 22.006495,181.791901
        C50.878536,210.711853 80.225372,240.107376 109.780037,269.711090
        C164.684708,201.373917 219.919525,132.625839 275.323364,63.667389
        C268.659302,58.300323 262.441193,53.292389 255.654495,47.857735
      M353.804077,96.320030
        C362.487091,85.470444 371.170135,74.620857 379.976624,63.617035
        C370.994873,56.416962 362.583740,49.674320 353.847198,42.670818
        C306.090546,102.089958 258.587524,161.193527 210.350983,221.209763
        C203.678436,214.247391 197.610779,207.916153 191.203201,201.230255
        C184.107117,210.069580 177.256149,218.603592 171.156403,226.201828
        C185.765137,240.911942 199.997864,255.243423 214.395203,269.740662
        C260.500061,212.375107 306.920135,154.617386 353.804077,96.320030
      z"
        fill={color || 'white'}
      />
    </StyledSVG>
  );
}

export function IconCheckActive({ color = colors.grass[500], size = 40, testId }: Props): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 32 32" fill="none" data-testid={testId}>
      <circle cx="16" cy="16" r="16" fill={color} />
      <path
        d="M20.2929 12.293L13.9999 18.586L11.7069 16.293C11.5183 16.1109 11.2657 16.0101 11.0035 16.0124C10.7413 16.0146 10.4905 16.1198 10.3051 16.3052C10.1197 16.4906 10.0145 16.7414 10.0122 17.0036C10.01 17.2658 10.1108 17.5184 10.2929 17.707L13.2929 20.707C13.4804 20.8945 13.7348 20.9998 13.9999 20.9998C14.2651 20.9998 14.5194 20.8945 14.7069 20.707L21.7069 13.707C21.8891 13.5184 21.9899 13.2658 21.9876 13.0036C21.9853 12.7414 21.8801 12.4906 21.6947 12.3052C21.5093 12.1198 21.2585 12.0146 20.9963 12.0124C20.7341 12.0101 20.4815 12.1109 20.2929 12.293Z"
        fill="#FFFFFF"
      />
    </StyledSVG>
  );
}

export function IconRadioButton({ isActive, disabled }: Props): JSX.Element {
  if (disabled) {
    return <IconRadioButtonDisabled />;
  }
  return isActive ? <IconRadioButtonActive /> : <IconRadioButtonInactive />;
}

export function IconRadioButtonRebrand({ isActive, disabled }: Props): JSX.Element {
  if (disabled) {
    return <IconRadioButtonDisabled />;
  }
  return isActive ? <IconRadioButtonRebrandActive /> : <IconRadioButtonInactive />;
}

export function IconRadioButtonRebrandActive(): JSX.Element {
  return (
    <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="9.5" cy="9.5" r="9" fill="#152023" />
      <g clipPath="url(#clip0_1897_10014)">
        <path
          d="M7.76972 13.3701L4.25972 9.86012C4.04884 9.64925 4.04884 9.30734 4.25972 9.09644L5.02338 8.33277C5.23425 8.12187 5.57618 8.12187 5.78705 8.33277L8.15156 10.6972L13.2161 5.63277C13.4269 5.42189 13.7689 5.42189 13.9797 5.63277L14.7434 6.39644C14.9543 6.60732 14.9543 6.94923 14.7434 7.16012L8.5334 13.3701C8.3225 13.581 7.98059 13.581 7.76972 13.3701Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_1897_10014">
          <rect width="10.8" height="10.8" fill="white" transform="translate(4.10156 4.10156)" />
        </clipPath>
      </defs>
    </svg>
  );
}

export function IconRadioButtonActive(): JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20">
      <circle cx="10" cy="10" r="10" fill={colors.brand[400]} />
      <g clipPath="url(#clip0)">
        <path
          d="M8.07574 14.2985L4.17574 10.3985C3.94144 10.1642 3.94144 9.78431 4.17574 9.54999L5.02425 8.70145C5.25855 8.46713 5.63847 8.46713 5.87278 8.70145L8.50001 11.3287L14.1272 5.70145C14.3615 5.46715 14.7415 5.46715 14.9758 5.70145L15.8243 6.54999C16.0586 6.78429 16.0586 7.16419 15.8243 7.39852L8.92427 14.2985C8.68994 14.5328 8.31004 14.5328 8.07574 14.2985V14.2985Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0">
          <rect width="12" height="12" fill="white" transform="translate(4 4)" />
        </clipPath>
      </defs>
    </svg>
  );
}

export function IconRadioButtonInactive(): JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20">
      <circle cx="10" cy="10" r="9.5" fill="white" stroke="#DBDBDB" />
    </svg>
  );
}

function IconRadioButtonDisabled(): JSX.Element {
  return (
    <div style={{ cursor: 'not-allowed' }}>
      <IconRadioButtonInactive />
    </div>
  );
}

export function IconLightning({ color = '#1CB779' }: Props): JSX.Element {
  return (
    <StyledSVG width="9" height="13" viewBox="0 0 9 13" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M0.874876 12.25C0.817199 12.25 0.759236 12.2331 0.708818 12.198C0.589617 12.1154 0.549312 11.9576 0.614111 11.8279L3.31972 6.41667H0.291685C0.192705 6.41667 0.100565 6.36654 0.046732 6.28337C-0.00695797 6.20019 -0.0149352 6.09552 0.0256532 6.00537L2.65065 0.17204C2.69779 0.0673633 2.8019 0 2.91669 0H6.70835C6.80947 0 6.90332 0.0524096 6.95644 0.138285C7.00956 0.224303 7.0144 0.331686 6.96926 0.422117L5.43032 3.5H8.45835C8.57442 3.5 8.67938 3.56879 8.72566 3.67503C8.77209 3.78141 8.75101 3.90517 8.67212 3.9902L1.08878 12.1569C1.03168 12.2182 0.953632 12.25 0.874876 12.25Z"
        fill={color}
      />
    </StyledSVG>
  );
}

export function IconSearch({ color = colors.steel[300], testId }: Props): JSX.Element {
  return (
    <StyledSVG
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      data-testid={testId}
      aria-label="Search"
      role="img"
    >
      <path
        xmlns="http://www.w3.org/2000/svg"
        d="M12.7 11.3C13.6 10.1 14.1 8.7 14.1 7.1C14.1 3.2 11 0 7.1 0C3.2 0 0 3.2 0 7.1C0 11 3.2 14.2 7.1 14.2C8.7 14.2 10.2 13.7 11.3 12.8L14.3 15.8C14.5 16 14.8 16.1 15 16.1C15.2 16.1 15.5 16 15.7 15.8C16.1 15.4 16.1 14.8 15.7 14.4L12.7 11.3ZM7.1 12.1C4.3 12.1 2 9.9 2 7.1C2 4.3 4.3 2 7.1 2C9.9 2 12.2 4.3 12.2 7.1C12.2 9.9 9.9 12.1 7.1 12.1Z"
        fill={color}
      />
    </StyledSVG>
  );
}

export function IconLongSearch({ color = 'currentColor' }: Props): JSX.Element {
  return (
    <StyledSVG width="18" height="17" viewBox="0 0 18 17" fill="none">
      <path
        d="M15.677 16.6068L9.96198 10.8908C7.41965 12.6982 3.91642 12.2561 1.90285 9.8737C-0.110711 7.49129 0.0371394 3.96336 2.24298 1.75778C4.44824 -0.448778 7.97651 -0.597268 10.3594 1.41619C12.7422 3.42965 13.1846 6.93322 11.377 9.47578L17.092 15.1918L15.678 16.6058L15.677 16.6068ZM6.48498 1.99976C4.58868 1.99934 2.95267 3.33045 2.56745 5.18721C2.18224 7.04397 3.15369 8.91605 4.89366 9.67001C6.63362 10.424 8.66388 9.85261 9.75522 8.30183C10.8466 6.75105 10.699 4.64709 9.40198 3.26378L10.007 3.86378L9.32498 3.18378L9.31298 3.17178C8.56477 2.41895 7.54637 1.9969 6.48498 1.99976Z"
        fill={color}
      />
    </StyledSVG>
  );
}

export function IconSpreadsheet({ testId }: Props): JSX.Element {
  return (
    <StyledSVG width="20" height="24" viewBox="0 0 20 24" fill="none" data-testid={testId}>
      <rect width="20" height="24" rx="1" fill="#1CB779" />
      <rect x="3" y="9" width="6" height="2" fill="white" />
      <rect x="3" y="12" width="6" height="2" fill="white" />
      <rect x="3" y="15" width="6" height="2" fill="white" />
      <rect x="11" y="9" width="6" height="2" fill="white" />
      <rect x="11" y="12" width="6" height="2" fill="white" />
      <rect x="11" y="15" width="6" height="2" fill="white" />
    </StyledSVG>
  );
}

export function IconPrint({ color = '#fff', size = 16, testId }: Props): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 17 17" fill="none" data-testid={testId}>
      <path d="M4 4V1H13V4" stroke={color} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M4 13H1V9C1 8.20435 1.31607 7.44129 1.87868 6.87868C2.44129 6.31607 3.20435 6 4 6H13C13.7956 6 14.5587 6.31607 15.1213 6.87868C15.6839 7.44129 16 8.20435 16 9V13H13"
        stroke={color}
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M13 10H4V16H13V10Z" stroke={color} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
    </StyledSVG>
  );
}

export function IconCircleTick({ color = colors.grass[500], size = 16, testId }: Props): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 18 18" fill="none" data-testid={testId}>
      <path
        d="M16.9824 9C16.9824 13.4192 13.4037 17 8.99121 17C4.57871 17 1 13.4192 1 9C1 4.5808 4.57871 1 8.99121 1C13.4037 1 16.9824 4.5808 16.9824 9Z"
        stroke={color}
        strokeWidth="2"
      />
      <path d="M6.29395 9.2885L8.41355 11.6992L12.1382 7.19922" stroke={color} strokeWidth="2" strokeLinecap="square" />
    </StyledSVG>
  );
}

export function IconArrowClock({ color = colors.brand[400], size = 16, testId }: Props): JSX.Element {
  return (
    <StyledSVG height={size} viewBox="0 0 21 18" fill="none" data-testid={testId}>
      <path
        d="M12 0C7.03 0 3 4.03 3 9H0L3.89 12.89L3.96 13.03L8 9H5C5 5.13 8.13 2 12 2C15.87 2 19 5.13 19 9C19 12.87 15.87 16 12 16C10.07 16 8.32 15.21 7.06 13.94L5.64 15.36C7.27 16.99 9.51 18 12 18C16.97 18 21 13.97 21 9C21 4.03 16.97 0 12 0ZM11 5V10L15.28 12.54L16 11.33L12.5 9.25V5H11Z"
        fill={color}
      />
    </StyledSVG>
  );
}

export function IconCircleSpinner({ size = 16, testId }: Props): JSX.Element {
  return (
    <StyledSVG height={size} width={size} viewBox="0 0 16 16" data-testid={testId}>
      <g fill={colors.steel[500]}>
        <path
          d="M8,16a8,8,0,1,1,8-8A8.009,8.009,0,0,1,8,16ZM8,2a6,6,0,1,0,6,6A6.006,6.006,0,0,0,8,2Z"
          fill={colors.steel[400]}
          opacity="0.4"
        />
        <path d="M16,8H14A6.006,6.006,0,0,0,8,2V0A8.009,8.009,0,0,1,16,8Z" />
      </g>
    </StyledSVG>
  );
}

export function IconCircleInformation({ color = colors.steel['400'], size = 16 }: Props): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 20 20" fill="none">
      <path
        d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V9H11V15ZM11 7H9V5H11V7Z"
        fill={color}
      />
    </StyledSVG>
  );
}

export function IconRibbon({ color = colors.steel['200'], size = 16 }: Props): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 20 20" fill="none">
      <path
        d="M17.2727 0H13.6364V8.63636C13.6364 8.75692 13.5885 8.87253 13.5032 8.95778C13.418 9.04302 13.3024 9.09091 13.1818 9.09091C13.0734 9.09066 12.9686 9.05165 12.8864 8.98091L10 6.50818L7.11364 8.98091C7.04766 9.03734 6.96691 9.0737 6.88092 9.08568C6.79494 9.09766 6.70732 9.08477 6.62843 9.04853C6.54954 9.01229 6.48268 8.95421 6.43576 8.88117C6.38883 8.80813 6.3638 8.72318 6.36364 8.63636V0H2.72727C2.00396 0 1.31026 0.287337 0.7988 0.7988C0.287337 1.31026 0 2.00396 0 2.72727V17.2727C0 17.996 0.287337 18.6897 0.7988 19.2012C1.31026 19.7127 2.00396 20 2.72727 20H17.2727C17.996 20 18.6897 19.7127 19.2012 19.2012C19.7127 18.6897 20 17.996 20 17.2727V2.72727C20 2.00396 19.7127 1.31026 19.2012 0.7988C18.6897 0.287337 17.996 0 17.2727 0Z"
        fill={color}
      />
    </StyledSVG>
  );
}

export function IconOrg({ color = colors.steel['200'], size = 16 }: Props): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 20 20" fill="none">
      <path
        d="M19.4058 2.535L11.0725 0.0349969C10.9481 -0.00228145 10.8167 -0.00995815 10.6887 0.0125795C10.5608 0.0351171 10.4399 0.0872449 10.3357 0.164803C10.2316 0.24236 10.1469 0.3432 10.0886 0.459274C10.0303 0.575347 9.99999 0.703441 10 0.83333V5H11.6667V1.95333L18.3333 3.95333V18.3333H13.3333V7.5C13.3333 7.27898 13.2455 7.06702 13.0893 6.91074C12.933 6.75446 12.721 6.66666 12.5 6.66666H4.16667C3.94565 6.66666 3.73369 6.75446 3.57741 6.91074C3.42113 7.06702 3.33333 7.27898 3.33333 7.5V18.3333H1.66667V10H0.833333C0.61232 10 0.400358 10.0878 0.244078 10.2441C0.0877974 10.4004 0 10.6123 0 10.8333L0 19.1667C0 19.3877 0.0877974 19.5996 0.244078 19.7559C0.400358 19.9122 0.61232 20 0.833333 20H19.1667C19.3877 20 19.5996 19.9122 19.7559 19.7559C19.9122 19.5996 20 19.3877 20 19.1667V3.33333C20 3.15405 19.9422 2.97955 19.8352 2.83573C19.7281 2.69191 19.5776 2.58645 19.4058 2.535V2.535ZM9.58333 17.5H7.08333C6.97283 17.5 6.86685 17.4561 6.78871 17.378C6.71056 17.2998 6.66667 17.1938 6.66667 17.0833V16.25C6.66667 16.1395 6.71056 16.0335 6.78871 15.9554C6.86685 15.8772 6.97283 15.8333 7.08333 15.8333H9.58333C9.69384 15.8333 9.79982 15.8772 9.87796 15.9554C9.9561 16.0335 10 16.1395 10 16.25V17.0833C10 17.1938 9.9561 17.2998 9.87796 17.378C9.79982 17.4561 9.69384 17.5 9.58333 17.5ZM9.58333 14.1667H7.08333C6.97283 14.1667 6.86685 14.1228 6.78871 14.0446C6.71056 13.9665 6.66667 13.8605 6.66667 13.75V12.9167C6.66667 12.8062 6.71056 12.7002 6.78871 12.622C6.86685 12.5439 6.97283 12.5 7.08333 12.5H9.58333C9.69384 12.5 9.79982 12.5439 9.87796 12.622C9.9561 12.7002 10 12.8062 10 12.9167V13.75C10 13.8605 9.9561 13.9665 9.87796 14.0446C9.79982 14.1228 9.69384 14.1667 9.58333 14.1667ZM9.58333 10.8333H7.08333C6.97283 10.8333 6.86685 10.7894 6.78871 10.7113C6.71056 10.6332 6.66667 10.5272 6.66667 10.4167V9.58333C6.66667 9.47282 6.71056 9.36684 6.78871 9.2887C6.86685 9.21056 6.97283 9.16666 7.08333 9.16666H9.58333C9.69384 9.16666 9.79982 9.21056 9.87796 9.2887C9.9561 9.36684 10 9.47282 10 9.58333V10.4167C10 10.5272 9.9561 10.6332 9.87796 10.7113C9.79982 10.7894 9.69384 10.8333 9.58333 10.8333Z"
        fill={color}
      />
      <path
        d="M16.25 6.66669H15.4167C15.1865 6.66669 15 6.85324 15 7.08335V16.25C15 16.4801 15.1865 16.6667 15.4167 16.6667H16.25C16.4801 16.6667 16.6667 16.4801 16.6667 16.25V7.08335C16.6667 6.85324 16.4801 6.66669 16.25 6.66669Z"
        fill={color}
      />
    </StyledSVG>
  );
}

export function IconSignOut({ color = colors.steel['200'], size = 16 }: Props): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 20 20" fill="none">
      <path
        d="M7.09 12.59L8.5 14L13.5 9L8.5 4L7.09 5.41L9.67 8H0V10H9.67L7.09 12.59ZM16 0H2C1.46957 0 0.960859 0.210714 0.585786 0.585786C0.210714 0.960859 0 1.46957 0 2V6H2V2H16V16H2V12H0V16C0 16.5304 0.210714 17.0391 0.585786 17.4142C0.960859 17.7893 1.46957 18 2 18H16C17.1 18 18 17.1 18 16V2C18 0.9 17.1 0 16 0Z"
        fill={color}
      />
    </StyledSVG>
  );
}

export function IconAdobeAcrobatDocument({ color = colors.steel['200'], size = 16 }: Props): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M18.9479 5.82738L15.6712 2.55067C15.3197 2.19918 14.8432 2 14.3472 2H6.3761C5.34114 2.00391 4.50146 2.84359 4.50146 3.87854V20.1254C4.50146 21.1603 5.34114 22 6.3761 22H17.6239C18.6589 22 19.4985 21.1603 19.4985 20.1254V7.15524C19.4985 6.65925 19.2994 6.17887 18.9479 5.82738ZM17.4716 7.00293H14.4995V4.03085L17.4716 7.00293ZM6.3761 20.1254V3.87854H12.6249V7.94025C12.6249 8.45968 13.0428 8.87756 13.5622 8.87756H17.6239V20.1254H6.3761ZM16.1476 14.5132C15.6712 14.0445 14.312 14.1734 13.6325 14.2593C12.9608 13.8492 12.5116 13.283 12.1953 12.4511C12.3476 11.8223 12.5897 10.8655 12.4062 10.264C12.2421 9.24077 10.9299 9.34232 10.7424 10.0336C10.5706 10.6624 10.7268 11.5372 11.0158 12.6542C10.6253 13.5876 10.0434 14.8412 9.63328 15.5599C8.85218 15.9621 7.7977 16.5831 7.64148 17.3642C7.5126 17.9813 8.6569 19.52 10.6136 16.1457C11.4884 15.8567 12.4413 15.5013 13.2849 15.3607C14.023 15.759 14.8862 16.0246 15.4642 16.0246C16.4601 16.0246 16.5577 14.9233 16.1476 14.5132ZM8.41086 17.5517C8.61004 17.0166 9.3677 16.3995 9.59813 16.1847C8.85608 17.3681 8.41086 17.579 8.41086 17.5517ZM11.5977 10.1078C11.8867 10.1078 11.8594 11.3615 11.668 11.7012C11.4962 11.1584 11.5001 10.1078 11.5977 10.1078ZM10.6448 15.4427C11.0236 14.7827 11.3478 13.9977 11.6095 13.3064C11.9336 13.8961 12.3476 14.3687 12.785 14.6928C11.9727 14.8608 11.2658 15.2045 10.6448 15.4427ZM15.7844 15.2474C15.7844 15.2474 15.5891 15.4817 14.3277 14.9428C15.6985 14.8412 15.925 15.1537 15.7844 15.2474Z"
        fill={color}
      />
    </svg>
  );
}

export function IconDocument({ color = colors.steel['200'], size = 16 }: Props): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 20 20" fill="none">
      <path
        d="M11 0H0.916667C0.673552 0 0.440394 0.0965771 0.268485 0.268485C0.0965771 0.440394 0 0.673552 0 0.916667V21.0833C0 21.3264 0.0965771 21.5596 0.268485 21.7315C0.440394 21.9034 0.673552 22 0.916667 22H17.4167C17.6598 22 17.8929 21.9034 18.0648 21.7315C18.2368 21.5596 18.3333 21.3264 18.3333 21.0833V7.33333H11.9167C11.6736 7.33333 11.4404 7.23676 11.2685 7.06485C11.0966 6.89294 11 6.65978 11 6.41667V0ZM3.20833 15.5833H15.125C15.2466 15.5833 15.3631 15.6316 15.4491 15.7176C15.535 15.8035 15.5833 15.9201 15.5833 16.0417V16.9583C15.5833 17.0799 15.535 17.1965 15.4491 17.2824C15.3631 17.3684 15.2466 17.4167 15.125 17.4167H3.20833C3.08678 17.4167 2.9702 17.3684 2.88424 17.2824C2.79829 17.1965 2.75 17.0799 2.75 16.9583V16.0417C2.75 15.9201 2.79829 15.8035 2.88424 15.7176C2.9702 15.6316 3.08678 15.5833 3.20833 15.5833V15.5833ZM3.20833 11H15.125C15.2466 11 15.3631 11.0483 15.4491 11.1342C15.535 11.2202 15.5833 11.3368 15.5833 11.4583V12.375C15.5833 12.4966 15.535 12.6131 15.4491 12.6991C15.3631 12.785 15.2466 12.8333 15.125 12.8333H3.20833C3.08678 12.8333 2.9702 12.785 2.88424 12.6991C2.79829 12.6131 2.75 12.4966 2.75 12.375V11.4583C2.75 11.3368 2.79829 11.2202 2.88424 11.1342C2.9702 11.0483 3.08678 11 3.20833 11V11ZM7.79167 8.25H3.20833C3.08678 8.25 2.9702 8.20171 2.88424 8.11576C2.79829 8.0298 2.75 7.91322 2.75 7.79167V6.875C2.75 6.75344 2.79829 6.63686 2.88424 6.55091C2.9702 6.46496 3.08678 6.41667 3.20833 6.41667H7.79167C7.91322 6.41667 8.0298 6.46496 8.11576 6.55091C8.20171 6.63686 8.25 6.75344 8.25 6.875V7.79167C8.25 7.91322 8.20171 8.0298 8.11576 8.11576C8.0298 8.20171 7.91322 8.25 7.79167 8.25Z"
        fill={color}
      />
      <path d="M17.7963 5.5H12.8335V0.53717L17.7963 5.5Z" fill={color} />
    </StyledSVG>
  );
}

export function IconHome({ color = colors.steel['200'], size = 16 }: Props): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 20 20" fill="none">
      <path d="M8.8 18.7V12.1H13.2V18.7H18.7V9.9H22L11 0L0 9.9H3.3V18.7H8.8Z" fill={color} />
    </StyledSVG>
  );
}

export function IconInspect({ color = colors.steel['200'], size = 16 }: Props): JSX.Element {
  return (
    <StyledSVG height={size} viewBox="0 0 18 22" fill="none">
      <path
        d="M18 4L9 0L0 4V10C0 15.55 3.84 20.74 9 22C11.3 21.44 13.33 20.1 14.88 18.29L11.76 15.17C10.7987 15.8054 9.64722 16.0888 8.50083 15.9721C7.35443 15.8555 6.28364 15.346 5.47 14.53C4.53338 13.592 4.00731 12.3206 4.00731 10.995C4.00731 9.66942 4.53338 8.39803 5.47 7.46C6.40803 6.52338 7.67942 5.99731 9.005 5.99731C10.3306 5.99731 11.602 6.52338 12.54 7.46C13.354 8.27482 13.8621 9.34538 13.9787 10.4912C14.0952 11.637 13.8132 12.7879 13.18 13.75L16.08 16.65C17.29 14.69 18 12.38 18 10V4Z"
        fill={color}
      />
      <path
        d="M9 14C9.79565 14 10.5587 13.6839 11.1213 13.1213C11.6839 12.5587 12 11.7956 12 11C12 10.2044 11.6839 9.44129 11.1213 8.87868C10.5587 8.31607 9.79565 8 9 8C8.20435 8 7.44129 8.31607 6.87868 8.87868C6.31607 9.44129 6 10.2044 6 11C6 11.7956 6.31607 12.5587 6.87868 13.1213C7.44129 13.6839 8.20435 14 9 14Z"
        fill={color}
      />
    </StyledSVG>
  );
}

export function IconCertificate({ color = colors.steel['200'], size = 16 }: Props): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 20 20" fill="none">
      <path
        d="M16.25 20H1.25C0.5 20 0 19.5 0 18.75V1.25C0 0.5 0.5 0 1.25 0H16.25V2.5H2.5V17.5H15V16.25H17.5V18.75C17.5 19.5 17 20 16.25 20Z"
        fill={color}
      />
      <path
        d="M20 7.5C20 5.375 18.375 3.75 16.25 3.75C14.125 3.75 12.5 5.375 12.5 7.5C12.5 8.625 13 9.625 13.75 10.25V15L16.25 12.5L18.75 15V10.25C19.5 9.625 20 8.625 20 7.5Z"
        fill={color}
      />
      <path d="M10 6.25H5V8.75H10V6.25Z" fill={color} />
      <path d="M11.25 11.25H5V13.75H11.25V11.25Z" fill={color} />
    </StyledSVG>
  );
}

export function IconClaim({ color = colors.steel['200'], size = 16 }: Props): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 20 20" fill="none">
      <path
        d="M14 0H2C0.9 0 0 0.9 0 2V18C0 19.1 0.9 20 2 20H14C15.1 20 16 19.1 16 18V2C16 0.9 15.1 0 14 0ZM2 2H7V10L4.5 8.5L2 10V2Z"
        fill={color}
      />
    </StyledSVG>
  );
}
export function IconBilling({ color = colors.steel['200'], size = 16 }: Props): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 20 20" fill="none">
      <path
        d="M16.3636 9.375V1.875C16.3636 0.84375 15.5455 0 14.5455 0H1.81818C0.818182 0 0 0.84375 0 1.875V9.375C0 10.4062 0.818182 11.25 1.81818 11.25H14.5455C15.5455 11.25 16.3636 10.4062 16.3636 9.375ZM8.18182 8.4375C6.67273 8.4375 5.45455 7.18125 5.45455 5.625C5.45455 4.06875 6.67273 2.8125 8.18182 2.8125C9.69091 2.8125 10.9091 4.06875 10.9091 5.625C10.9091 7.18125 9.69091 8.4375 8.18182 8.4375ZM20 2.8125V13.125C20 14.1562 19.1818 15 18.1818 15H2.72727V13.125H18.1818V2.8125H20Z"
        fill={color}
      />
    </StyledSVG>
  );
}

export function IconManageMember({ color = colors.steel['200'], size = 16 }: Props): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 23 21" fill="none">
      <path
        d="M10.2 11.8618H6.8C4.99715 11.8638 3.26872 12.5809 1.99391 13.8557C0.719102 15.1305 0.00202497 16.859 1.78337e-07 18.6618C-0.000116522 18.842 0.0570445 19.0176 0.163229 19.1632C0.269414 19.3088 0.419127 19.4169 0.59075 19.4719C3.17206 20.1397 5.83482 20.4393 8.5 20.3618C11.1652 20.4393 13.8279 20.1397 16.4093 19.4719C16.5809 19.4169 16.7306 19.3088 16.8368 19.1632C16.943 19.0176 17.0001 18.842 17 18.6618C16.998 16.859 16.2809 15.1305 15.0061 13.8557C13.7313 12.5809 12.0029 11.8638 10.2 11.8618Z"
        fill={color}
      />
      <path
        d="M8.49971 10.1617C11.1347 10.1617 13.1747 7.21225 13.1747 4.675C13.1747 3.43511 12.6822 2.24601 11.8054 1.36928C10.9287 0.492543 9.73959 0 8.49971 0C7.25982 0 6.07071 0.492543 5.19398 1.36928C4.31725 2.24601 3.82471 3.43511 3.82471 4.675C3.82471 7.21225 5.86471 10.1617 8.49971 10.1617Z"
        fill={color}
      />
      <path
        d="M19.7441 9.27441H21.6411C22.0545 9.27441 22.3896 9.60954 22.3896 10.0229C22.3896 10.4364 22.0545 10.7715 21.6411 10.7715H19.7441V12.6548C19.7441 13.0682 19.409 13.4033 18.9956 13.4033C18.5822 13.4033 18.2471 13.0682 18.2471 12.6548V10.7715H16.3501C15.9367 10.7715 15.6016 10.4364 15.6016 10.0229C15.6016 9.60954 15.9367 9.27441 16.3501 9.27441H18.2471V7.35693C18.2471 6.94353 18.5822 6.6084 18.9956 6.6084C19.409 6.6084 19.7441 6.94353 19.7441 7.35693V9.27441Z"
        fill={color}
      />
    </StyledSVG>
  );
}

export function IconEmail({ color = '#fff', size = 16, testId }: Props): JSX.Element {
  return (
    <StyledSVG height={size} width={size} viewBox="0 0 8 7" data-testid={testId}>
      <path d="M7.5 0H0.5C0.2 0 0 0.2 0 0.5V1.2L4 3.45L8 1.25V0.5C8 0.2 7.8 0 7.5 0Z" fill={color} />
      <path
        d="M3.75 4.4501L0 2.3501V6.5001C0 6.8001 0.2 7.0001 0.5 7.0001H7.5C7.8 7.0001 8 6.8001 8 6.5001V2.3501L4.25 4.4501C4.11 4.5201 3.89 4.5201 3.75 4.4501Z"
        fill={color}
      />
    </StyledSVG>
  );
}

export function IconAssociate({ color = colors.steel[400], size = 16, testId }: Props): JSX.Element {
  return (
    <svg height={size} width={size} viewBox="0 0 17 16" fill="none" data-testid={testId}>
      <path
        d="M13.8125 4.96338V7.23287C14.4447 7.60156 14.875 8.27944 14.875 9.0625V11.1875C14.875 12.3594 13.9219 13.3125 12.75 13.3125H8.5C7.32806 13.3125 6.375 12.3594 6.375 11.1875V9.0625C6.375 7.89056 7.32806 6.9375 8.5 6.9375H9.5625V4.8125H8.5C6.15612 4.8125 4.25 6.71862 4.25 9.0625V11.1875C4.25 13.5314 6.15612 15.4375 8.5 15.4375H12.75C15.0939 15.4375 17 13.5314 17 11.1875V9.0625C17 7.08731 15.6411 5.43831 13.8125 4.96338Z"
        fill={color}
      />
      <path
        d="M8.5 0.5625H4.25C1.90612 0.5625 0 2.46862 0 4.8125V6.9375C0 8.91269 1.35894 10.5617 3.1875 11.0366V8.76713C2.55531 8.39844 2.125 7.72056 2.125 6.9375V4.8125C2.125 3.64056 3.07806 2.6875 4.25 2.6875H8.5C9.67194 2.6875 10.625 3.64056 10.625 4.8125V6.9375C10.625 8.10944 9.67194 9.0625 8.5 9.0625H7.4375V11.1875H8.5C10.8439 11.1875 12.75 9.28137 12.75 6.9375V4.8125C12.75 2.46862 10.8439 0.5625 8.5 0.5625Z"
        fill={color}
      />
    </svg>
  );
}

export function IconWarning({ color = colors.fire[500], size = 16 }: Props): JSX.Element {
  return (
    <svg height={size} viewBox="0 0 20 18" fill="none">
      <path
        d="M18.9878 15.9112L10.9702 1.06745C10.4943 0.184954 9.21331 0.184954 8.73611 1.06745L0.719832 15.9112C0.270475 16.7437 0.881854 17.75 1.83753 17.75H17.8713C18.8258 17.75 19.4371 16.7437 18.9878 15.9112ZM9.8538 15.25C9.15508 15.25 8.58801 14.69 8.58801 14C8.58801 13.31 9.15508 12.75 9.8538 12.75C10.5525 12.75 11.1196 13.31 11.1196 14C11.1196 14.69 10.5525 15.25 9.8538 15.25ZM11.1196 11.5H8.58801V6.49995H11.1196V11.5Z"
        fill={color}
      />
    </svg>
  );
}

export function IconEmailOutline({ color = colors.steel[400], size = 16 }: Props): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M0.4375 4.375L7 7.875L13.5625 4.375"
        stroke={color}
        strokeWidth="0.875"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12.6875 12.6875H1.3125C0.8295 12.6875 0.4375 12.2955 0.4375 11.8125V2.1875C0.4375 1.7045 0.8295 1.3125 1.3125 1.3125H12.6875C13.1705 1.3125 13.5625 1.7045 13.5625 2.1875V11.8125C13.5625 12.2955 13.1705 12.6875 12.6875 12.6875Z"
        stroke={color}
        strokeWidth="0.875"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function IconPhone({ color = colors.steel[400], size = 16 }: Props): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0)">
        <path
          d="M8.03074 9.21286L7.10412 10.3722C5.66846 9.52805 4.47193 8.33152 3.62774 6.89586L4.78712 5.96923C4.92295 5.86049 5.01895 5.70981 5.0601 5.54075C5.10126 5.37168 5.08527 5.19375 5.01462 5.03473L3.95849 2.65648C3.88278 2.48588 3.74894 2.34771 3.58083 2.26662C3.41272 2.18553 3.22127 2.16679 3.04062 2.21373L1.02812 2.73873C0.839935 2.78683 0.676163 2.90277 0.568274 3.06428C0.460384 3.22579 0.415989 3.42148 0.443618 3.61373C0.797057 6.12604 1.95881 8.45499 3.75325 10.2485C5.54769 12.042 7.87725 13.2025 10.3897 13.5546C10.582 13.5822 10.7777 13.5378 10.9392 13.43C11.1007 13.3221 11.2167 13.1583 11.2647 12.9701L11.7897 10.9576C11.8365 10.7771 11.8176 10.5858 11.7365 10.4179C11.6554 10.25 11.5174 10.1163 11.347 10.0406L8.96524 8.98623C8.80632 8.91549 8.62845 8.89936 8.45939 8.94035C8.29033 8.98135 8.13961 9.07717 8.03074 9.21286V9.21286Z"
          stroke={color}
          strokeWidth="0.875"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M7.4375 3.5C8.24973 3.5 9.02868 3.82266 9.60301 4.39699C10.1773 4.97132 10.5 5.75027 10.5 6.5625"
          stroke={color}
          strokeWidth="0.875"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M7.4375 0.875C8.18439 0.875 8.92397 1.02211 9.61401 1.30794C10.3041 1.59376 10.931 2.0127 11.4592 2.54083C11.9873 3.06896 12.4062 3.69595 12.6921 4.38599C12.9779 5.07603 13.125 5.81561 13.125 6.5625"
          stroke={color}
          strokeWidth="0.875"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0">
          <rect width="14" height="14" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

export function IconCross({ color = colors.steel[400], size = 16, strokeWidth }: Props & WithSvgProps): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M13.5 2.5L2.5 13.5"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2.5 2.5L13.5 13.5"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function IconPreview({ color = 'currentColor', height = 14 }: Pick<Props, 'color' | 'height'>): JSX.Element {
  return (
    <svg width="18" height={height} viewBox={`0 0 ${height} 18`} fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9.0002 13.75C13.0502 13.75 16.2002 10.2625 17.5502 8.2375C18.1127 7.45 18.1127 6.4375 17.5502 5.65C16.2002 3.7375 13.0502 0.25 9.0002 0.25C4.9502 0.25 1.8002 3.7375 0.450195 5.7625C-0.112305 6.55 -0.112305 7.5625 0.450195 8.2375C1.8002 10.2625 4.9502 13.75 9.0002 13.75ZM9.0002 3.625C10.9127 3.625 12.3752 5.0875 12.3752 7C12.3752 8.9125 10.9127 10.375 9.0002 10.375C7.0877 10.375 5.6252 8.9125 5.6252 7C5.6252 5.0875 7.0877 3.625 9.0002 3.625Z"
        fill={color}
      />
    </svg>
  );
}

export function IconDownload({ color = 'currentColor', size = 16 }: Props): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8.0001 12C8.3001 12 8.5001 11.9 8.7001 11.7L14.4001 6L13.0001 4.6L9.0001 8.6V0H7.0001V8.6L3.0001 4.6L1.6001 6L7.3001 11.7C7.5001 11.9 7.7001 12 8.0001 12Z"
        fill={color}
      />
      <path d="M14 14H2V11H0V15C0 15.6 0.4 16 1 16H15C15.6 16 16 15.6 16 15V11H14V14Z" fill={color} />
    </svg>
  );
}

export function IconKanban({ color = 'currentColor', size }: Props): JSX.Element {
  return (
    <StyledSVG width={size || 18} height={size || 18} viewBox="0 0 18 18" fill="none">
      <line y1="1" x2="18" y2="1" stroke={color} strokeWidth="2" rx="1" strokeLinecap="round" />
      <rect x="1" y="5" width="6" height="8" rx="1" stroke={color} strokeWidth="2" />
      <rect x="11" y="5" width="6" height="12" rx=".5" stroke={color} strokeWidth="2" />
    </StyledSVG>
  );
}

export function IconAccounts({ color = 'currentColor', size }: Props): JSX.Element {
  return (
    <StyledSVG
      width={size || 20}
      height={size || 20}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10 11.9998C12.2091 11.9998 14 10.2089 14 7.99979C14 5.79065 12.2091 3.99979 10 3.99979C7.79086 3.99979 6 5.79065 6 7.99979C6 10.2089 7.79086 11.9998 10 11.9998ZM10 5.99979C11.1046 5.99979 12 6.89522 12 7.99979C12 9.10436 11.1046 9.99979 10 9.99979C8.89543 9.99979 8 9.10436 8 7.99979C8 6.89522 8.89543 5.99979 10 5.99979Z"
        fill={color}
      />
      <path
        d="M2 19.9998C0.895431 19.9998 0 19.1044 0 17.9998V1.99979C0 0.895217 0.895431 -0.000213623 2 -0.000213623H18C19.1046 -0.000213623 20 0.895217 20 1.99979V17.9998C20 19.1044 19.1046 19.9998 18 19.9998H2ZM2 1.99979V17.9998H5C5 15.2384 7.23858 12.9998 10 12.9998C12.7614 12.9998 15 15.2384 15 17.9998H18V1.99979H2ZM13 17.9998C13 16.3429 11.6569 14.9998 10 14.9998C8.34315 14.9998 7 16.3429 7 17.9998H13Z"
        fill={color}
      />
    </StyledSVG>
  );
}

/**
 * @deprecated Prefer the use of IconExternal2
 */
export function IconExternal({ color = colors.steel[500], size = 12 }: Props): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 12 12" fill="none">
      <path d="M10.5 1.5L1.5 10.5" stroke={color} strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4.5 1.5H10.5V7.5" stroke={color} strokeLinecap="round" strokeLinejoin="round" />
    </StyledSVG>
  );
}

export function IconExternal2(): JSX.Element {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path
        d="M11.25 0H5.99998V1.5H9.44998L4.72498 6.225L5.77498 7.275L10.5 2.55V6H12V0.75C12 0.3 11.7 0 11.25 0Z"
        fill="currentColor"
      />
      <path
        d="M10.5 12H0.75C0.3 12 0 11.7 0 11.25V1.5C0 1.05 0.3 0.75 0.75 0.75H3.75V2.25H1.5V10.5H9.75V8.25H11.25V11.25C11.25 11.7 10.95 12 10.5 12Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function IconFilter({ color = colors.steel[400], size }: Props): JSX.Element {
  return (
    <svg width={size || 16} height={size || 14} viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9 14H7C6.448 14 6 13.552 6 13C6 12.448 6.448 12 7 12H9C9.552 12 10 12.448 10 13C10 13.552 9.552 14 9 14Z"
        fill={color}
      />
      <path
        d="M11 10H5C4.448 10 4 9.552 4 9C4 8.448 4.448 8 5 8H11C11.552 8 12 8.448 12 9C12 9.552 11.552 10 11 10Z"
        fill={color}
      />
      <path
        d="M13 6H3C2.448 6 2 5.552 2 5C2 4.448 2.448 4 3 4H13C13.552 4 14 4.448 14 5C14 5.552 13.552 6 13 6Z"
        fill={color}
      />
      <path
        d="M15 2H1C0.448 2 0 1.552 0 1C0 0.448 0.448 0 1 0H15C15.552 0 16 0.448 16 1C16 1.552 15.552 2 15 2Z"
        fill={color}
      />
    </svg>
  );
}

export function IconSort({ color = colors.steel[400], size }: Props): JSX.Element {
  return (
    <svg width={size || 16} height={size || 16} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="m15.364 11.121l-4.2427 4.2426c-0.3903 0.3904-1.0239 0.3904-1.4142 0-0.39033-0.3903-0.39033-1.0238 0-1.4142l4.2426-4.2426c0.3904-0.39032 1.0239-0.39032 1.4143 0 0.3903 0.39031 0.3903 1.0239 0 1.4142z"
        fill={color}
      />
      <path
        d="m6.364 2.1212l-4.2426 4.2426c-0.39032 0.39032-1.0239 0.39032-1.4142 0-0.39032-0.39033-0.39032-1.0239 0-1.4142l4.2426-4.2426c0.39032-0.39032 1.0239-0.39032 1.4142 0 0.39032 0.39032 0.39032 1.0239 0 1.4142z"
        fill={color}
      />
      <path d="m7 1v14c0 0.552-0.448 1-1 1s-1-0.448-1-1v-14c0-0.552 0.448-1 1-1s1 0.448 1 1z" fill={color} />
      <path d="m11 1v14c0 0.552-0.448 1-1 1s-1-0.448-1-1v-14c0-0.552 0.448-1 1-1s1 0.448 1 1z" fill={color} />
    </svg>
  );
}

export function IconAlarm({ color = colors.steel[300], size = 12 }: Props): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7.5 10.5H4.5C4.5 11.325 5.175 12 6 12C6.825 12 7.5 11.325 7.5 10.5Z" fill={color} />
      <path
        d="M11.25 8.25H10.875C10.35 7.725 9.75 6.975 9.75 6V3.75C9.75 1.65 8.1 0 6 0C3.9 0 2.25 1.65 2.25 3.75V6C2.25 6.975 1.65 7.725 1.125 8.25H0.75C0.3 8.25 0 8.55 0 9C0 9.45 0.3 9.75 0.75 9.75H11.25C11.7 9.75 12 9.45 12 9C12 8.55 11.7 8.25 11.25 8.25Z"
        fill={color}
      />
    </StyledSVG>
  );
}

export function IconBoardView({ color = colors.steel[200], size = 24 }: Props): JSX.Element {
  return (
    <StyledSVG xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill={color}>
      <rect x="6" y="5" width="3" height="13" />
      <rect x="11" y="5" width="3" height="9" />
      <rect x="16" y="5" width="3" height="13" />
    </StyledSVG>
  );
}

export function IconViewOptions({ color = colors.steel[400] }: Props): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path
        d="M14.1935 17.5645H11.4839C11.1028 17.5645 10.8065 17.9032 10.8065 18.2419V18.9194C10.8065 19.3004 11.1028 19.5968 11.4839 19.5968H14.1935C14.5323 19.5968 14.871 19.3004 14.871 18.9194V18.2419C14.871 17.9032 14.5323 17.5645 14.1935 17.5645ZM2.50806 7.06452H4.37097V21.2903C4.37097 21.6714 4.66733 21.9677 5.04839 21.9677H5.72581C6.06452 21.9677 6.40323 21.6714 6.40323 21.2903V7.06452H8.26613C8.52016 7.06452 8.77419 6.85282 8.77419 6.55645C8.77419 6.42944 8.68952 6.30242 8.60484 6.21774L5.72581 3.16935C5.64113 3.08468 5.51411 3 5.3871 3C5.21774 3 5.09073 3.08468 5.00604 3.16935L2.12702 6.21774C2.04234 6.30242 2 6.42944 2 6.55645C2 6.85282 2.21169 7.06452 2.50806 7.06452ZM19.6129 9.43548H11.4839C11.1028 9.43548 10.8065 9.77419 10.8065 10.1129V10.7903C10.8065 11.1714 11.1028 11.4677 11.4839 11.4677H19.6129C19.9516 11.4677 20.2903 11.1714 20.2903 10.7903V10.1129C20.2903 9.77419 19.9516 9.43548 19.6129 9.43548ZM22.3226 5.37097H11.4839C11.1028 5.37097 10.8065 5.70968 10.8065 6.04839V6.72581C10.8065 7.10686 11.1028 7.40323 11.4839 7.40323H22.3226C22.6613 7.40323 23 7.10686 23 6.72581V6.04839C23 5.70968 22.6613 5.37097 22.3226 5.37097ZM16.9032 13.5H11.4839C11.1028 13.5 10.8065 13.8387 10.8065 14.1774V14.8548C10.8065 15.2359 11.1028 15.5323 11.4839 15.5323H16.9032C17.2419 15.5323 17.5806 15.2359 17.5806 14.8548V14.1774C17.5806 13.8387 17.2419 13.5 16.9032 13.5Z"
        fill={color}
      />
    </svg>
  );
}

export function IconListView({ color = colors.steel[200], size = 24 }: Props): JSX.Element {
  return (
    <StyledSVG xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill={color}>
      <rect x="9" y="13" width="2.5" height="12" transform="rotate(-90 9 13)" />
      <rect x="9" y="18" width="2.5" height="12" transform="rotate(-90 9 18)" />
      <rect x="9" y="8" width="2.5" height="12" transform="rotate(-90 9 8)" />
      <circle cx="4.75" cy="6.75" r="1.75" />
      <circle cx="4.75" cy="11.75" r="1.75" />
      <circle cx="4.75" cy="16.75" r="1.75" />
    </StyledSVG>
  );
}

export const IconSearch2 = ({ size = 18, color = 'currentColor' }: Props): JSX.Element => {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M15.677 16.607L9.96198 10.891C7.41965 12.6985 3.91642 12.2564 1.90285 9.87395C-0.110711 7.49153 0.0371394 3.96361 2.24298 1.75802C4.44824 -0.448534 7.97651 -0.597024 10.3594 1.41644C12.7422 3.42989 13.1846 6.93347 11.377 9.47602L17.092 15.192L15.678 16.606L15.677 16.607ZM6.48498 2.00001C4.58868 1.99958 2.95267 3.3307 2.56745 5.18745C2.18224 7.04421 3.15369 8.91629 4.89366 9.67026C6.63362 10.4242 8.66388 9.85285 9.75522 8.30207C10.8466 6.75129 10.699 4.64734 9.40198 3.26402L10.007 3.86402L9.32498 3.18402L9.31298 3.17202C8.56477 2.4192 7.54637 1.99715 6.48498 2.00001Z"
        fill={color}
      />
    </svg>
  );
};

export const IconLock = ({ color = colors.steel[300], size = 16 }: Props): JSX.Element => {
  return (
    <svg width={size} height={size} viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M11 6H9V4C9 3.46957 8.78929 2.96086 8.41421 2.58579C8.03914 2.21071 7.53043 2 7 2C6.46957 2 5.96086 2.21071 5.58579 2.58579C5.21071 2.96086 5 3.46957 5 4V6H3V4C3 2.93913 3.42143 1.92172 4.17157 1.17157C4.92172 0.421427 5.93913 0 7 0C8.06087 0 9.07828 0.421427 9.82843 1.17157C10.5786 1.92172 11 2.93913 11 4V6Z"
        fill={color}
      />
      <path
        d="M13 7H1C0.734784 7 0.48043 7.10536 0.292893 7.29289C0.105357 7.48043 0 7.73478 0 8V15C0 15.2652 0.105357 15.5196 0.292893 15.7071C0.48043 15.8946 0.734784 16 1 16H13C13.2652 16 13.5196 15.8946 13.7071 15.7071C13.8946 15.5196 14 15.2652 14 15V8C14 7.73478 13.8946 7.48043 13.7071 7.29289C13.5196 7.10536 13.2652 7 13 7ZM7 13C6.60444 13 6.21776 12.8827 5.88886 12.6629C5.55996 12.4432 5.30362 12.1308 5.15224 11.7654C5.00087 11.3999 4.96126 10.9978 5.03843 10.6098C5.1156 10.2219 5.30608 9.86549 5.58579 9.58579C5.86549 9.30608 6.22186 9.1156 6.60982 9.03843C6.99778 8.96126 7.39991 9.00087 7.76537 9.15224C8.13082 9.30362 8.44318 9.55996 8.66294 9.88886C8.8827 10.2178 9 10.6044 9 11C9 11.5304 8.78929 12.0391 8.41421 12.4142C8.03914 12.7893 7.53043 13 7 13Z"
        fill={color}
      />
    </svg>
  );
};

export function IconLocationPin({ color = colors.steel[400], size = 16 }: Props): JSX.Element {
  return (
    <svg height={size} viewBox="0 0 13 17" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M1.90464 1.71735C3.13084 0.610612 4.76449 0 6.5 0C8.23551 0 9.86916 0.610612 11.0954 1.71735C12.3235 2.82582 13 4.29857 13 5.865C13 6.66643 12.8232 7.44357 12.4753 8.17561C12.1409 8.99612 10.9397 11.4178 6.96511 16.4154L6.5 17L6.03489 16.4154C2.0584 11.4178 0.859106 8.99786 0.524689 8.17561C0.176818 7.44357 0 6.66643 0 5.865C0 4.29857 0.676523 2.82582 1.90464 1.71735ZM6.5 7.25969C7.63971 7.25969 8.56224 6.42531 8.56224 5.39837C8.56224 4.36969 7.63779 3.53704 6.5 3.53704C5.36029 3.53704 4.43776 4.37143 4.43776 5.39837C4.43776 6.42704 5.36029 7.25969 6.5 7.25969Z"
        fill={color}
      />
    </svg>
  );
}

export function IconBookmark({ color = colors.steel[400], size = 16 }: Props): JSX.Element {
  return (
    <svg height={size} viewBox="0 0 13 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M1 15.5V2C1 1.44772 1.44771 1 2 1H11C11.5523 1 12 1.44771 12 2V15.5L6.5 10L1 15.5Z"
        fill={color}
        stroke={color}
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function IconPlus({ color = colors.steel[400], size = 12 }: Props): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M11.3031 4.71429H7.44593V0.857143C7.44593 0.383839 7.06209 0 6.58879 0H5.73165C5.25834 0 4.8745 0.383839 4.8745 0.857143V4.71429H1.01736C0.544057 4.71429 0.160217 5.09812 0.160217 5.57143V6.42857C0.160217 6.90188 0.544057 7.28571 1.01736 7.28571H4.8745V11.1429C4.8745 11.6162 5.25834 12 5.73165 12H6.58879C7.06209 12 7.44593 11.6162 7.44593 11.1429V7.28571H11.3031C11.7764 7.28571 12.1602 6.90188 12.1602 6.42857V5.57143C12.1602 5.09812 11.7764 4.71429 11.3031 4.71429Z"
        fill={color}
      />
    </svg>
  );
}

export function IconSuccess({ color = colors.grass[500], size = 40 }: Props): JSX.Element {
  return (
    <svg width={size} height="40" viewBox="0 0 496 496" className="icon">
      {/* eslint-disable max-len */}
      <path
        d="M248 0C111.033 0 0 111.033 0 248C0 384.967 111.033 496 248 496C384.967 496 496 384.967 496 248C496 111.033 384.967 0 248 0ZM248 464C129.336 464 32 367.945 32 248C32 129.337 128.055 32 248 32C366.664 32 464 128.055 464 248C464 366.663 367.945 464 248 464ZM389.63 189.039L209.15 368.071C204.445 372.738 196.847 372.708 192.18 368.003L106.302 281.431C101.635 276.726 101.665 269.128 106.37 264.461L114.89 256.01C119.595 251.343 127.193 251.373 131.86 256.078L200.836 325.611L364.277 163.481C368.982 158.814 376.58 158.844 381.247 163.549L389.698 172.069C394.366 176.774 394.335 184.372 389.63 189.039Z"
        fill={color}
      />
      {/* eslint-enable max-len */}
      <style>
        {`
        @keyframes enterBounce {
          from {
            transform: scale(0.7);
          }
          to {
            transform: scale(1);
          }
        }
        .icon {
          animation: enterBounce 300ms cubic-bezier(0.495, 1.2, 0.66, 1.3);
        }
      `}
      </style>
    </svg>
  );
}

export function IconCommercialAccount({ color = colors.brand[300], size = 32 }: Props): JSX.Element {
  return (
    <svg width={size} height="31" viewBox="0 0 32 31" fill={color} xmlns="http://www.w3.org/2000/svg">
      <path d="M24 6.99902H22V25.999H24V6.99902Z" />
      <path d="M28 8.99902H26V25.999H28V8.99902Z" />
      <path
        // eslint-disable-next-line max-len
        d="M31.336 5.05814L17.336 0.0581383C17.1852 0.00432888 17.0236 -0.0124669 16.8649 0.00916401C16.7063 0.0307949 16.5551 0.0902198 16.4242 0.182439C16.2932 0.274659 16.1864 0.396975 16.1125 0.539097C16.0387 0.68122 16.0001 0.838991 16 0.999138V8.99914H18V2.41814L30 6.70414V28.9991H20V11.9991C20 11.7339 19.8946 11.4796 19.7071 11.292C19.5196 11.1045 19.2652 10.9991 19 10.9991H6C5.73478 10.9991 5.48043 11.1045 5.29289 11.292C5.10536 11.4796 5 11.7339 5 11.9991V28.9991H3V15.9991H1C0.734784 15.9991 0.48043 16.1045 0.292893 16.292C0.105357 16.4796 0 16.7339 0 16.9991L0 29.9991C0 30.2644 0.105357 30.5187 0.292893 30.7062C0.48043 30.8938 0.734784 30.9991 1 30.9991H31C31.2652 30.9991 31.5196 30.8938 31.7071 30.7062C31.8946 30.5187 32 30.2644 32 29.9991V5.99914C31.9998 5.79297 31.9359 5.5919 31.8171 5.42345C31.6982 5.255 31.5302 5.12741 31.336 5.05814ZM15 25.9991H10V23.9991H15V25.9991ZM15 21.9991H10V19.9991H15V21.9991ZM15 17.9991H10V15.9991H15V17.9991Z"
      />
    </svg>
  );
}

export function IconPersonalAccount({ color = colors.brand[300], size = 32 }: Props): JSX.Element {
  return (
    <svg width={size} height="30" viewBox="0 0 29 30" fill={color} xmlns="http://www.w3.org/2000/svg">
      <path
        // eslint-disable-next-line max-len
        d="M25.6838 21.5494L18.7725 17.7666C21.0197 15.7444 22.5 12.3169 22.5 8.4375C22.5 3.78469 18.7144 0 14.0625 0C9.41063 0 5.625 3.78469 5.625 8.4375C5.625 12.3169 7.10531 15.7444 9.3525 17.7675L2.44313 21.5494C0.936562 22.3697 0 23.9466 0 25.6641V29.0625C0 29.5809 0.42 30 0.9375 30H27.1875C27.705 30 28.125 29.5809 28.125 29.0625V25.6641C28.125 23.9466 27.1884 22.3697 25.6838 21.5494ZM7.52063 8.99531C8.415 9.23812 9.35812 9.375 10.3125 9.375C12.5794 9.375 14.7 8.68594 16.2722 7.46063C17.4431 8.48625 18.9525 9.12938 20.5922 9.31406C20.2716 14.0034 17.4131 17.8125 14.0625 17.8125C10.6369 17.8125 7.73156 13.8291 7.52063 8.99531ZM22.5 26.25H16.875V24.375H22.5V26.25Z"
      />
    </svg>
  );
}

export function IconUnassignPerson({ color = colors.brand[400], size = 32 }: Props): JSX.Element {
  const height = size - 2;
  return (
    <StyledSVG width={size} height={height} viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8 8.5C10.2091 8.5 12 6.70914 12 4.5C12 2.29086 10.2091 0.5 8 0.5C5.79086 0.5 4 2.29086 4 4.5C4 6.70914 5.79086 8.5 8 8.5Z"
        fill={color}
      />
      <path d="M16 12.5H10V14.5H16V12.5Z" fill={color} />
      <path
        d="M8 13.5C8 12.383 8.379 11.363 8.998 10.53C8.673 10.513 8.344 10.5 8 10.5C4.808 10.5 2.461 11.295 1.163 11.882C0.45 12.205 0 12.917 0 13.7V16.5H9.023C8.39 15.662 8 14.631 8 13.5Z"
        fill={color}
      />
    </StyledSVG>
  );
}

export function IconPlan({ color = colors.steel[500], size = 16, testId }: Props): JSX.Element {
  return (
    <StyledSVG
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      data-testid={testId ? `${testId}-icon-plan` : 'icon-plan'}
    >
      <path
        d="M12.6666 1L14.9999 3.33333L7.53327 10.8L4.2666 11.7333L5.19993 8.46667L12.6666 1Z"
        stroke={color}
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.8 2.86664L13.1334 5.19997"
        stroke={color}
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.1333 9.39997V14.0666C13.1333 14.5818 12.7152 15 12.2 15H1.93333C1.41813 15 1 14.5818 1 14.0666V3.79997C1 3.28477 1.41813 2.86664 1.93333 2.86664H6.6"
        stroke={color}
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </StyledSVG>
  );
}

export function IconSubmit({ color = colors.steel[500] }: Props): JSX.Element {
  return (
    <StyledSVG width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M5.28564 6.99988H13.8571"
        stroke={color}
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.4285 3.57123L13.857 6.9998L10.4285 10.4284"
        stroke={color}
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7.85714 13H1.85714C1.384 13 1 12.616 1 12.1429V1.85714C1 1.384 1.384 1 1.85714 1H7.85714"
        stroke={color}
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </StyledSVG>
  );
}

export function IconCompare({ color = colors.steel[500] }: Props): JSX.Element {
  return (
    <StyledSVG width="20" height="9" viewBox="0 0 20 9" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 4.59967H11.8" stroke={color} strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M15.4 1L11.8 4.6L15.4 8.2"
        stroke={color}
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M1 4.59967H8.2" stroke={color} strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M4.6001 1L8.2001 4.6L4.6001 8.2"
        stroke={color}
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </StyledSVG>
  );
}

export function IconFolder({ color = colors.steel[500] }: Props): JSX.Element {
  return (
    <StyledSVG width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M7 3.4L5.8 1H1V11.8C1 12.1183 1.12643 12.4235 1.35147 12.6485C1.57652 12.8736 1.88174 13 2.2 13H13C13.3183 13 13.6235 12.8736 13.8485 12.6485C14.0736 12.4235 14.2 12.1183 14.2 11.8V3.4H7Z"
        stroke={color}
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </StyledSVG>
  );
}

export function IconFolderOpen({ color = colors.steel[500] }: Props): JSX.Element {
  return (
    <StyledSVG width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M13 5.00001V3.00001H6.00001L5.00001 1H1V12"
        stroke={color}
        strokeWidth="1.25"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2 15L5.00001 7H16L13 15H2Z"
        stroke={color}
        strokeWidth="1.25"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </StyledSVG>
  );
}

export function IconPropose({ color = colors.steel[500] }: Props): JSX.Element {
  return (
    <StyledSVG width="13" height="15" viewBox="0 0 13 15" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M7.93333 14H1V1H12.2667V9.66667L7.93333 14Z"
        stroke={color}
        strokeWidth="0.75"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7.93359 14V9.66663H12.2669"
        stroke={color}
        strokeWidth="0.75"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.60034 4.46667H9.66701"
        stroke={color}
        strokeWidth="0.75"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.60034 7.06665H9.66701"
        stroke={color}
        strokeWidth="0.75"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.60034 9.66663H5.33368"
        stroke={color}
        strokeWidth="0.75"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </StyledSVG>
  );
}

export function IconPrepare({ color = colors.steel[500] }: Props): JSX.Element {
  return (
    <StyledSVG width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        // eslint-disable-next-line max-len
        d="M11.2667 1.93311H12.2C12.4475 1.93311 12.6849 2.03144 12.86 2.20647C13.035 2.38151 13.1333 2.6189 13.1333 2.86644V14.0664C13.1333 14.314 13.035 14.5514 12.86 14.7264C12.6849 14.9014 12.4475 14.9998 12.2 14.9998H1.93333C1.6858 14.9998 1.4484 14.9014 1.27337 14.7264C1.09833 14.5514 1 14.314 1 14.0664V2.86644C1 2.6189 1.09833 2.38151 1.27337 2.20647C1.4484 2.03144 1.6858 1.93311 1.93333 1.93311H2.86667"
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M9.40006 1H4.7334V2.86667H9.40006V1Z" stroke={color} strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M4.2666 8.93356L6.13327 10.8002L9.8666 7.06689"
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </StyledSVG>
  );
}

export function IconBind({ color = colors.steel[500], size = 16 }: Props): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1.82764 11.3447L5.9155 7.25684" stroke={color} strokeWidth="0.75" strokeMiterlimit="10" />
      <path
        d="M8.03453 1L3.48281 3.06897L1.82764 11.3448L10.1035 9.68966L12.1725 5.13793L8.03453 1Z"
        stroke={color}
        strokeWidth="1.25"
        strokeMiterlimit="10"
      />
      <path
        // eslint-disable-next-line max-len
        d="M7.67094 7.25717C8.15572 6.77239 8.15572 5.9864 7.67094 5.50162C7.18615 5.01683 6.40016 5.01683 5.91538 5.50162C5.43059 5.9864 5.43059 6.77239 5.91538 7.25717C6.40016 7.74196 7.18615 7.74196 7.67094 7.25717Z"
        stroke={color}
        strokeWidth="1.25"
        strokeMiterlimit="10"
        strokeLinecap="square"
      />
      <path d="M1 13H13.4138" stroke={color} strokeWidth="1" strokeMiterlimit="10" strokeLinecap="square" />
    </StyledSVG>
  );
}

export function IconPencil({ color = colors.steel[500], size = 12, testId }: Props): JSX.Element {
  return (
    <StyledSVG
      width={size}
      height={size}
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      data-testid={testId ? `${testId}-icon-pencil` : 'icon-pencil'}
    >
      <path
        // eslint-disable-next-line max-len
        d="M11.707 1.87897L10.121 0.29297C9.93348 0.105499 9.67917 0.000183105 9.414 0.000183105C9.14884 0.000183105 8.89453 0.105499 8.707 0.29297L7.354 1.64597L10.354 4.64597L11.707 3.29297C11.8945 3.10544 11.9998 2.85113 11.9998 2.58597C11.9998 2.32081 11.8945 2.0665 11.707 1.87897Z"
        fill={color}
      />
      <path d="M6.646 2.35394L1 7.99994L0 11.9999L4 10.9999L9.646 5.35394L6.646 2.35394Z" fill={color} />
    </StyledSVG>
  );
}

export function IconDots({ color = colors.steel[500] }: Props): JSX.Element {
  return (
    <StyledSVG width="12" height="4" viewBox="0 0 12 4" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M6 3.5C6.82843 3.5 7.5 2.82843 7.5 2C7.5 1.17157 6.82843 0.5 6 0.5C5.17157 0.5 4.5 1.17157 4.5 2C4.5 2.82843 5.17157 3.5 6 3.5Z"
        fill={color}
      />
      <path
        d="M10.5 3.5C11.3284 3.5 12 2.82843 12 2C12 1.17157 11.3284 0.5 10.5 0.5C9.67157 0.5 9 1.17157 9 2C9 2.82843 9.67157 3.5 10.5 3.5Z"
        fill={color}
      />
      <path
        d="M1.5 3.5C2.32843 3.5 3 2.82843 3 2C3 1.17157 2.32843 0.5 1.5 0.5C0.671573 0.5 0 1.17157 0 2C0 2.82843 0.671573 3.5 1.5 3.5Z"
        fill={color}
      />
    </StyledSVG>
  );
}

export function IconCaretLeft({ color = colors.steel[400], size = 12 }: Props): JSX.Element {
  return (
    <StyledSVG height={size} viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        // eslint-disable-next-line max-len
        d="M0.840653 6.65935L5.42399 11.2427C5.59687 11.4097 5.82842 11.5021 6.06877 11.5C6.30912 11.4979 6.53903 11.4015 6.70899 11.2315C6.87894 11.0616 6.97535 10.8316 6.97744 10.5913C6.97953 10.351 6.88713 10.1194 6.72015 9.94651L2.7849 6.01126L6.72015 2.07601C6.8077 1.99146 6.87754 1.89031 6.92558 1.77847C6.97362 1.66663 6.99891 1.54635 6.99997 1.42463C7.00102 1.30292 6.97783 1.18221 6.93174 1.06956C6.88565 0.956902 6.81758 0.854555 6.73152 0.768487C6.64545 0.682418 6.5431 0.614353 6.43044 0.568262C6.31779 0.522171 6.19708 0.498978 6.07537 0.500035C5.95365 0.501093 5.83337 0.52638 5.72153 0.574422C5.6097 0.622463 5.50855 0.692298 5.42399 0.779849L0.840654 5.36318C0.668805 5.53508 0.572266 5.7682 0.572266 6.01126C0.572266 6.25433 0.668805 6.48745 0.840653 6.65935V6.65935Z"
        fill={color}
      />
    </StyledSVG>
  );
}

export function IconSquareExternal({ color = colors.steel[400], size = 12, ...otherProps }: Props): JSX.Element {
  return (
    <StyledSVG
      width={size}
      height={size}
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...otherProps}
    >
      <path
        d="M11.25 0H5.99998V1.5H9.44998L4.72498 6.225L5.77498 7.275L10.5 2.55V6H12V0.75C12 0.3 11.7 0 11.25 0Z"
        fill={color}
      />
      <path
        d="M10.5 12H0.75C0.3 12 0 11.7 0 11.25V1.5C0 1.05 0.3 0.75 0.75 0.75H3.75V2.25H1.5V10.5H9.75V8.25H11.25V11.25C11.25 11.7 10.95 12 10.5 12Z"
        fill={color}
      />
    </StyledSVG>
  );
}

export function IconDate({ color = colors.steel[400] }: Props): JSX.Element {
  return (
    <svg width="14" height="16" viewBox="0 0 14 16" role="img" aria-label="Calendar">
      <path
        // eslint-disable-next-line max-len
        d="M0 14.5C0 15.3281 0.671875 16 1.5 16H12.5C13.3281 16 14 15.3281 14 14.5V6H0V14.5ZM2 8.5C2 8.225 2.225 8 2.5 8H5.5C5.775 8 6 8.225 6 8.5V11.5C6 11.775 5.775 12 5.5 12H2.5C2.225 12 2 11.775 2 11.5V8.5ZM12.5 2H11V0.5C11 0.225 10.775 0 10.5 0H9.5C9.225 0 9 0.225 9 0.5V2H5V0.5C5 0.225 4.775 0 4.5 0H3.5C3.225 0 3 0.225 3 0.5V2H1.5C0.671875 2 0 2.67188 0 3.5V5H14V3.5C14 2.67188 13.3281 2 12.5 2Z"
        fill={color}
      />
    </svg>
  );
}

export function IconBrokenHeart({ color = colors.steel[500] }: Props): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
      <title>broken-heart</title>
      <g fill={color}>
        <path
          d="M17,1a6.831,6.831,0,0,0-2.216.378L11,8l5,3-5,7,2-6L8,9l3.258-6.515A6.961,6.961,0,0,0,0,8c0,6.4,10.9,15.4,11.367,15.774a1,1,0,0,0,1.266,0C13.1,23.4,24,14.4,24,8A7.008,7.008,0,0,0,17,1Z"
          fill={color}
        />
      </g>
    </svg>
  );
}

export function IconArchive({ color = colors.steel[500], size = 18 }: Props): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 21 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        // eslint-disable-next-line max-len
        d="M19.901 3.51245L16.2847 0.239727C16.1152 0.0862778 15.8853 4.63397e-05 15.6455 0H4.79671C4.55695 4.63397e-05 4.32704 0.0862778 4.15753 0.239727L0.541259 3.51245C0.371702 3.66586 0.276418 3.87393 0.276367 4.09091V15.5455C0.276367 16.1964 0.562117 16.8208 1.07075 17.2811C1.57939 17.7414 2.26925 18 2.98857 18H17.4537C18.173 18 18.8628 17.7414 19.3715 17.2811C19.8801 16.8208 20.1659 16.1964 20.1659 15.5455V4.09091C20.1658 3.87393 20.0705 3.66586 19.901 3.51245ZM10.2211 14.7273L5.70078 9.81818H8.41298V6.54545H12.0293V9.81818H14.7415L10.2211 14.7273ZM3.36286 3.27273L5.17099 1.63636H15.2712L17.0794 3.27273H3.36286Z"
        fill={color}
      />
    </StyledSVG>
  );
}

export function IconUnarchive({ color = colors.steel[500], size = 18 }: Props): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 21 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        // eslint-disable-next-line max-len
        d="M19.6576 2.23L18.1217 0.55C17.8233 0.21 17.3703 0 16.851 0H3.59128C3.07195 0 2.61891 0.21 2.30952 0.55L0.784655 2.23C0.464213 2.57 0.276367 3.02 0.276367 3.5V16C0.276367 17.1 1.27084 18 2.48631 18H17.9559C19.1714 18 20.1659 17.1 20.1659 16V3.5C20.1659 3.02 19.978 2.57 19.6576 2.23ZM3.85648 2H16.5858L17.5029 3H2.9504L3.85648 2ZM2.48631 16V5H17.9559V16H2.48631ZM5.80123 11H8.61891V14H11.8233V11H14.641L10.2211 7L5.80123 11Z"
        fill={color}
      />
    </StyledSVG>
  );
}

export function IconPdf({ color = colors.steel[400] }: Props): JSX.Element {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M0 4C0 1.79086 1.79086 0 4 0H36C38.2091 0 40 1.79086 40 4V36C40 38.2091 38.2091 40 36 40H4C1.79086 40 0 38.2091 0 36V4Z"
        fill="#F4F8FE"
      />
      <path
        d="M8 14C8 12.8954 8.89543 12 10 12H30C31.1046 12 32 12.8954 32 14V26C32 27.1046 31.1046 28 30 28H10C8.89543 28 8 27.1046 8 26V14Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M14.875 17.2891C15.612 17.2891 16.1497 17.4479 16.4883 17.7656C16.8268 18.0807 16.9961 18.5156 16.9961 19.0703C16.9961 19.3203 16.9583 19.5599 16.8828 19.7891C16.8073 20.0156 16.6836 20.2174 16.5117 20.3945C16.3424 20.5716 16.1159 20.7122 15.832 20.8164C15.5482 20.918 15.1979 20.9688 14.7812 20.9688H14.2617V23H13.0508V17.2891H14.875ZM14.8125 18.2812H14.2617V19.9766H14.6602C14.8867 19.9766 15.0833 19.9466 15.25 19.8867C15.4167 19.8268 15.5456 19.7331 15.6367 19.6055C15.7279 19.4779 15.7734 19.3138 15.7734 19.1133C15.7734 18.832 15.6953 18.6237 15.5391 18.4883C15.3828 18.3503 15.1406 18.2812 14.8125 18.2812ZM22.8125 20.0898C22.8125 20.7331 22.6888 21.2708 22.4414 21.7031C22.1966 22.1328 21.8411 22.457 21.375 22.6758C20.9089 22.8919 20.3477 23 19.6914 23H18.0742V17.2891H19.8672C20.4661 17.2891 20.9857 17.3958 21.4258 17.6094C21.8659 17.8203 22.207 18.1341 22.4492 18.5508C22.6914 18.9648 22.8125 19.4779 22.8125 20.0898ZM21.5547 20.1211C21.5547 19.6992 21.4922 19.3529 21.3672 19.082C21.2448 18.8086 21.0625 18.6068 20.8203 18.4766C20.5807 18.3464 20.2839 18.2812 19.9297 18.2812H19.2852V22H19.8047C20.3958 22 20.8346 21.8424 21.1211 21.5273C21.4102 21.2122 21.5547 20.7435 21.5547 20.1211ZM25.1875 23H23.9961V17.2891H27.2695V18.2812H25.1875V19.7539H27.125V20.7422H25.1875V23Z"
        fill="white"
      />
    </svg>
  );
}

export function IconEdit({ color = 'currentColor' }: Props): JSX.Element {
  return (
    <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        // eslint-disable-next-line max-len
        d="M11.8737 10.1818L12.8182 9.23731C12.9657 9.08974 13.2225 9.19304 13.2225 9.40554V13.697C13.2225 14.4791 12.5879 15.1137 11.8058 15.1137H1.4167C0.634562 15.1137 0 14.4791 0 13.697V3.30784C0 2.52571 0.634562 1.89114 1.4167 1.89114H9.48892C9.69847 1.89114 9.80473 2.14497 9.65715 2.29549L8.71269 3.23996C8.66842 3.28423 8.60939 3.30784 8.54446 3.30784H1.4167V13.697H11.8058V10.3471C11.8058 10.2851 11.8294 10.226 11.8737 10.1818ZM16.4957 4.22574L8.74515 11.9763L6.07704 12.2714C5.30376 12.357 4.64559 11.7047 4.73118 10.9255L5.02632 8.25743L12.7768 0.506912C13.4527 -0.168971 14.5448 -0.168971 15.2177 0.506912L16.4927 1.78194C17.1686 2.45782 17.1686 3.55281 16.4957 4.22574ZM13.5796 5.13774L11.8648 3.42295L6.38104 8.9097L6.16558 10.837L8.09288 10.6215L13.5796 5.13774ZM15.4922 2.78543L14.2171 1.51041C14.0961 1.3894 13.8984 1.3894 13.7803 1.51041L12.8683 2.4224L14.5831 4.1372L15.4951 3.2252C15.6132 3.10124 15.6132 2.90644 15.4922 2.78543Z"
        fill={color}
      />
    </svg>
  );
}

export function IconAttach(): JSX.Element {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path
        // eslint-disable-next-line max-len
        d="M10.217 6.63599L7.25398 9.59999C6.677 10.1774 5.8943 10.5019 5.07804 10.5022C4.26179 10.5025 3.47886 10.1785 2.90148 9.60149C2.32411 9.02451 1.99958 8.2418 1.9993 7.42555C1.99902 6.6093 2.323 5.82636 2.89998 5.24899L6.12398 2.02699C6.46739 1.70008 6.92336 1.51775 7.39748 1.51775C7.87161 1.51775 8.32758 1.70008 8.67098 2.02699C8.83834 2.19416 8.97111 2.39268 9.06169 2.6112C9.15227 2.82971 9.1989 3.06394 9.1989 3.30049C9.1989 3.53704 9.15227 3.77126 9.06169 3.98978C8.97111 4.2083 8.83834 4.40682 8.67098 4.57399L5.70798 7.53599C5.60937 7.63436 5.47577 7.68961 5.33648 7.68961C5.19719 7.68961 5.06359 7.63436 4.96498 7.53599C4.86709 7.43712 4.81218 7.30362 4.81218 7.16449C4.81218 7.02536 4.86709 6.89185 4.96498 6.79299L7.15398 4.59999C7.22767 4.53133 7.28677 4.44853 7.32776 4.35653C7.36876 4.26453 7.3908 4.16521 7.39257 4.06451C7.39435 3.96381 7.37583 3.86378 7.33811 3.77039C7.30038 3.677 7.24424 3.59217 7.17302 3.52095C7.1018 3.44973 7.01697 3.39359 6.92358 3.35587C6.83019 3.31814 6.73016 3.29962 6.62946 3.3014C6.52876 3.30317 6.42944 3.32522 6.33744 3.36621C6.24545 3.4072 6.16264 3.4663 6.09398 3.53999L3.89998 5.73299C3.5198 6.11344 3.30631 6.62934 3.3065 7.1672C3.30669 7.70505 3.52053 8.2208 3.90098 8.60099C4.28144 8.98118 4.79734 9.19466 5.33519 9.19447C5.87304 9.19428 6.38879 8.98044 6.76898 8.59999L9.73098 5.63499C10.35 5.01597 10.6978 4.17641 10.6978 3.30099C10.6978 2.42557 10.35 1.586 9.73098 0.966988C9.11197 0.347973 8.2724 0.000213623 7.39698 0.000213623C6.52156 0.000213623 5.682 0.347973 5.06298 0.966988L1.84298 4.18799C0.984742 5.0461 0.502535 6.20999 0.502441 7.42363C0.502348 8.63728 0.984375 9.80125 1.84248 10.6595C2.70059 11.5177 3.86449 11.9999 5.07813 12C6.29177 12.0001 7.45574 11.5181 8.31398 10.66L11.277 7.69999C11.4095 7.55781 11.4816 7.36977 11.4782 7.17547C11.4747 6.98116 11.396 6.79578 11.2586 6.65837C11.1212 6.52095 10.9358 6.44224 10.7415 6.43881C10.5472 6.43539 10.3592 6.50751 10.217 6.63999V6.63599Z"
        fill="#8B8F98"
      />
    </svg>
  );
}

export const IconClose = forwardRef<SVGSVGElement, SVGProps<SVGSVGElement>>((props, ref) => {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" ref={ref} {...props}>
      <path
        // eslint-disable-next-line max-len
        d="M11.7849 1.77117L7.55606 6L11.7849 10.2288C12.0717 10.5156 12.0717 10.9794 11.7849 11.2662L11.2662 11.7849C10.9794 12.0717 10.5156 12.0717 10.2288 11.7849L6 7.55606L1.77117 11.7849C1.48436 12.0717 1.02059 12.0717 0.733791 11.7849L0.215103 11.2662C-0.071701 10.9794 -0.071701 10.5156 0.215103 10.2288L4.44394 6L0.215103 1.77117C-0.071701 1.48436 -0.071701 1.02059 0.215103 0.733791L0.733791 0.215103C1.02059 -0.071701 1.48436 -0.071701 1.77117 0.215103L6 4.44394L10.2288 0.215103C10.5156 -0.071701 10.9794 -0.071701 11.2662 0.215103L11.7849 0.733791C12.0656 1.02059 12.0656 1.48436 11.7849 1.77117Z"
        fill="currentColor"
      />
    </svg>
  );
});

export function IconRemove({ color = 'currentColor', size = 16 }: { color?: string; size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 14 16">
      <path
        // eslint-disable-next-line max-len
        d="M13.5 0.999831H9.75L9.45625 0.415456C9.39402 0.290522 9.29817 0.185431 9.17947 0.112005C9.06078 0.0385783 8.92395 -0.0002701 8.78438 -0.000169997H5.2125C5.07324 -0.000705327 4.93665 0.0379982 4.81838 0.111506C4.7001 0.185014 4.60492 0.290354 4.54375 0.415456L4.25 0.999831H0.5C0.367392 0.999831 0.240215 1.05251 0.146447 1.14628C0.0526784 1.24005 0 1.36722 0 1.49983L0 2.49983C0 2.63244 0.0526784 2.75962 0.146447 2.85339C0.240215 2.94716 0.367392 2.99983 0.5 2.99983H13.5C13.6326 2.99983 13.7598 2.94716 13.8536 2.85339C13.9473 2.75962 14 2.63244 14 2.49983V1.49983C14 1.36722 13.9473 1.24005 13.8536 1.14628C13.7598 1.05251 13.6326 0.999831 13.5 0.999831V0.999831ZM1.6625 14.5936C1.68635 14.9744 1.85443 15.3318 2.13252 15.5931C2.41061 15.8543 2.77781 15.9998 3.15937 15.9998H10.8406C11.2222 15.9998 11.5894 15.8543 11.8675 15.5931C12.1456 15.3318 12.3137 14.9744 12.3375 14.5936L13 3.99983H1L1.6625 14.5936Z"
        fill={color}
      />
    </svg>
  );
}

export function IconDuplicate({ color = '#546A83' }: { color?: string }): JSX.Element {
  return (
    <svg width="18" height="22" viewBox="0 0 18 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        // eslint-disable-next-line max-len
        d="M16 0C16.5304 0 17.0391 0.210714 17.4142 0.585786C17.7893 0.960859 18 1.46957 18 2V14C18 14.5304 17.7893 15.0391 17.4142 15.4142C17.0391 15.7893 16.5304 16 16 16H14V18C14 18.5304 13.7893 19.0391 13.4142 19.4142C13.0391 19.7893 12.5304 20 12 20H2C1.46957 20 0.960859 19.7893 0.585786 19.4142C0.210714 19.0391 0 18.5304 0 18V6C0 5.46957 0.210714 4.96086 0.585786 4.58579C0.960859 4.21071 1.46957 4 2 4H4V2C4 1.46957 4.21071 0.960859 4.58579 0.585786C4.96086 0.210714 5.46957 0 6 0H16ZM12 6H2V18H12V6ZM7 13C7.26522 13 7.51957 13.1054 7.70711 13.2929C7.89464 13.4804 8 13.7348 8 14C8 14.2652 7.89464 14.5196 7.70711 14.7071C7.51957 14.8946 7.26522 15 7 15H5C4.73478 15 4.48043 14.8946 4.29289 14.7071C4.10536 14.5196 4 14.2652 4 14C4 13.7348 4.10536 13.4804 4.29289 13.2929C4.48043 13.1054 4.73478 13 5 13H7ZM16 2H6V4H12C12.5304 4 13.0391 4.21071 13.4142 4.58579C13.7893 4.96086 14 5.46957 14 6V14H16V2ZM9 9C9.25488 9.00028 9.50003 9.09788 9.68537 9.27285C9.8707 9.44782 9.98223 9.68695 9.99717 9.94139C10.0121 10.1958 9.92933 10.4464 9.76574 10.6418C9.60215 10.8373 9.3701 10.9629 9.117 10.993L9 11H5C4.74512 10.9997 4.49997 10.9021 4.31463 10.7272C4.1293 10.5522 4.01777 10.313 4.00283 10.0586C3.98789 9.80416 4.07067 9.55362 4.23426 9.35817C4.39786 9.16271 4.6299 9.0371 4.883 9.007L5 9H9Z"
        fill={color}
      />
    </svg>
  );
}

export function IconMore({ color = 'currentColor' }: { color?: string }): JSX.Element {
  return (
    <svg width="4" height="16" viewBox="0 0 4 16">
      <circle cx="2" cy="2" r="2" fill={color} />
      <circle cx="2" cy="8" r="2" fill={color} />
      <circle cx="2" cy="14" r="2" fill={color} />
    </svg>
  );
}

export function IconAdd({ color = 'currentColor' }: Pick<Props, 'color'>): JSX.Element {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12">
      <path
        // eslint-disable-next-line max-len
        d="M11.1429 4.71429H7.28571V0.857143C7.28571 0.383839 6.90188 0 6.42857 0H5.57143C5.09813 0 4.71429 0.383839 4.71429 0.857143V4.71429H0.857143C0.383839 4.71429 0 5.09813 0 5.57143V6.42857C0 6.90188 0.383839 7.28571 0.857143 7.28571H4.71429V11.1429C4.71429 11.6162 5.09813 12 5.57143 12H6.42857C6.90188 12 7.28571 11.6162 7.28571 11.1429V7.28571H11.1429C11.6162 7.28571 12 6.90188 12 6.42857V5.57143C12 5.09813 11.6162 4.71429 11.1429 4.71429Z"
        fill={color}
      />
    </svg>
  );
}

export function IconChevronLeft({ color = '#444444', size = 12 }: Pick<Props, 'color' | 'size'>): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12">
      <g transform="translate(0, 0)">
        <path
          d="M8.625.219A1,1,0,0,0,7.219.375l-4,5a1,1,0,0,0,0,1.25l4,5a1,1,0,0,0,1.562-1.25L5.281,6l3.5-4.375A1,1,0,0,0,8.625.219Z"
          fill={color}
        />
      </g>
    </svg>
  );
}

export function IconChevronRight({ color = '#444444', size = 12 }: Pick<Props, 'color' | 'size'>): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12">
      <g transform="translate(0, 0)">
        <path
          d="M4.781.375a1,1,0,0,0-1.562,1.25L6.719,6l-3.5,4.375a1,1,0,0,0,1.562,1.25l4-5a1,1,0,0,0,0-1.25Z"
          fill={color}
        />
      </g>
    </svg>
  );
}

export function IconProducerBook({ color }: Props): JSX.Element {
  return (
    <svg height="32" width="32" viewBox="0 0 48 48">
      <g fill={color}>
        <rect height="8" width="2" x="44" y="9" />
        <rect height="8" width="2" x="44" y="22" />
        <path d="M9,1H5C4.447,1,4,1.448,4,2v44c0,0.552,0.447,1,1,1h4V1z" />
        <path
          // eslint-disable-next-line max-len
          d="M35,1H11v46h24c3.859,0,7-3.14,7-7V8C42,4.14,38.859,1,35,1z M26,15c2.209,0,4,1.791,4,4 c0,2.209-1.791,4-4,4s-4-1.791-4-4C22,16.791,23.791,15,26,15z M19,31c0-3.314,2.686-6,6-6h2c3.314,0,6,2.686,6,6H19z"
          fill={color}
        />
      </g>
    </svg>
  );
}

export function IconMail({ color = 'currentColor' }: Pick<Props, 'color'>): JSX.Element {
  return (
    <svg width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M14 0H2C1.46957 0 0.960859 0.210714 0.585786 0.585786C0.210714 0.960859 0 1.46957 0 2L0 2.4L8 6.9L16 2.5V2C16 1.46957 15.7893 0.960859 15.4142 0.585786C15.0391 0.210714 14.5304 0 14 0Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M7.5 8.9002L0 4.7002V12.0002C0 12.5306 0.210714 13.0393 0.585786 13.4144C0.960859 13.7895 1.46957 14.0002 2 14.0002H14C14.5304 14.0002 15.0391 13.7895 15.4142 13.4144C15.7893 13.0393 16 12.5306 16 12.0002V4.7002L8.5 8.9002C8.3424 8.96944 8.17214 9.00519 8 9.00519C7.82786 9.00519 7.6576 8.96944 7.5 8.9002Z"
        fill={color}
      />
    </svg>
  );
}

export function IconMoveLeft({ color = 'currentColor' }: Pick<Props, 'color'>): JSX.Element {
  return (
    <svg width="23" height="24" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M1.00248 11.6994L11.3147 11.6994"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        // eslint-disable-next-line max-len
        d="M8.60709 8.49712L8.60896 7.21209C8.61056 6.10731 9.50762 5.21309 10.6124 5.21499L17.1119 5.2262C18.2162 5.22811 19.11 6.12474 19.1084 7.2291L19.1023 11.4797L19.0947 16.7292C19.0931 17.834 18.196 18.7282 17.0912 18.7263L10.5888 18.715C9.48562 18.7131 8.5923 17.8183 8.5923 16.7151L8.5923 14.9359"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4.5007 15.2217L1.0007 11.7217L4.5007 8.22168"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function IconMoveRight({ color = 'currentColor' }: Pick<Props, 'color'>): JSX.Element {
  return (
    <svg width="23" height="24" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M21.812 12.2366L11.4998 12.2366"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        // eslint-disable-next-line max-len
        d="M14.2074 15.4389L14.2056 16.7239C14.204 17.8287 13.3069 18.7229 12.2021 18.721L5.70264 18.7098C4.59829 18.7079 3.7045 17.8113 3.7061 16.7069L3.71226 12.4564L3.71986 7.20687C3.72146 6.10208 4.61852 5.20786 5.72331 5.20977L12.2257 5.22098C13.3289 5.22289 14.2222 6.11776 14.2222 7.22098L14.2222 9.00015"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18.3137 8.71436L21.8137 12.2144L18.3137 15.7144"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function IconMoveLeftRight({ color = 'currentColor' }: Pick<Props, 'color'>): JSX.Element {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M22.3121 10.1324L13.9999 10.1324"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        // eslint-disable-next-line max-len
        d="M16.2025 12.9999L16.2025 16.7204C16.2025 17.8265 15.3048 18.7225 14.1987 18.7204L8.69927 18.71C7.59619 18.7079 6.70309 17.813 6.70309 16.71L6.70309 16.4999M16.2221 7.49989L16.2221 7.22039C16.2221 6.11731 15.329 5.22249 14.2259 5.22039L8.72645 5.2099C7.6204 5.2078 6.72264 6.10384 6.72264 7.2099L6.72264 11.4999"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19.7042 7.5L22.3143 10.1101L19.7035 12.7202"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M1.00231 14.1326L9.31458 14.1327"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.65394 16.8097L1.00012 14.1549L3.65491 11.5001"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function TreeViewExpandedIcon(): JSX.Element {
  return (
    <svg width="13" height="14" viewBox="0 0 13 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0.5" y="1" width="12" height="12" rx="1.5" stroke={colors.steel[400]} />
      <line x1="2" y1="7" x2="11" y2="7" stroke={colors.steel[400]} />
    </svg>
  );
}

export function TreeViewCollapsedIcon(): JSX.Element {
  return (
    <svg width="13" height="14" viewBox="0 0 13 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0.5" y="1" width="12" height="12" rx="1.5" fill={colors.steel[400]} stroke={colors.steel[400]} />
      <line x1="2" y1="7" x2="11" y2="7" stroke="white" />
      <line x1="6.5" y1="2.5" x2="6.5" y2="11.5" stroke="white" />
    </svg>
  );
}

export function IconWandStar(): JSX.Element {
  return (
    /* eslint-disable max-len */
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M16.15 13.0501L14 16.5001C13.8166 16.7834 13.5626 16.9001 13.238 16.8501C12.9133 16.8001 12.709 16.6084 12.625 16.2751L11.925 13.4751L5.09995 20.3001C4.91662 20.4834 4.68762 20.5794 4.41295 20.5881C4.13828 20.5967 3.90062 20.5007 3.69995 20.3001C3.51662 20.1167 3.42495 19.8834 3.42495 19.6001C3.42495 19.3167 3.51662 19.0834 3.69995 18.9001L10.525 12.0501L7.72495 11.3501C7.39162 11.2667 7.19995 11.0627 7.14995 10.7381C7.09995 10.4134 7.21662 10.1591 7.49995 9.97505L10.95 7.85005L10.65 3.77505C10.6166 3.44172 10.75 3.20005 11.05 3.05005C11.35 2.90005 11.625 2.93339 11.875 3.15005L15 5.77505L18.775 4.25005C19.0916 4.11672 19.3666 4.16672 19.6 4.40005C19.8333 4.63339 19.8833 4.90839 19.75 5.22505L18.225 9.00005L20.85 12.1001C21.0666 12.3501 21.1 12.6251 20.95 12.9251C20.8 13.2251 20.5583 13.3584 20.225 13.3251L16.15 13.0501ZM3.34995 6.35005C3.24995 6.25005 3.19995 6.13339 3.19995 6.00005C3.19995 5.86672 3.24995 5.75005 3.34995 5.65005L4.64995 4.35005C4.74995 4.25005 4.86662 4.20005 4.99995 4.20005C5.13328 4.20005 5.24995 4.25005 5.34995 4.35005L6.64995 5.65005C6.74995 5.75005 6.79995 5.86672 6.79995 6.00005C6.79995 6.13339 6.74995 6.25005 6.64995 6.35005L5.34995 7.65005C5.24995 7.75005 5.13328 7.80005 4.99995 7.80005C4.86662 7.80005 4.74995 7.75005 4.64995 7.65005L3.34995 6.35005ZM13.875 12.9251L15.075 10.9501L17.4 11.1251L15.9 9.35005L16.775 7.20005L14.625 8.07505L12.85 6.60005L13.025 8.90005L11.05 10.1251L13.3 10.6751L13.875 12.9251ZM17.65 20.6501L16.35 19.3501C16.25 19.2501 16.2 19.1334 16.2 19.0001C16.2 18.8667 16.25 18.7501 16.35 18.6501L17.65 17.3501C17.75 17.2501 17.8666 17.2001 18 17.2001C18.1333 17.2001 18.25 17.2501 18.35 17.3501L19.65 18.6501C19.75 18.7501 19.7999 18.8667 19.7999 19.0001C19.7999 19.1334 19.75 19.2501 19.65 19.3501L18.35 20.6501C18.25 20.7501 18.1333 20.8001 18 20.8001C17.8666 20.8001 17.75 20.7501 17.65 20.6501Z"
        fill="#008CFB"
      />
    </svg>
  );
}

export function IconCollapseOutline(): JSX.Element {
  return (
    /* eslint-disable max-len */
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M18.9999 9.00019H16.4199L19.7099 5.71019C19.8982 5.52188 20.004 5.26649 20.004 5.00019C20.004 4.73388 19.8982 4.47849 19.7099 4.29019C19.5216 4.10188 19.2662 3.99609 18.9999 3.99609C18.7336 3.99609 18.4782 4.10188 18.2899 4.29019L14.9999 7.57019V5.00019C14.9999 4.73497 14.8946 4.48062 14.707 4.29308C14.5195 4.10554 14.2652 4.00019 13.9999 4.00019C13.7347 4.00019 13.4804 4.10554 13.2928 4.29308C13.1053 4.48062 12.9999 4.73497 12.9999 5.00019V10.0002C12.9999 10.2654 13.1053 10.5198 13.2928 10.7073C13.4804 10.8948 13.7347 11.0002 13.9999 11.0002H18.9999C19.2652 11.0002 19.5195 10.8948 19.707 10.7073C19.8946 10.5198 19.9999 10.2654 19.9999 10.0002C19.9999 9.73497 19.8946 9.48062 19.707 9.29308C19.5195 9.10554 19.2652 9.00019 18.9999 9.00019ZM9.99994 13.0002H4.99994C4.73472 13.0002 4.48037 13.1055 4.29283 13.2931C4.1053 13.4806 3.99994 13.735 3.99994 14.0002C3.99994 14.2654 4.1053 14.5198 4.29283 14.7073C4.48037 14.8948 4.73472 15.0002 4.99994 15.0002H7.56994L4.28994 18.2902C4.19621 18.3831 4.12182 18.4937 4.07105 18.6156C4.02028 18.7375 3.99414 18.8682 3.99414 19.0002C3.99414 19.1322 4.02028 19.2629 4.07105 19.3848C4.12182 19.5066 4.19621 19.6172 4.28994 19.7102C4.3829 19.8039 4.4935 19.8783 4.61536 19.9291C4.73722 19.9798 4.86793 20.006 4.99994 20.006C5.13195 20.006 5.26266 19.9798 5.38452 19.9291C5.50638 19.8783 5.61698 19.8039 5.70994 19.7102L8.99994 16.4202V19.0002C8.99994 19.2654 9.1053 19.5198 9.29283 19.7073C9.48037 19.8948 9.73472 20.0002 9.99994 20.0002C10.2652 20.0002 10.5195 19.8948 10.707 19.7073C10.8946 19.5198 10.9999 19.2654 10.9999 19.0002V14.0002C10.9999 13.735 10.8946 13.4806 10.707 13.2931C10.5195 13.1055 10.2652 13.0002 9.99994 13.0002Z"
        fill="#008CFB"
      />
    </svg>
  );
}

export function IconExpandOutline(): JSX.Element {
  return (
    /* eslint-disable max-len */
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M20 5C20 4.73478 19.8946 4.48043 19.7071 4.29289C19.5196 4.10536 19.2652 4 19 4H14C13.7348 4 13.4804 4.10536 13.2929 4.29289C13.1054 4.48043 13 4.73478 13 5C13 5.26522 13.1054 5.51957 13.2929 5.70711C13.4804 5.89464 13.7348 6 14 6H16.57L13.29 9.29C13.1963 9.38296 13.1219 9.49356 13.0711 9.61542C13.0203 9.73728 12.9942 9.86799 12.9942 10C12.9942 10.132 13.0203 10.2627 13.0711 10.3846C13.1219 10.5064 13.1963 10.617 13.29 10.71C13.383 10.8037 13.4936 10.8781 13.6154 10.9289C13.7373 10.9797 13.868 11.0058 14 11.0058C14.132 11.0058 14.2627 10.9797 14.3846 10.9289C14.5064 10.8781 14.617 10.8037 14.71 10.71L18 7.42V10C18 10.2652 18.1054 10.5196 18.2929 10.7071C18.4804 10.8946 18.7348 11 19 11C19.2652 11 19.5196 10.8946 19.7071 10.7071C19.8946 10.5196 20 10.2652 20 10V5ZM10.71 13.29C10.617 13.1963 10.5064 13.1219 10.3846 13.0711C10.2627 13.0203 10.132 12.9942 10 12.9942C9.86799 12.9942 9.73728 13.0203 9.61542 13.0711C9.49356 13.1219 9.38296 13.1963 9.29 13.29L6 16.57V14C6 13.7348 5.89464 13.4804 5.70711 13.2929C5.51957 13.1054 5.26522 13 5 13C4.73478 13 4.48043 13.1054 4.29289 13.2929C4.10536 13.4804 4 13.7348 4 14V19C4 19.2652 4.10536 19.5196 4.29289 19.7071C4.48043 19.8946 4.73478 20 5 20H10C10.2652 20 10.5196 19.8946 10.7071 19.7071C10.8946 19.5196 11 19.2652 11 19C11 18.7348 10.8946 18.4804 10.7071 18.2929C10.5196 18.1054 10.2652 18 10 18H7.42L10.71 14.71C10.8037 14.617 10.8781 14.5064 10.9289 14.3846C10.9797 14.2627 11.0058 14.132 11.0058 14C11.0058 13.868 10.9797 13.7373 10.9289 13.6154C10.8781 13.4936 10.8037 13.383 10.71 13.29Z"
        fill="#008CFB"
      />
    </svg>
  );
}

export function IconPolicy({ size = 24 }: Pick<Props, 'size'>): JSX.Element {
  return (
    /* eslint-disable max-len */
    <StyledSVG width={size} height={size} viewBox="0 0 36 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M30.939 27.3327H8.83951C8.02551 27.3327 7.36621 26.736 7.36621 25.9993V1.99935C7.36621 1.26268 8.02551 0.666016 8.83951 0.666016H30.939C31.753 0.666016 32.4123 1.26268 32.4123 1.99935V25.9993C32.4123 26.736 31.753 27.3327 30.939 27.3327Z"
        fill="#257FFB"
      />
      <path
        d="M26.519 31.3327H4.41959C3.60559 31.3327 2.94629 30.736 2.94629 29.9993V5.99935C2.94629 5.26268 3.60559 4.66602 4.41959 4.66602H26.519C27.333 4.66602 27.9923 5.26268 27.9923 5.99935V29.9993C27.9923 30.736 27.333 31.3327 26.519 31.3327Z"
        fill="#A8CBFB"
      />
      <path
        d="M21.3629 12.6654H9.57649C9.16986 12.6654 8.83984 12.3667 8.83984 11.9987C8.83984 11.6307 9.16986 11.332 9.57649 11.332H21.3629C21.7695 11.332 22.0995 11.6307 22.0995 11.9987C22.0995 12.3667 21.7695 12.6654 21.3629 12.6654Z"
        fill="#F4F5F6"
      />
      <path
        d="M21.3629 17.9993H9.57649C9.16986 17.9993 8.83984 17.7007 8.83984 17.3327C8.83984 16.9647 9.16986 16.666 9.57649 16.666H21.3629C21.7695 16.666 22.0995 16.9647 22.0995 17.3327C22.0995 17.7007 21.7695 17.9993 21.3629 17.9993Z"
        fill="#F4F5F6"
      />
      <path
        d="M15.4697 23.3333H9.57649C9.16986 23.3333 8.83984 23.0347 8.83984 22.6667C8.83984 22.2987 9.16986 22 9.57649 22H15.4697C15.8763 22 16.2063 22.2987 16.2063 22.6667C16.2063 23.0347 15.8763 23.3333 15.4697 23.3333Z"
        fill="#F4F5F6"
      />
    </StyledSVG>
    /* eslint-enable max-len */
  );
}

export function IconSheetPlus(): JSX.Element {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        // eslint-disable-next-line max-len
        d="M18.8398 8.01071L15.1716 4.16786C15.0693 4.06071 14.9312 4 14.7864 4H7.54545C7.24375 4 7 4.25536 7 4.57143V19.4286C7 19.7446 7.24375 20 7.54545 20H18.4545C18.7563 20 19 19.7446 19 19.4286V8.41607C19 8.26429 18.942 8.11786 18.8398 8.01071ZM17.742 8.67857H14.5341V5.31786L17.742 8.67857ZM17.7727 18.7143H8.22727V5.28571H13.375V9.14286C13.375 9.34177 13.4504 9.53254 13.5847 9.67319C13.7189 9.81384 13.901 9.89286 14.0909 9.89286H17.7727V18.7143Z"
        fill="#546A83"
      />
      <circle cx="7.5" cy="12.5" r="5.5" fill="white" />
      <path d="M8.375 11.625V9H6.625V11.625H4V13.375H6.625V16H8.375V13.375H11V11.625H8.375Z" fill="#546A83" />
    </svg>
  );
}

export function IconEyeOpen({ size = 18, color = colors.steel[400] }: Pick<Props, 'size' | 'color'>): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 18 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        // eslint-disable-next-line max-len
        d="M9.16618 1.66715C12.3245 1.66715 15.1412 3.44215 16.5162 6.25049C15.1412 9.05882 12.3245 10.8338 9.16618 10.8338C6.00784 10.8338 3.19118 9.05882 1.81618 6.25049C3.19118 3.44215 6.00784 1.66715 9.16618 1.66715ZM9.16618 0.000488281C4.99951 0.000488281 1.44118 2.59215 -0.000488281 6.25049C1.44118 9.90882 4.99951 12.5005 9.16618 12.5005C13.3328 12.5005 16.8912 9.90882 18.3328 6.25049C16.8912 2.59215 13.3328 0.000488281 9.16618 0.000488281ZM9.16618 4.16715C10.3162 4.16715 11.2495 5.10049 11.2495 6.25049C11.2495 7.40049 10.3162 8.33382 9.16618 8.33382C8.01618 8.33382 7.08284 7.40049 7.08284 6.25049C7.08284 5.10049 8.01618 4.16715 9.16618 4.16715ZM9.16618 2.50049C7.09951 2.50049 5.41618 4.18382 5.41618 6.25049C5.41618 8.31715 7.09951 10.0005 9.16618 10.0005C11.2328 10.0005 12.9162 8.31715 12.9162 6.25049C12.9162 4.18382 11.2328 2.50049 9.16618 2.50049Z"
        fill={color}
      />
    </svg>
  );
}

export function IconEyeClosed({ size = 24, color = colors.steel[400] }: Pick<Props, 'size' | 'color'>): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        // eslint-disable-next-line max-len
        d="M12.1662 7.66715C15.3245 7.66715 18.1412 9.44215 19.5162 12.2505C18.1412 15.0588 15.3245 16.8338 12.1662 16.8338C9.00784 16.8338 6.19118 15.0588 4.81618 12.2505C6.19118 9.44215 9.00784 7.66715 12.1662 7.66715ZM12.1662 6.00049C7.99951 6.00049 4.44118 8.59215 2.99951 12.2505C4.44118 15.9088 7.99951 18.5005 12.1662 18.5005C16.3328 18.5005 19.8912 15.9088 21.3328 12.2505C19.8912 8.59215 16.3328 6.00049 12.1662 6.00049ZM12.1662 10.1672C13.3162 10.1672 14.2495 11.1005 14.2495 12.2505C14.2495 13.4005 13.3162 14.3338 12.1662 14.3338C11.0162 14.3338 10.0828 13.4005 10.0828 12.2505C10.0828 11.1005 11.0162 10.1672 12.1662 10.1672ZM12.1662 8.50049C10.0995 8.50049 8.41618 10.1838 8.41618 12.2505C8.41618 14.3172 10.0995 16.0005 12.1662 16.0005C14.2328 16.0005 15.9162 14.3172 15.9162 12.2505C15.9162 10.1838 14.2328 8.50049 12.1662 8.50049Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M4.85177 18.3751L4.66838 18.5461L4.83999 18.729L5.52426 19.4582L5.69481 19.6399L5.87707 19.4699L20.9425 5.41914L21.1259 5.2481L20.9543 5.06523L20.27 4.33607L20.0995 4.15434L19.9172 4.32433L4.85177 18.3751Z"
        fill={color}
        stroke="white"
        strokeWidth="0.5"
      />
    </svg>
  );
}

export function IconBug({ size = 32, color = colors.fire[500] }: Pick<Props, 'size' | 'color'>): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 32 32" fill="none">
      <g clipPath="url(#clip0_106_1776)">
        <path
          // eslint-disable-next-line max-len
          d="M32 18V16H27.902C27.601 13.146 26.585 10.512 25.015 8.399L27.707 5.707L26.293 4.293L23.704 6.882C23.14 6.325 22.527 5.827 21.872 5.392C21.684 4.689 21.369 4.043 20.943 3.471L22.707 1.707L21.293 0.292999L19.495 2.091C18.496 1.413 17.295 0.999999 16 0.999999C14.705 0.999999 13.504 1.413 12.505 2.091L10.707 0.292999L9.293 1.707L11.057 3.471C10.632 4.043 10.316 4.689 10.128 5.392C9.473 5.828 8.86 6.325 8.296 6.882L5.707 4.293L4.293 5.707L6.986 8.4C5.416 10.513 4.4 13.148 4.099 16H0V18H4.009C4.048 21.077 4.919 23.913 6.364 26.222L3.293 29.293L4.707 30.707L7.549 27.865C9.72 30.419 12.706 32 16 32C19.294 32 22.28 30.419 24.451 27.865L27.293 30.707L28.707 29.293L25.636 26.222C27.081 23.913 27.952 21.077 27.991 18H32ZM10.5 23C9.119 23 8 21.881 8 20.5C8 19.119 9.119 18 10.5 18C11.881 18 13 19.119 13 20.5C13 21.881 11.881 23 10.5 23ZM12 15C10.895 15 10 14.105 10 13C10 11.895 10.895 11 12 11C13.105 11 14 11.895 14 13C14 14.105 13.105 15 12 15ZM12.088 5.729C12.598 4.138 14.18 3 16 3C17.82 3 19.402 4.138 19.912 5.729C19.07 6.227 17.453 7.016 16 7.016C14.547 7.016 12.93 6.227 12.088 5.729ZM18 13C18 11.895 18.895 11 20 11C21.105 11 22 11.895 22 13C22 14.105 21.105 15 20 15C18.895 15 18 14.105 18 13ZM21.5 23C20.119 23 19 21.881 19 20.5C19 19.119 20.119 18 21.5 18C22.881 18 24 19.119 24 20.5C24 21.881 22.881 23 21.5 23Z"
          fill={color}
        />
      </g>
      <defs>
        <clipPath id="clip0_106_1776">
          <rect width={size} height={size} fill="white" />
        </clipPath>
      </defs>
    </StyledSVG>
  );
}

export function IconNotFound({ size = 32, color = colors.fire[500] }: Pick<Props, 'size' | 'color'>): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 32 32" fill="none">
      <path d="M13 0H9V2H13V0Z" fill={color} />
      <path
        // eslint-disable-next-line max-len
        d="M11.05 10.05C8.31601 12.784 8.31601 17.216 11.05 19.949C13.784 22.683 18.216 22.683 20.95 19.949C23.684 17.215 23.684 12.783 20.95 10.05C18.216 7.31699 13.784 7.31699 11.05 10.05ZM19.536 17.121L18.122 18.535L16 16.414L13.879 18.535L12.465 17.121L14.586 15L12.465 12.879L13.879 11.465L16 13.586L18.121 11.465L19.535 12.879L17.414 15L19.536 17.121Z"
        fill={color}
      />
      <path
        d="M19 27L13 22V26H7.5C4.468 26 2 23.533 2 20.5C2 17.467 4.468 15 7.5 15H9V13H7.5C3.364 13 0 16.364 0 20.5C0 24.636 3.364 28 7.5 28H13V32L19 27Z"
        fill={color}
      />
      <path
        d="M24.5 0H16V2H24.5C27.532 2 30 4.467 30 7.5C30 10.533 27.532 13 24.5 13H23V15H24.5C28.636 15 32 11.636 32 7.5C32 3.364 28.636 0 24.5 0Z"
        fill={color}
      />
    </StyledSVG>
  );
}

export function IconNotAllowed({ size = 32, color = colors.fire[500] }: Pick<Props, 'size' | 'color'>): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 32 32" fill="none">
      <path
        d="M16 11C15.4696 11 14.9609 11.2107 14.5858 11.5858C14.2107 11.9609 14 12.4696 14 13V15H18V13C18 12.4696 17.7893 11.9609 17.4142 11.5858C17.0391 11.2107 16.5304 11 16 11V11Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M28.243 4.03L16.243 1.03C16.0836 0.989019 15.9164 0.989019 15.757 1.03L3.757 4.03C3.54075 4.08417 3.34881 4.20905 3.21166 4.38479C3.0745 4.56054 3.00001 4.77707 3 5V19C3 22.4478 4.36964 25.7544 6.80761 28.1924C9.24558 30.6304 12.5522 32 16 32C19.4478 32 22.7544 30.6304 25.1924 28.1924C27.6304 25.7544 29 22.4478 29 19V5C29 4.77707 28.9255 4.56054 28.7883 4.38479C28.6512 4.20905 28.4592 4.08417 28.243 4.03V4.03ZM22 23C22 23.2652 21.8946 23.5196 21.7071 23.7071C21.5196 23.8946 21.2652 24 21 24H11C10.7348 24 10.4804 23.8946 10.2929 23.7071C10.1054 23.5196 10 23.2652 10 23V16C10 15.7348 10.1054 15.4804 10.2929 15.2929C10.4804 15.1054 10.7348 15 11 15H12V13C12 11.9391 12.4214 10.9217 13.1716 10.1716C13.9217 9.42143 14.9391 9 16 9C17.0609 9 18.0783 9.42143 18.8284 10.1716C19.5786 10.9217 20 11.9391 20 13V15H21C21.2652 15 21.5196 15.1054 21.7071 15.2929C21.8946 15.4804 22 15.7348 22 16V23Z"
        fill={color}
      />
    </StyledSVG>
  );
}

export function IconNetworkLost({ size = 32, color = colors.fire[500] }: Pick<Props, 'size' | 'color'>): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 32 32" fill="none">
      <path
        // eslint-disable-next-line max-len
        d="M16.22 22.022L13.022 25.22C13.0706 25.9988 13.4215 26.7277 13.9999 27.2515C14.5783 27.7752 15.3385 28.0523 16.1182 28.0236C16.898 27.9949 17.6357 27.6627 18.174 27.0979C18.7124 26.533 19.0088 25.7802 19 25C18.9984 24.2443 18.7108 23.5172 18.1951 22.9648C17.6794 22.4124 16.9738 22.0755 16.22 22.022V22.022Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M15.988 18.001L19.551 14.499C18.396 14.1698 17.201 14.0018 16 14C14.2921 13.9952 12.6003 14.3293 11.0224 14.9829C9.44457 15.6366 8.01211 16.5968 6.808 17.808L9.636 20.636C10.4684 19.7992 11.4582 19.1355 12.5485 18.6832C13.6387 18.231 14.8077 17.9991 15.988 18.001V18.001Z"
        fill={color}
      />
      <path
        d="M19.525 18.718C20.5859 19.1693 21.5501 19.8205 22.365 20.636L25.193 17.807C24.387 17.0063 23.48 16.3142 22.495 15.748L19.525 18.718V18.718Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M16 10C18.354 10 20.633 10.474 22.737 11.367L25.774 8.382C22.7552 6.81288 19.4022 5.99573 16 6C13.2084 5.99224 10.443 6.53835 7.86395 7.60672C5.28487 8.67508 2.94336 10.2445 0.974998 12.224L3.803 15.052C5.40104 13.4453 7.30187 12.1715 9.39549 11.3043C11.4891 10.4372 13.7339 9.99383 16 10V10Z"
        fill={color}
      />
      <path
        d="M25.433 12.809C26.4287 13.46 27.3549 14.2117 28.197 15.052L31.025 12.224C30.181 11.3893 29.2692 10.626 28.299 9.942L25.433 12.809V12.809Z"
        fill={color}
      />
      <path d="M5.586 27L29 3.586L30.414 5L7 28.414L5.586 27Z" fill={color} />
    </StyledSVG>
  );
}

export function IconCalendar({ size = 24, color = colors.steel[400] }: Pick<Props, 'size' | 'color'>): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z"
        fill={color}
      />
    </StyledSVG>
  );
}

export function IconVerticalSwap({ size = 24, color = colors.steel[400] }: Pick<Props, 'size' | 'color'>): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 24 24" fill="none">
      <g fill="none" className="nc-icon-wrapper">
        <path d="M16 17.01V10h-2v7.01h-3L15 21l4-3.99h-3zM9 3L5 6.99h3V14h2V6.99h3L9 3z" fill={color} />
      </g>
    </StyledSVG>
  );
}

export function IconCopy({ size = 16, color = colors.steel[400] }: Pick<Props, 'size' | 'color'>): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 17 16" fill="none">
      <g clipPath="url(#clip0_2941_61964)">
        <g clipPath="url(#clip1_2941_61964)">
          <path
            d="M11.2471 1H1.24707V11H11.2471V1Z"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M13.4289 5H15.2471V15H5.24707V13.1818"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      </g>
      <defs>
        <clipPath id="clip0_2941_61964">
          <rect width={size} height={size} fill="white" transform="translate(0.24707)" />
        </clipPath>
        <clipPath id="clip1_2941_61964">
          <rect width={size} height={size} fill="white" transform="translate(0.24707)" />
        </clipPath>
      </defs>
    </svg>
  );
}

export function IconIdCard({ size = 20, color = colors.steel[400] }: Props): JSX.Element {
  return (
    <StyledSVG width={size} height={size} viewBox="0 0 20 16" fill="none">
      <path
        // eslint-disable-next-line max-len
        d="M18 0H2C0.897 0 0 0.897 0 2V14C0 15.103 0.897 16 2 16H18C19.103 16 20 15.103 20 14V2C20 0.897 19.103 0 18 0ZM6.715 4C7.866 4 8.715 4.849 8.715 6C8.715 7.151 7.866 8 6.715 8C5.564 8 4.715 7.151 4.715 6C4.715 4.849 5.563 4 6.715 4ZM10.43 12H3V11.535C3 10.162 4.676 8.75 6.715 8.75C8.754 8.75 10.43 10.162 10.43 11.535V12ZM17 11H13V9H17V11ZM17 7H12V5H17V7Z"
        fill={color}
      />
    </StyledSVG>
  );
}

interface IconFileCountProps {
  filesCount: number;
}

export function IconFileCount({ filesCount }: IconFileCountProps): JSX.Element {
  if (filesCount < 1) {
    return <IconFileCountZero />;
  }

  /**
   * TODO: Replace once we have a design for file counts greater than 99
   */
  if (filesCount > 99) {
    return <></>;
  }

  /**
   * If the number of files has two digits,
   * we need to expand the icon to account for this
   */
  const isDoubleDigits = filesCount > 9;
  const svgWidth = isDoubleDigits ? '25' : '21';
  const viewBox = isDoubleDigits ? '0 0 25 23' : '0 0 21 23';
  const rectWidth = isDoubleDigits ? '17' : '13';
  const textX = isDoubleDigits ? '11' : '11.5';

  return (
    <svg width={svgWidth} height="23" viewBox={viewBox} fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        // eslint-disable-next-line max-len
        d="M11.5594 3.0619L8.9375 0.440539C8.65625 0.159344 8.275 0 7.87812 0H1.5C0.671875 0.00312439 0 0.674868 0 1.50283V14.5003C0 15.3283 0.671875 16 1.5 16H10.5C11.3281 16 12 15.3283 12 14.5003V4.12419C12 3.7274 11.8406 3.3431 11.5594 3.0619ZM10.3781 4.00234H8V1.62468L10.3781 4.00234ZM1.5 14.5003V1.50283H6.5V4.7522C6.5 5.16774 6.83437 5.50205 7.25 5.50205H10.5V14.5003H1.5Z"
        fill="#546A83"
      />
      <rect x="7.5" y="9.5" width={rectWidth} height="13" rx="6.5" fill="#0957C3" />
      <text id="textid" x={textX} y="19.5" fontWeight="bold" fontSize={9} fill="white" fontFamily="Open Sans">
        {filesCount}
      </text>
      <rect x="7.5" y="9.5" width={rectWidth} height="13" rx="6.5" stroke="white" />
    </svg>
  );
}

export function IconFileCountZero(): JSX.Element {
  return (
    <svg width="21" height="23" viewBox="0 0 21 23" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        // eslint-disable-next-line max-len
        d="M11.5594 3.0619L8.9375 0.440539C8.65625 0.159344 8.275 0 7.87812 0H1.5C0.671875 0.00312439 0 0.674868 0 1.50283V14.5003C0 15.3283 0.671875 16 1.5 16H10.5C11.3281 16 12 15.3283 12 14.5003V4.12419C12 3.7274 11.8406 3.3431 11.5594 3.0619ZM10.3781 4.00234H8V1.62468L10.3781 4.00234ZM1.5 14.5003V1.50283H6.5V4.7522C6.5 5.16774 6.83437 5.50205 7.25 5.50205H10.5V14.5003H1.5Z"
        fill="#BEC7D0"
      />
      <rect x="7.5" y="9.5" width="13" height="13" rx="6.5" fill="#98A6B5" />
      <path
        // eslint-disable-next-line max-len
        d="M16.3164 16.2876C16.3164 16.8062 16.2754 17.269 16.1934 17.6763C16.1143 18.0835 15.9854 18.4292 15.8066 18.7134C15.6309 18.9976 15.3994 19.2144 15.1123 19.3638C14.8252 19.5132 14.4766 19.5879 14.0664 19.5879C13.5508 19.5879 13.1274 19.4575 12.7964 19.1968C12.4653 18.9331 12.2207 18.5552 12.0625 18.063C11.9043 17.5679 11.8252 16.9761 11.8252 16.2876C11.8252 15.5933 11.897 15 12.0405 14.5078C12.187 14.0127 12.4243 13.6333 12.7524 13.3696C13.0806 13.106 13.5186 12.9741 14.0664 12.9741C14.5791 12.9741 15.001 13.106 15.332 13.3696C15.666 13.6304 15.9136 14.0083 16.0747 14.5034C16.2358 14.9956 16.3164 15.5903 16.3164 16.2876ZM13.1743 16.2876C13.1743 16.7769 13.2007 17.1855 13.2534 17.5137C13.3091 17.8389 13.4014 18.0835 13.5303 18.2476C13.6592 18.4116 13.8379 18.4937 14.0664 18.4937C14.292 18.4937 14.4692 18.4131 14.5981 18.252C14.73 18.0879 14.8237 17.8433 14.8794 17.5181C14.9351 17.1899 14.9629 16.7798 14.9629 16.2876C14.9629 15.7983 14.9351 15.3896 14.8794 15.0615C14.8237 14.7334 14.73 14.4873 14.5981 14.3232C14.4692 14.1562 14.292 14.0728 14.0664 14.0728C13.8379 14.0728 13.6592 14.1562 13.5303 14.3232C13.4014 14.4873 13.3091 14.7334 13.2534 15.0615C13.2007 15.3896 13.1743 15.7983 13.1743 16.2876Z"
        fill="white"
      />
      <rect x="7.5" y="9.5" width="13" height="13" rx="6.5" stroke="white" />
    </svg>
  );
}

export function IconDownload2({ color, size = 24 }: Props): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24">
      <g fill={color}>
        <polygon points="6,10 12,17 18,10 13,10 13,1 11,1 11,10 " />{' '}
        <path fill={color} d="M22,21H2v-6H0v7c0,0.552,0.448,1,1,1h22c0.552,0,1-0.448,1-1v-7h-2V21z" />
      </g>
    </svg>
  );
}

export function IconEye({ color = 'currentColor', size = 24 }: Props): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24">
      <g fill={color}>
        <path
          // eslint-disable-next-line max-len
          d="M23.444,10.239C21.905,8.062,17.708,3,12,3S2.1,8.062.555,10.24a3.058,3.058,0,0,0,0,3.52h0C2.1,15.938,6.292,21,12,21s9.905-5.062,11.445-7.24A3.058,3.058,0,0,0,23.444,10.239ZM12,17a5,5,0,1,1,5-5A5,5,0,0,1,12,17Z"
          fill={color}
        />
      </g>
    </svg>
  );
}

export function IconAdd2({ color, size = 24 }: Props): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        // eslint-disable-next-line max-len
        d="M17.8333 10.8333H13.1667V6.16667C13.1667 5.85725 13.0437 5.5605 12.825 5.34171C12.6062 5.12292 12.3094 5 12 5C11.6906 5 11.3938 5.12292 11.175 5.34171C10.9562 5.5605 10.8333 5.85725 10.8333 6.16667V10.8333H6.16667C5.85725 10.8333 5.5605 10.9562 5.34171 11.175C5.12292 11.3938 5 11.6906 5 12C5 12.3094 5.12292 12.6062 5.34171 12.825C5.5605 13.0437 5.85725 13.1667 6.16667 13.1667H10.8333V17.8333C10.8333 18.1428 10.9562 18.4395 11.175 18.6583C11.3938 18.8771 11.6906 19 12 19C12.3094 19 12.6062 18.8771 12.825 18.6583C13.0437 18.4395 13.1667 18.1428 13.1667 17.8333V13.1667H17.8333C18.1428 13.1667 18.4395 13.0437 18.6583 12.825C18.8771 12.6062 19 12.3094 19 12C19 11.6906 18.8771 11.3938 18.6583 11.175C18.4395 10.9562 18.1428 10.8333 17.8333 10.8333Z"
        fill={color}
      />
    </svg>
  );
}

export function IconHealth({ color, size = 24 }: Props): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24">
      <path
        // eslint-disable-next-line max-len
        d="M5.9457 2.85626C5.9457 2.49064 6.2457 2.19064 6.61133 2.19064H13.3895C13.7551 2.19064 14.0551 2.49064 14.0551 2.85626V5.40626H15.8738V2.85626C15.8738 1.48751 14.7582 0.371887 13.3895 0.371887H6.61133C5.24258 0.371887 4.12695 1.48751 4.12695 2.85626V5.40626H5.9457V2.85626Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M1.45938 11.2844C1.41719 11.2844 1.37031 11.2844 1.32812 11.2797V18.4563C1.32812 19.1031 1.94219 19.6281 2.69687 19.6281H17.3078C18.0625 19.6281 18.6766 19.1031 18.6766 18.4563V11.275C18.6344 11.2797 18.5922 11.2797 18.5453 11.2797H1.45938V11.2844ZM12.8734 16.7172H11.2656V18.325H8.73438V16.7172H7.12656V14.186H8.73438V12.5781H11.2656V14.186H12.8734V16.7172Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M19.5861 7.45001V7.10782C19.5861 6.53126 19.1174 6.0672 18.5455 6.0672H1.45957C0.883008 6.0672 0.418945 6.53595 0.418945 7.10782V9.58282C0.418945 10.1594 0.887696 10.6281 1.45957 10.6281H18.5455C19.1221 10.6281 19.5861 10.1594 19.5861 9.58282V8.10626C19.5861 8.28907 19.4033 8.10626 19.5861 8.10626C19.5861 7.92345 19.5861 8.60533 19.5861 7.45001Z"
        fill={color}
      />
    </svg>
  );
}

export function IconDental({ color, size = 24 }: Props): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24">
      <path
        // eslint-disable-next-line max-len
        d="M14.5779 2.19373C13.6217 2.19373 12.7357 2.55466 11.9998 3.16873C11.2639 2.55466 10.3779 2.19373 9.42168 2.19373C6.89512 2.19373 4.84668 4.69685 4.84668 7.78123C4.84668 8.72341 5.03887 9.60935 5.37637 10.3875C6.09824 12.0562 6.57637 13.8234 6.83887 15.6234C6.82012 15.6234 6.85293 15.6187 6.83887 15.6234C6.86583 15.8083 6.83887 15.6234 6.92793 16.275L7.50918 21.1172C7.60762 21.914 8.69512 22.064 8.9998 21.3234L11.1795 16.0922C11.442 15.4594 12.342 15.4594 12.6045 16.0922L14.7842 21.3234C15.0936 22.064 16.1811 21.914 16.2748 21.1172L16.7811 16.9031C17.0482 14.6812 17.6623 12.5203 18.5811 10.4812C18.942 9.67966 19.1482 8.76091 19.1482 7.78123C19.1529 4.69685 17.1045 2.19373 14.5779 2.19373ZM15.3279 11.5781H13.4295V13.4765H10.5701V11.5781H8.67168V8.71873H10.5701V6.82029H13.4295V8.71873H15.3279V11.5781Z"
        fill={color}
      />
    </svg>
  );
}

export function IconVision({ color, size = 24 }: Props): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24">
      <path
        // eslint-disable-next-line max-len
        d="M6.80845 16.2509C9.15613 16.2509 11.0593 14.3478 11.0593 12.0001C11.0593 9.65243 9.15613 7.74927 6.80845 7.74927C4.46078 7.74927 2.55762 9.65243 2.55762 12.0001C2.55762 14.3478 4.46078 16.2509 6.80845 16.2509Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M2.55787 12C2.55787 11.7281 2.58445 11.4626 2.63321 11.2048H1.08989C0.747679 11.2048 0.470215 11.4822 0.470215 11.8245V12.1755C0.470215 12.5177 0.747616 12.7952 1.08989 12.7952H2.63327C2.58451 12.5374 2.55787 12.2719 2.55787 12Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M17.1913 16.2509C19.5389 16.2509 21.4421 14.3478 21.4421 12.0001C21.4421 9.65243 19.5389 7.74927 17.1913 7.74927C14.8436 7.74927 12.9404 9.65243 12.9404 12.0001C12.9404 14.3478 14.8436 16.2509 17.1913 16.2509Z"
        fill={color}
      />
      <path
        d="M21.3667 11.2048H22.9101C23.2523 11.2048 23.5298 11.4822 23.5298 11.8245V12.1755C23.5298 12.5177 23.2524 12.7952 22.9101 12.7952H21.3667C21.4671 12.2646 21.4672 11.7357 21.3667 11.2048Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M5.31409 9.35862C4.64509 9.73774 4.1487 10.3489 3.9163 11.0795C3.83758 11.3269 3.97434 11.5912 4.22178 11.6699C4.4691 11.7486 4.73352 11.6119 4.81218 11.3644C4.9725 10.8603 5.3154 10.4385 5.77757 10.1765C6.00345 10.0485 6.0828 9.76162 5.95475 9.53574C5.82683 9.30992 5.53997 9.23063 5.31409 9.35862Z"
        fill="white"
      />
      <path
        // eslint-disable-next-line max-len
        d="M22.9103 10.7348H21.7389C21.1843 8.74434 19.3564 7.27912 17.1915 7.27912C14.9974 7.27912 13.1483 8.78364 12.6213 10.8151C12.23 10.6223 11.7703 10.6222 11.3787 10.8151C10.8517 8.78364 9.00258 7.27905 6.80856 7.27905C4.64375 7.27905 2.81582 8.74428 2.26127 10.7347H1.08968C0.488806 10.7348 0 11.2236 0 11.8245V12.1755C0 12.7764 0.488806 13.2652 1.08968 13.2652H2.26121C2.81576 15.2556 4.64369 16.7209 6.8085 16.7209C9.36597 16.7209 11.4534 14.6763 11.526 12.1361C11.528 12.1181 11.5294 12.0999 11.5294 12.0813C11.5294 11.8218 11.7405 11.6107 12 11.6107C12.2594 11.6107 12.4705 11.8218 12.4705 12.0813C12.4705 12.0999 12.4719 12.1181 12.474 12.1361C12.5464 14.6763 14.634 16.7209 17.1914 16.7209C19.3563 16.7209 21.1842 15.2556 21.7388 13.2652H22.9103C23.5111 13.2652 24 12.7764 24 12.1755V11.8245C24 11.2236 23.5112 10.7348 22.9103 10.7348ZM2.09906 12.3251H1.08968C1.00719 12.3251 0.940132 12.258 0.940132 12.1755V11.8245C0.940132 11.742 1.00719 11.6749 1.08968 11.6749H2.099C2.08446 11.8833 2.08415 12.1115 2.09906 12.3251ZM6.8085 15.7807C4.7162 15.7807 3.02773 14.0745 3.02773 12C3.02773 9.93762 4.70455 8.21919 6.8085 8.21919C8.89321 8.21919 10.5893 9.91525 10.5893 12C10.5893 14.0847 8.89327 15.7807 6.8085 15.7807ZM17.1915 15.7807C15.1067 15.7807 13.4107 14.0847 13.4107 12C13.4107 9.91525 15.1068 8.21919 17.1915 8.21919C19.2751 8.21919 20.9723 9.91625 20.9723 12C20.9723 14.0738 19.2845 15.7807 17.1915 15.7807ZM23.0599 12.1755C23.0599 12.258 22.9928 12.3251 22.9103 12.3251H21.9009C21.9172 12.0922 21.9147 11.8716 21.9009 11.6749H22.9103C22.9927 11.6749 23.0599 11.742 23.0599 11.8245V12.1755Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M15.6974 9.3586C15.0284 9.73779 14.532 10.3489 14.2996 11.0795C14.2209 11.3269 14.3577 11.5912 14.605 11.6699C14.8526 11.7486 15.1168 11.6117 15.1955 11.3644C15.3558 10.8604 15.6987 10.4384 16.1609 10.1765C16.3868 10.0485 16.4661 9.76161 16.3381 9.53572C16.2101 9.30997 15.9232 9.23062 15.6974 9.3586Z"
        fill="white"
      />
    </svg>
  );
}

export function IconDisability({ color, size = 24 }: Props): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24">
      <path
        // eslint-disable-next-line max-len
        d="M9.12666 15.0188V14.9953C9.12197 14.9906 9.10322 14.9672 9.09854 14.9578C9.08447 14.9391 9.06572 14.9156 9.04697 14.8922L9.04228 14.8875C9.02353 14.8594 9.00479 14.8313 8.98603 14.8078C8.98135 14.7985 8.94853 14.7516 8.94853 14.7516L8.94385 14.7469C8.93916 14.7375 8.92041 14.7094 8.92041 14.7094L8.79853 14.625L8.77979 14.4235C8.7751 14.4141 8.76572 14.3906 8.76572 14.3906C8.75635 14.3625 8.74229 14.3344 8.73291 14.3063C8.72354 14.2735 8.70947 14.2453 8.7001 14.2125L3.95166 18.975L5.55947 20.5828L10.3267 15.8156C9.95166 15.7031 9.60478 15.4969 9.31885 15.211L9.12666 15.0188Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M3.27627 19.2282C2.97627 18.9282 2.49346 18.9282 2.19814 19.2282C1.89814 19.5282 1.89814 20.011 2.19814 20.3063L4.22783 22.336C4.52783 22.636 5.01064 22.636 5.30596 22.336C5.60596 22.036 5.60596 21.5532 5.30596 21.2579L3.27627 19.2282Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M15.061 13.9829C15.2907 13.8469 15.511 13.6782 15.7079 13.4766L20.7048 8.48445L19.2563 7.03601L14.2594 12.0329C14.2173 12.0751 14.1704 12.1173 14.1188 12.1548C13.9829 12.2532 13.8329 12.3235 13.6782 12.361C13.2704 12.4641 12.8157 12.3516 12.4969 12.0329C12.0094 11.5454 12.0094 10.7579 12.4969 10.2704L17.4938 5.27351L17.0157 4.7907C17.1269 4.90297 17.0771 4.85265 17.0157 4.7907C16.5516 4.32664 16.6173 4.39226 16.5516 4.32664C16.0501 3.82507 16.4251 4.20007 16.5516 4.32664L16.0501 3.82507L12.0001 7.87507L11.0532 8.82195C10.8798 8.99539 10.7344 9.1782 10.6079 9.37507C10.1298 10.1204 9.98445 11.0204 10.1673 11.8594L9.78289 12.2438C9.39851 12.6282 9.22976 13.1438 9.27195 13.6454C9.27664 13.6735 9.27664 13.7063 9.28132 13.7344C9.28132 13.7532 9.28601 13.7673 9.2907 13.786C9.2907 13.7954 9.29539 13.8048 9.29539 13.8141C9.29539 13.8235 9.30007 13.8376 9.30007 13.8469C9.30007 13.8516 9.30007 13.8516 9.30007 13.8563C9.30007 13.8657 9.30476 13.8751 9.30476 13.8844C9.30945 13.9032 9.31414 13.9266 9.31882 13.9454C9.32351 13.9641 9.3282 13.9829 9.33289 13.9969C9.34226 14.0251 9.34695 14.0485 9.35632 14.0766C9.3657 14.1001 9.37507 14.1235 9.37976 14.1469C9.38445 14.161 9.38914 14.1704 9.39382 14.1798C9.39851 14.1938 9.4032 14.2032 9.40789 14.2126C9.41257 14.2266 9.41726 14.236 9.42664 14.2501C9.43601 14.2688 9.4407 14.2829 9.45007 14.3016C9.46414 14.3298 9.4782 14.3532 9.49226 14.3766C9.50164 14.3907 9.51101 14.4048 9.5157 14.4188V14.4235C9.52039 14.4329 9.52507 14.4423 9.53445 14.4516C9.54382 14.4704 9.5532 14.4844 9.56726 14.4985C9.57195 14.5032 9.57664 14.5126 9.58132 14.5173C9.5907 14.5313 9.60476 14.5454 9.61414 14.5641C9.61882 14.5688 9.62351 14.5782 9.6282 14.5829C9.63757 14.5923 9.64226 14.6016 9.65164 14.6157C9.66101 14.6251 9.6657 14.6344 9.67507 14.6438C9.68914 14.6579 9.7032 14.6766 9.71726 14.6907C9.73601 14.7094 9.75476 14.7282 9.77351 14.7516C10.0782 15.0563 10.4719 15.2298 10.8751 15.2626C11.2594 15.2954 11.6532 15.2016 11.9907 14.9813C12.0938 14.9157 12.1876 14.8407 12.2766 14.7516L12.661 14.3673C13.2938 14.5079 13.9641 14.4563 14.5735 14.2219C14.7469 14.1516 14.9063 14.0766 15.061 13.9829Z"
        fill={color}
      />
      <path
        d="M8.83584 11.4141C8.39053 11.8828 8.10928 12.4781 8.02959 13.1063L7.97334 13.1625L2.9624 8.15156L4.41084 6.70312L8.82647 11.1188C8.82647 11.2172 8.82647 11.3156 8.83584 11.4141Z"
        fill={color}
      />
      <path d="M7.4738 1.43953L0.907715 8.00562L2.00814 9.10604L8.57423 2.53995L7.4738 1.43953Z" fill={color} />
      <path
        d="M11.1046 6.98445L10.1577 7.93132C9.98897 8.10007 9.83428 8.2782 9.69834 8.47039L6.17334 4.94539L7.62178 3.49695L11.1046 6.98445Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M15.9749 14.8969C15.8858 14.9578 15.7968 15.014 15.703 15.0703C15.4921 15.1969 15.2624 15.3047 15.0327 15.3937C14.564 15.5765 14.0671 15.6797 13.5562 15.6984L18.4358 20.5781L20.0437 18.9703L15.9749 14.8969Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M21.2625 19.0032C21.0656 19.0032 20.8734 19.0782 20.7234 19.2282L20.5125 19.4391L18.9047 21.0469L18.6938 21.2579C18.3938 21.5579 18.3938 22.0407 18.6938 22.336C18.9938 22.636 19.4766 22.636 19.7719 22.336L21.8016 20.3063C22.1016 20.0063 22.1016 19.5235 21.8016 19.2282C21.6563 19.0782 21.4594 19.0032 21.2625 19.0032Z"
        fill={color}
      />
      <path
        d="M16.8794 3.99846L17.3435 4.46252L17.8263 4.94534L21.0372 8.15159L21.9935 9.10784L23.0903 8.00627L16.5231 1.43909L15.4263 2.54065L16.3825 3.4969L16.8794 3.99846Z"
        fill={color}
      />
    </svg>
  );
}

export function IconLife({ color, size = 24 }: Props): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        // eslint-disable-next-line max-len
        d="M8.9248 14.4609H3.8623C4.77637 15.7828 6.29043 17.2594 8.76074 18.8578C9.76855 19.5094 10.917 19.8328 12.0701 19.8328C12.1779 19.8328 12.2904 19.8281 12.3982 19.8234L9.85293 12.5859L8.9248 14.4609Z"
        fill={color}
      />
      <path
        d="M15.0371 14.1562L12.5059 19.8187C13.5043 19.7484 14.4934 19.425 15.3699 18.8578C17.4465 17.5125 18.834 16.2563 19.7574 15.0984H15.5105L15.0371 14.1562Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M17.0389 12.6328L14.9107 8.38596L12.7123 13.3031L10.2232 6.22034L7.38731 11.9953H1.14355V13.8047H8.51699L9.94668 10.8938L12.5154 18.1922L15.0045 12.6188L15.9186 14.4422H22.8561V12.6328H17.0389Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M6.97949 11.3391L10.3217 4.52817L12.7873 11.536L14.8779 6.85317L17.442 11.9813H21.3561C21.4967 11.3813 21.5342 10.8422 21.5342 10.3641C21.5342 10.1954 21.5248 10.0266 21.5107 9.85786C21.5014 9.85786 21.4873 9.85786 21.4779 9.85786H21.5107C21.4779 9.49471 21.4653 9.45112 21.417 9.20161C20.8732 6.3938 18.4029 4.27036 15.4357 4.27036C14.1936 4.27036 13.0404 4.64067 12.0795 5.27817C11.0904 4.57974 9.88105 4.16724 8.57324 4.16724C5.20762 4.16724 2.47949 6.89536 2.47949 10.261C2.47949 10.5891 2.49824 10.95 2.55449 11.3391H6.97949Z"
        fill={color}
      />
    </svg>
  );
}

export function IconArticle({ color, size = 24 }: Props): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24">
      <g fill={color}>
        <path
          // eslint-disable-next-line max-len
          d="M22,0H6A1,1,0,0,0,5,1V20.5a1.5,1.5,0,0,1-3,0V14H4V12H1a1,1,0,0,0-1,1v7.5A3.5,3.5,0,0,0,3.5,24H20a3.015,3.015,0,0,0,3-3V1A1,1,0,0,0,22,0ZM18.5,19h-9a.5.5,0,0,1-.5-.5v-1a.5.5,0,0,1,.5-.5h9a.5.5,0,0,1,.5.5v1A.5.5,0,0,1,18.5,19Zm0-5h-9a.5.5,0,0,1-.5-.5v-1a.5.5,0,0,1,.5-.5h9a.5.5,0,0,1,.5.5v1A.5.5,0,0,1,18.5,14Zm0-5h-9A.5.5,0,0,1,9,8.5v-4A.5.5,0,0,1,9.5,4h9a.5.5,0,0,1,.5.5v4A.5.5,0,0,1,18.5,9Z"
          fill={color}
        />
      </g>
    </svg>
  );
}

export const IconBugReport = ({ size = 24, color = 'currentColor' }: Props): JSX.Element => {
  return (
    <svg width={size} height={size} viewBox="-2.2 -2.2 26.40 26.40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        // eslint-disable-next-line max-len
        d="M7.54473 21.0707C6.49881 20.475 5.57883 19.6172 4.8451 18.5691L3.70711 19.7071C3.31658 20.0976 2.68342 20.0976 2.29289 19.7071C1.90237 19.3166 1.90237 18.6834 2.29289 18.2929L3.84103 16.7448C3.48231 15.8933 3.22814 14.9688 3.09852 13.9952C3.06611 13.9984 3.03324 14 3 14H1C0.44772 14 0 13.5523 0 13C0 12.4477 0.44772 12 1 12H3C3.00363 12 3.00726 12 3.01089 12.0001C3.06942 10.6594 3.362 9.3923 3.84103 8.25525L2.29289 6.70711C1.90237 6.31658 1.90237 5.68342 2.29289 5.29289C2.68342 4.90237 3.31658 4.90237 3.70711 5.29289L4.8451 6.43089C5.45625 5.55786 6.19663 4.81693 7.03137 4.24949C7.01089 4.16974 7 4.08614 7 4C7 1.79086 8.79086 0 11 0C13.2091 0 15 1.79086 15 4C15 4.08614 14.9891 4.16974 14.9686 4.24949C15.8034 4.81693 16.5437 5.55786 17.1549 6.43089L18.2929 5.29289C18.6834 4.90237 19.3166 4.90237 19.7071 5.29289C20.0976 5.68342 20.0976 6.31658 19.7071 6.70711L18.159 8.25525C18.638 9.3923 18.9306 10.6594 18.9891 12.0001C18.9927 12 18.9964 12 19 12H21C21.5523 12 22 12.4477 22 13C22 13.5523 21.5523 14 21 14H19C18.9668 14 18.9339 13.9984 18.9015 13.9952C18.7719 14.9688 18.5177 15.8933 18.159 16.7448L19.7071 18.2929C20.0976 18.6834 20.0976 19.3166 19.7071 19.7071C19.3166 20.0976 18.6834 20.0976 18.2929 19.7071L17.1549 18.5691C16.4212 19.6172 15.5012 20.475 14.4553 21.0707C13.4093 21.6663 12.2375 22 11 22C9.7625 22 8.59065 21.6663 7.54473 21.0707zM10.0005 19.8964L10.0013 19.8966L10 19.8939V13C10 12.4477 10.4477 12 11 12C11.5523 12 12 12.4477 12 13V19.8939C14.5476 19.3489 16.6227 16.7613 16.9539 13.4345C16.9843 13.1283 17 12.8165 17 12.5C17 8.35786 14.3137 5 11 5C7.68629 5 5 8.35786 5 12.5C5 12.8165 5.01568 13.1283 5.04612 13.4345C5.37726 16.7613 7.45244 19.3489 10 19.8939C10.0002 19.8947 10.0004 19.8956 10.0005 19.8964zM9.1424 3.25743H12.8576C12.5628 2.52054 11.8422 2 11 2C10.1578 2 9.4372 2.52054 9.1424 3.25743C9.7385 3.08911 10.3605 3 11 3C10.3605 3 9.7385 3.08911 9.1424 3.25743z"
        fill={color}
      />
    </svg>
  );
};

export const IconChevron = ({ color = 'currentColor' }: Props): JSX.Element => (
  <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      // eslint-disable-next-line max-len
      d="M1.73154 7.6945L6.02453 3.4015L10.3175 7.6945C10.5061 7.87665 10.7587 7.97745 11.0209 7.97517C11.2831 7.97289 11.5339 7.86772 11.7193 7.68231C11.9048 7.49691 12.0099 7.24609 12.0122 6.9839C12.0145 6.7217 11.9137 6.4691 11.7315 6.2805L6.73153 1.2805C6.544 1.09302 6.2897 0.987712 6.02453 0.987712C5.75937 0.987712 5.50506 1.09302 5.31753 1.2805L0.317545 6.2805C0.222045 6.37274 0.145845 6.48309 0.0934449 6.60509C0.0410449 6.72709 0.0134443 6.85831 0.0122443 6.99109C0.0111443 7.12387 0.0364444 7.25555 0.0867444 7.37845C0.136944 7.50135 0.211244 7.613 0.305144 7.70689C0.399044 7.80078 0.510645 7.87504 0.633545 7.92532C0.756445 7.9756 0.888145 8.0009 1.02094 7.99975C1.15374 7.99859 1.28494 7.97101 1.40694 7.9186C1.52894 7.86619 1.63924 7.79001 1.73154 7.6945Z"
      fill={color}
    />
  </svg>
);

export const IconQuestionMark = ({
  size = 20,
  height = 20,
  backgroundColor = '#B8BEC2',
  foregroundColor = '#FFFFFF',
}: Props): JSX.Element => (
  <svg width={size} height={height} viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <mask id="question">
        <rect width="100%" height="100%" fill="white" />
        <path
          // eslint-disable-next-line max-len
          d="M5.0439 7.64992V7.28802C5.0439 6.97503 5.11231 6.7028 5.24914 6.47131C5.38596 6.23983 5.63626 5.99368 6.00001 5.73285C6.35042 5.48833 6.58069 5.28945 6.69082 5.13622C6.80429 4.98298 6.86102 4.81181 6.86102 4.62272C6.86102 4.4108 6.78093 4.24941 6.62074 4.13856C6.46055 4.02771 6.23696 3.97228 5.94996 3.97228C5.44937 3.97228 4.87871 4.13204 4.23796 4.45155L3.69232 3.38054C4.43652 2.973 5.22578 2.76923 6.06008 2.76923C6.74755 2.76923 7.29319 2.93061 7.69699 3.25338C8.10414 3.57615 8.30771 4.00652 8.30771 4.54447C8.30771 4.9031 8.22428 5.21283 8.05741 5.47366C7.89055 5.73448 7.57352 6.02791 7.1063 6.35394C6.78593 6.58543 6.58236 6.76148 6.49559 6.88211C6.41216 7.00275 6.37045 7.16087 6.37045 7.35649V7.64992H5.0439ZM4.88371 9.32246C4.88371 9.04859 4.9588 8.84156 5.10897 8.70137C5.25915 8.56118 5.47774 8.49108 5.76474 8.49108C6.04173 8.49108 6.25531 8.56281 6.40549 8.70626C6.559 8.84972 6.63576 9.05512 6.63576 9.32246C6.63576 9.58003 6.559 9.7838 6.40549 9.93377C6.25197 10.0805 6.03839 10.1538 5.76474 10.1538C5.48441 10.1538 5.26749 10.0821 5.11398 9.93866C4.96047 9.79195 4.88371 9.58655 4.88371 9.32246Z"
          fill="black"
        />
      </mask>
    </defs>
    <circle cx="6" cy="6" r="6" fill={backgroundColor} mask="url(#question)" />
    <path
      // eslint-disable-next-line max-len
      d="M5.0439 7.64992V7.28802C5.0439 6.97503 5.11231 6.7028 5.24914 6.47131C5.38596 6.23983 5.63626 5.99368 6.00001 5.73285C6.35042 5.48833 6.58069 5.28945 6.69082 5.13622C6.80429 4.98298 6.86102 4.81181 6.86102 4.62272C6.86102 4.4108 6.78093 4.24941 6.62074 4.13856C6.46055 4.02771 6.23696 3.97228 5.94996 3.97228C5.44937 3.97228 4.87871 4.13204 4.23796 4.45155L3.69232 3.38054C4.43652 2.973 5.22578 2.76923 6.06008 2.76923C6.74755 2.76923 7.29319 2.93061 7.69699 3.25338C8.10414 3.57615 8.30771 4.00652 8.30771 4.54447C8.30771 4.9031 8.22428 5.21283 8.05741 5.47366C7.89055 5.73448 7.57352 6.02791 7.1063 6.35394C6.78593 6.58543 6.58236 6.76148 6.49559 6.88211C6.41216 7.00275 6.37045 7.16087 6.37045 7.35649V7.64992H5.0439ZM4.88371 9.32246C4.88371 9.04859 4.9588 8.84156 5.10897 8.70137C5.25915 8.56118 5.47774 8.49108 5.76474 8.49108C6.04173 8.49108 6.25531 8.56281 6.40549 8.70626C6.559 8.84972 6.63576 9.05512 6.63576 9.32246C6.63576 9.58003 6.559 9.7838 6.40549 9.93377C6.25197 10.0805 6.03839 10.1538 5.76474 10.1538C5.48441 10.1538 5.26749 10.0821 5.11398 9.93866C4.96047 9.79195 4.88371 9.58655 4.88371 9.32246Z"
      fill={foregroundColor}
    />
  </svg>
);

export const IconAmericanFlag = ({
  size = 20,
  height = 12,
  color = colors.steel[400],
  className,
}: Props): JSX.Element => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={height}
    viewBox="0 0 20 12"
    fill="none"
    className={className}
  >
    <g clipPath="url(#clip0_3097_78289)">
      <path
        // eslint-disable-next-line max-len
        d="M0.0302734 10.8717V10.4717H10.0303H20.0303V10.8717V11.2717H10.0303H0.0302734V10.8717ZM0.0302734 9.23834V8.83834H10.0303H20.0303V9.23834V9.63834H10.0303H0.0302734V9.23834ZM0.0302734 7.62168V7.23834H10.0303H20.0303V7.62168V8.00501H10.0303H0.0302734V7.62168ZM0.0302734 3.57168V0.738342H10.0303H20.0303V1.13834V1.53834H14.0303H8.03027V1.95501V2.37168H14.0303H20.0303V2.77168V3.17168H14.0303H8.03027V3.58834V4.00501H14.0303H20.0303V4.38834V4.77168H14.0303H8.03027V5.18834V5.60501H14.0303H20.0303V6.00501V6.40501H10.0303H0.0302734V3.57168ZM0.638607 6.01134C0.690507 5.96677 0.703373 5.96677 0.755273 6.01134C0.877283 6.11613 0.896657 6.11405 0.857044 6.00042C0.822663 5.9018 0.827224 5.88771 0.916714 5.81611L1.01361 5.73858L0.887774 5.73841C0.774677 5.73828 0.759917 5.72812 0.74194 5.63825C0.73094 5.58325 0.71069 5.53825 0.69694 5.53825C0.68319 5.53825 0.66294 5.58325 0.65194 5.63825C0.633964 5.72813 0.619204 5.73826 0.506107 5.73841L0.380273 5.73858L0.477167 5.81611C0.566657 5.88772 0.571217 5.9018 0.536837 6.00042C0.497227 6.11405 0.516597 6.11612 0.638607 6.01134ZM1.97194 6.01134C2.02384 5.96677 2.03671 5.96677 2.08861 6.01134C2.21062 6.11613 2.22999 6.11405 2.19038 6.00042C2.156 5.9018 2.16056 5.88771 2.25005 5.81611L2.34694 5.73858L2.22111 5.73841C2.10801 5.73828 2.09325 5.72812 2.07527 5.63825C2.06427 5.58325 2.04402 5.53825 2.03027 5.53825C2.01652 5.53825 1.99627 5.58325 1.98527 5.63825C1.9673 5.72813 1.95254 5.73826 1.83944 5.73841L1.71361 5.73858L1.8105 5.81611C1.89999 5.88772 1.90455 5.9018 1.87017 6.00042C1.83056 6.11405 1.84993 6.11612 1.97194 6.01134ZM3.30527 6.01134C3.35717 5.96677 3.37004 5.96677 3.42194 6.01134C3.54395 6.11613 3.56332 6.11405 3.52371 6.00042C3.48933 5.9018 3.49389 5.88771 3.58338 5.81611L3.68027 5.73858L3.55444 5.73841C3.44134 5.73828 3.42658 5.72812 3.40861 5.63824C3.39761 5.58324 3.37736 5.53824 3.36361 5.53824C3.34986 5.53824 3.32961 5.58324 3.31861 5.63824C3.30063 5.72812 3.28587 5.73826 3.17277 5.73841L3.04694 5.73858L3.14383 5.81611C3.23332 5.88771 3.23788 5.9018 3.2035 6.00042C3.16389 6.11405 3.18326 6.11612 3.30527 6.01134ZM4.63861 6.01134C4.69051 5.96677 4.70337 5.96677 4.75527 6.01134C4.87728 6.11613 4.89666 6.11405 4.85704 6.00042C4.82266 5.9018 4.82722 5.88771 4.91671 5.81611L5.01361 5.73858L4.88777 5.73841C4.77468 5.73828 4.75992 5.72812 4.74194 5.63824C4.73094 5.58324 4.71069 5.53824 4.69694 5.53824C4.68319 5.53824 4.66294 5.58324 4.65194 5.63824C4.63397 5.72812 4.6192 5.73826 4.50611 5.73841L4.38027 5.73858L4.47717 5.81611C4.56666 5.88771 4.57121 5.9018 4.53684 6.00042C4.49723 6.11405 4.5166 6.11612 4.63861 6.01134ZM5.97194 6.01134C6.02384 5.96677 6.03671 5.96677 6.08861 6.01134C6.21062 6.11613 6.22999 6.11405 6.19038 6.00042C6.156 5.9018 6.16056 5.88771 6.25005 5.81611L6.34694 5.73858L6.22111 5.73841C6.10801 5.73828 6.09325 5.72812 6.07527 5.63824C6.06427 5.58324 6.04402 5.53824 6.03027 5.53824C6.01652 5.53824 5.99627 5.58324 5.98527 5.63824C5.9673 5.72812 5.95254 5.73826 5.83944 5.73841L5.71361 5.73858L5.8105 5.81611C5.89999 5.88771 5.90455 5.9018 5.87017 6.00042C5.83056 6.11405 5.84993 6.11612 5.97194 6.01134ZM7.30527 6.01134C7.35717 5.96677 7.37004 5.96677 7.42194 6.01134C7.54395 6.11613 7.56332 6.11405 7.52371 6.00042C7.48933 5.9018 7.49389 5.88771 7.58338 5.81611L7.68027 5.73858L7.55444 5.73841C7.44134 5.73828 7.42658 5.72812 7.40861 5.63824C7.39761 5.58324 7.37736 5.53824 7.36361 5.53824C7.34986 5.53824 7.32961 5.58324 7.31861 5.63824C7.30063 5.72812 7.28587 5.73826 7.17277 5.73841L7.04694 5.73858L7.14383 5.81611C7.23332 5.88771 7.23788 5.9018 7.2035 6.00042C7.16389 6.11405 7.18326 6.11612 7.30527 6.01134ZM1.30527 5.44468C1.35717 5.4001 1.37004 5.4001 1.42194 5.44468C1.54395 5.54946 1.56332 5.54738 1.52371 5.43375C1.48933 5.33513 1.49389 5.32105 1.58338 5.24944L1.68027 5.17191L1.55792 5.17174C1.44913 5.17161 1.43314 5.16049 1.41361 5.07158C1.40153 5.01658 1.37903 4.97158 1.36361 4.97158C1.34819 4.97158 1.32569 5.01658 1.31361 5.07158C1.29408 5.1605 1.27808 5.1716 1.16929 5.17175L1.04694 5.17191L1.14383 5.24944C1.23332 5.32105 1.23788 5.33513 1.2035 5.43375C1.16389 5.54738 1.18326 5.54946 1.30527 5.44468ZM2.63861 5.44468C2.69051 5.4001 2.70337 5.4001 2.75527 5.44468C2.87728 5.54946 2.89666 5.54738 2.85704 5.43375C2.82266 5.33513 2.82722 5.32105 2.91671 5.24944L3.01361 5.17191L2.89126 5.17174C2.78247 5.17161 2.76647 5.16049 2.74694 5.07158C2.73486 5.01658 2.71236 4.97158 2.69694 4.97158C2.68152 4.97158 2.65902 5.01658 2.64694 5.07158C2.62741 5.1605 2.61141 5.1716 2.50263 5.17175L2.38027 5.17191L2.47717 5.24944C2.56666 5.32105 2.57122 5.33513 2.53684 5.43375C2.49723 5.54738 2.5166 5.54946 2.63861 5.44468ZM3.97194 5.44468C4.02384 5.4001 4.03671 5.4001 4.08861 5.44468C4.21062 5.54946 4.22999 5.54738 4.19038 5.43375C4.156 5.33513 4.16056 5.32105 4.25005 5.24944L4.34694 5.17191L4.22459 5.17174C4.1158 5.17161 4.0998 5.16049 4.08027 5.07158C4.06819 5.01658 4.04569 4.97158 4.03027 4.97158C4.01485 4.97158 3.99235 5.01658 3.98027 5.07158C3.96074 5.16049 3.94475 5.1716 3.83596 5.17174L3.71361 5.17191L3.8105 5.24944C3.89999 5.32105 3.90455 5.33513 3.87017 5.43375C3.83056 5.54738 3.84993 5.54946 3.97194 5.44468ZM5.30527 5.44468C5.35717 5.4001 5.37004 5.4001 5.42194 5.44468C5.54395 5.54946 5.56332 5.54738 5.52371 5.43375C5.48933 5.33513 5.49389 5.32105 5.58338 5.24944L5.68027 5.17191L5.55792 5.17174C5.44913 5.17161 5.43314 5.16049 5.41361 5.07158C5.40153 5.01658 5.37903 4.97158 5.36361 4.97158C5.34819 4.97158 5.32569 5.01658 5.31361 5.07158C5.29408 5.16049 5.27808 5.1716 5.16929 5.17174L5.04694 5.17191L5.14383 5.24944C5.23332 5.32105 5.23788 5.33513 5.2035 5.43375C5.16389 5.54738 5.18326 5.54946 5.30527 5.44468ZM6.63861 5.44468C6.69051 5.4001 6.70337 5.4001 6.75527 5.44468C6.87728 5.54946 6.89666 5.54738 6.85704 5.43375C6.82266 5.33513 6.82722 5.32105 6.91671 5.24944L7.01361 5.17191L6.89126 5.17174C6.78247 5.17161 6.76647 5.16049 6.74694 5.07158C6.73486 5.01658 6.71236 4.97158 6.69694 4.97158C6.68152 4.97158 6.65902 5.01658 6.64694 5.07158C6.62741 5.16049 6.61141 5.1716 6.50262 5.17174L6.38027 5.17191L6.47717 5.24944C6.56666 5.32105 6.57121 5.33513 6.53684 5.43375C6.49723 5.54738 6.5166 5.54946 6.63861 5.44468ZM0.638607 4.87801C0.690507 4.83344 0.703373 4.83344 0.755273 4.87801C0.877283 4.98279 0.896657 4.98071 0.857044 4.86708C0.822663 4.76846 0.827224 4.75438 0.916714 4.68277L1.01361 4.60524L0.891257 4.60508C0.782467 4.60494 0.76647 4.59383 0.74694 4.50491C0.73486 4.44991 0.71236 4.40491 0.69694 4.40491C0.68152 4.40491 0.65902 4.44991 0.64694 4.50491C0.627411 4.59383 0.611413 4.60493 0.502627 4.60508L0.380273 4.60525L0.477167 4.68278C0.566657 4.75438 0.571217 4.76847 0.536837 4.86709C0.497227 4.98072 0.516597 4.98279 0.638607 4.87801ZM1.97194 4.87801C2.02384 4.83344 2.03671 4.83344 2.08861 4.87801C2.20929 4.98165 2.22783 4.97917 2.19421 4.86389C2.16571 4.76617 2.17196 4.74718 2.25468 4.68008L2.34694 4.60525L2.22459 4.60508C2.1158 4.60494 2.0998 4.59383 2.08027 4.50491C2.06819 4.44991 2.04569 4.40492 2.03027 4.40492C2.01485 4.40492 1.99235 4.44991 1.98027 4.50491C1.96074 4.59383 1.94475 4.60494 1.83596 4.60508L1.71361 4.60525L1.8105 4.68278C1.89999 4.75438 1.90455 4.76847 1.87017 4.86709C1.83056 4.98072 1.84993 4.98279 1.97194 4.87801ZM3.30527 4.87801C3.35717 4.83344 3.37004 4.83344 3.42194 4.87801C3.54395 4.98279 3.56332 4.98071 3.52371 4.86708C3.48933 4.76846 3.49389 4.75438 3.58338 4.68277L3.68027 4.60524L3.55792 4.60508C3.44913 4.60494 3.43314 4.59383 3.41361 4.50491C3.40153 4.44991 3.37903 4.40491 3.36361 4.40491C3.34819 4.40491 3.32569 4.44991 3.31361 4.50491C3.29408 4.59383 3.27808 4.60493 3.16929 4.60508L3.04694 4.60524L3.1392 4.68008C3.22192 4.74718 3.22817 4.76617 3.19967 4.86389C3.16605 4.97917 3.18459 4.98165 3.30527 4.87801ZM4.63861 4.87801C4.69051 4.83344 4.70337 4.83344 4.75527 4.87801C4.87595 4.98165 4.89449 4.97917 4.86087 4.86389C4.83238 4.76617 4.83862 4.74718 4.92135 4.68008L5.01361 4.60525L4.89126 4.60508C4.78247 4.60494 4.76647 4.59383 4.74694 4.50491C4.73486 4.44991 4.71236 4.40491 4.69694 4.40491C4.68152 4.40491 4.65902 4.44991 4.64694 4.50491C4.62741 4.59383 4.61141 4.60493 4.50262 4.60508L4.38027 4.60525L4.47717 4.68278C4.56666 4.75438 4.57121 4.76847 4.53684 4.86709C4.49723 4.98072 4.5166 4.98279 4.63861 4.87801ZM5.97194 4.87801C6.02384 4.83344 6.03671 4.83344 6.08861 4.87801C6.21062 4.98279 6.22999 4.98071 6.19038 4.86708C6.156 4.76846 6.16056 4.75438 6.25005 4.68277L6.34694 4.60524L6.22459 4.60508C6.1158 4.60494 6.0998 4.59383 6.08027 4.50491C6.06819 4.44991 6.04569 4.40491 6.03027 4.40491C6.01485 4.40491 5.99235 4.44991 5.98027 4.50491C5.96074 4.59383 5.94475 4.60493 5.83596 4.60508L5.71361 4.60524L5.80586 4.68008C5.88859 4.74718 5.89484 4.76617 5.86634 4.86389C5.83272 4.97917 5.85126 4.98165 5.97194 4.87801ZM7.30527 4.87801C7.35717 4.83344 7.37004 4.83344 7.42194 4.87801C7.54395 4.98279 7.56332 4.98071 7.52371 4.86708C7.48933 4.76846 7.49389 4.75438 7.58338 4.68277L7.68027 4.60524L7.55792 4.60508C7.44913 4.60494 7.43314 4.59383 7.41361 4.50491C7.40153 4.44991 7.37903 4.40491 7.36361 4.40491C7.34819 4.40491 7.32569 4.44991 7.31361 4.50491C7.29408 4.59383 7.27808 4.60493 7.16929 4.60508L7.04694 4.60524L7.14383 4.68277C7.23332 4.75438 7.23788 4.76846 7.2035 4.86708C7.16389 4.98071 7.18326 4.98279 7.30527 4.87801ZM1.30527 4.31134C1.35717 4.26677 1.37004 4.26677 1.42194 4.31134C1.54395 4.41613 1.56332 4.41405 1.52371 4.30042C1.48933 4.2018 1.49389 4.18771 1.58338 4.11611L1.68027 4.03858L1.55792 4.03841C1.44913 4.03828 1.43314 4.02716 1.41361 3.93825C1.40153 3.88325 1.37903 3.83825 1.36361 3.83825C1.34819 3.83825 1.32569 3.88325 1.31361 3.93825C1.29408 4.02716 1.27808 4.03827 1.16929 4.03841L1.04694 4.03858L1.14383 4.11611C1.23332 4.18772 1.23788 4.2018 1.2035 4.30042C1.16389 4.41405 1.18326 4.41612 1.30527 4.31134ZM3.97194 4.31134C4.02384 4.26677 4.03671 4.26677 4.08861 4.31134C4.21062 4.41613 4.22999 4.41405 4.19038 4.30042C4.156 4.2018 4.16056 4.18771 4.25005 4.11611L4.34694 4.03858L4.22459 4.03841C4.1158 4.03828 4.0998 4.02716 4.08027 3.93825C4.06819 3.88325 4.04569 3.83825 4.03027 3.83825C4.01485 3.83825 3.99235 3.88325 3.98027 3.93825C3.96074 4.02716 3.94475 4.03827 3.83596 4.03841L3.71361 4.03858L3.8105 4.11611C3.89999 4.18772 3.90455 4.2018 3.87017 4.30042C3.83056 4.41405 3.84993 4.41612 3.97194 4.31134ZM6.63861 4.31134C6.69051 4.26677 6.70337 4.26677 6.75527 4.31134C6.87728 4.41613 6.89666 4.41405 6.85704 4.30042C6.82266 4.2018 6.82722 4.18771 6.91671 4.11611L7.01361 4.03858L6.89126 4.03841C6.78247 4.03828 6.76647 4.02716 6.74694 3.93825C6.73486 3.88325 6.71236 3.83825 6.69694 3.83825C6.68152 3.83825 6.65902 3.88325 6.64694 3.93825C6.62741 4.02716 6.61141 4.03827 6.50262 4.03841L6.38027 4.03858L6.47717 4.11611C6.56666 4.18772 6.57121 4.2018 6.53684 4.30042C6.49723 4.41405 6.5166 4.41612 6.63861 4.31134ZM2.63594 4.31641C2.69266 4.26508 2.70113 4.26508 2.75786 4.31641C2.84606 4.39623 2.87172 4.3846 2.84779 4.27564C2.83038 4.19636 2.84294 4.1676 2.92014 4.11003L3.01357 4.04036L2.89122 4.0393C2.78271 4.03837 2.76638 4.02693 2.7469 3.93825C2.73482 3.88325 2.71232 3.83825 2.6969 3.83825C2.68148 3.83825 2.65898 3.88325 2.6469 3.93825C2.62742 4.02693 2.61109 4.03837 2.50259 4.0393L2.38023 4.04036L2.47366 4.11003C2.55086 4.1676 2.56342 4.19636 2.54601 4.27564C2.52208 4.3846 2.54774 4.39623 2.63594 4.31641ZM5.30261 4.31641C5.35933 4.26508 5.3678 4.26508 5.42452 4.31641C5.51272 4.39623 5.53839 4.3846 5.51446 4.27564C5.49704 4.19636 5.5096 4.1676 5.58681 4.11003L5.68023 4.04036L5.55788 4.0393C5.44938 4.03837 5.43305 4.02693 5.41357 3.93825C5.40149 3.88325 5.37899 3.83825 5.36357 3.83825C5.34815 3.83825 5.32565 3.88325 5.31357 3.93825C5.29409 4.02693 5.27776 4.03837 5.16925 4.0393L5.0469 4.04036L5.14032 4.11003C5.21753 4.1676 5.23009 4.19636 5.21268 4.27564C5.18875 4.3846 5.21441 4.39623 5.30261 4.31641ZM0.63594 3.74978C0.692663 3.69845 0.701133 3.69845 0.757857 3.74978C0.846097 3.82964 0.87172 3.81795 0.847743 3.70879C0.830502 3.63029 0.843087 3.59967 0.916257 3.54212L1.00589 3.47161H0.887377C0.782797 3.47161 0.76628 3.45985 0.7469 3.37161C0.73482 3.31661 0.71232 3.27161 0.6969 3.27161C0.68148 3.27161 0.65898 3.31661 0.6469 3.37161C0.627519 3.45985 0.611 3.47161 0.506423 3.47161H0.38791L0.477543 3.54212C0.550713 3.59967 0.563297 3.63029 0.546057 3.70879C0.52208 3.81795 0.547697 3.82964 0.63594 3.74978ZM1.96927 3.74978C2.026 3.69845 2.03447 3.69845 2.09119 3.74978C2.17943 3.82964 2.20505 3.81795 2.18108 3.70879C2.16384 3.63029 2.17642 3.59967 2.24959 3.54212L2.33922 3.47161H2.22071C2.11613 3.47161 2.09961 3.45985 2.08023 3.37161C2.06815 3.31661 2.04565 3.27161 2.03023 3.27161C2.01481 3.27161 1.99231 3.31661 1.98023 3.37161C1.96085 3.45985 1.94433 3.47161 1.83976 3.47161H1.72124L1.81088 3.54212C1.88405 3.59967 1.89663 3.63029 1.87939 3.70879C1.85541 3.81795 1.88103 3.82964 1.96927 3.74978ZM3.30261 3.74978C3.35933 3.69845 3.3678 3.69845 3.42452 3.74978C3.51276 3.82964 3.53839 3.81795 3.51441 3.70879C3.49717 3.63029 3.50975 3.59967 3.58292 3.54212L3.67256 3.47161H3.55404C3.44946 3.47161 3.43295 3.45985 3.41357 3.37161C3.40149 3.31661 3.37899 3.27161 3.36357 3.27161C3.34815 3.27161 3.32565 3.31661 3.31357 3.37161C3.29419 3.45985 3.27767 3.47161 3.17309 3.47161H3.05458L3.14421 3.54212C3.21738 3.59967 3.22996 3.63029 3.21272 3.70879C3.18875 3.81795 3.21436 3.82964 3.30261 3.74978ZM4.63594 3.74978C4.69266 3.69845 4.70113 3.69845 4.75786 3.74978C4.8461 3.82964 4.87172 3.81795 4.84774 3.70879C4.8305 3.63029 4.84309 3.59967 4.91626 3.54212L5.00589 3.47161H4.88738C4.7828 3.47161 4.76628 3.45985 4.7469 3.37161C4.73482 3.31661 4.71232 3.27161 4.6969 3.27161C4.68148 3.27161 4.65898 3.31661 4.6469 3.37161C4.62752 3.45985 4.611 3.47161 4.50642 3.47161H4.38791L4.47754 3.54212C4.55071 3.59967 4.5633 3.63029 4.54606 3.70879C4.52208 3.81795 4.5477 3.82964 4.63594 3.74978ZM5.96927 3.74978C6.026 3.69845 6.03447 3.69845 6.09119 3.74978C6.17943 3.82964 6.20505 3.81795 6.18108 3.70879C6.16384 3.63029 6.17642 3.59967 6.24959 3.54212L6.33922 3.47161H6.22071C6.11613 3.47161 6.09961 3.45985 6.08023 3.37161C6.06815 3.31661 6.04565 3.27161 6.03023 3.27161C6.01481 3.27161 5.99231 3.31661 5.98023 3.37161C5.96085 3.45985 5.94433 3.47161 5.83976 3.47161H5.72124L5.81088 3.54212C5.88405 3.59967 5.89663 3.63029 5.87939 3.70879C5.85541 3.81795 5.88103 3.82964 5.96927 3.74978ZM7.30261 3.74978C7.35933 3.69845 7.3678 3.69845 7.42452 3.74978C7.51276 3.82964 7.53839 3.81795 7.51441 3.70879C7.49717 3.63029 7.50975 3.59967 7.58292 3.54212L7.67256 3.47161H7.55404C7.44946 3.47161 7.43295 3.45985 7.41357 3.37161C7.40149 3.31661 7.37899 3.27161 7.36357 3.27161C7.34815 3.27161 7.32565 3.31661 7.31357 3.37161C7.29419 3.45985 7.27767 3.47161 7.17309 3.47161H7.05458L7.14421 3.54212C7.21738 3.59967 7.22996 3.63029 7.21272 3.70879C7.18875 3.81795 7.21436 3.82964 7.30261 3.74978ZM1.30527 3.17805C1.35717 3.13347 1.37004 3.13347 1.42194 3.17805C1.54394 3.28282 1.56332 3.28075 1.52373 3.16718C1.4898 3.06984 1.49447 3.05363 1.57693 2.98271L1.66732 2.90495H1.55145C1.4498 2.90495 1.43287 2.89267 1.41361 2.80495C1.38552 2.67705 1.3417 2.67705 1.31361 2.80495C1.29434 2.89267 1.27742 2.90495 1.17577 2.90495H1.05989L1.15029 2.98271C1.23274 3.05363 1.23742 3.06984 1.20348 3.16718C1.16389 3.28075 1.18328 3.28281 1.30527 3.17805ZM2.63861 3.17805C2.69051 3.13347 2.70337 3.13347 2.75527 3.17805C2.87727 3.28282 2.89665 3.28075 2.85707 3.16718C2.82313 3.06984 2.82781 3.05363 2.91026 2.98271L3.00066 2.90495H2.88478C2.78313 2.90495 2.76621 2.89267 2.74694 2.80495C2.71885 2.67705 2.67503 2.67705 2.64694 2.80495C2.62767 2.89267 2.61075 2.90495 2.5091 2.90495H2.39322L2.48362 2.98271C2.56608 3.05363 2.57075 3.06984 2.53681 3.16718C2.49722 3.28075 2.51661 3.28281 2.63861 3.17805ZM3.97194 3.17805C4.02384 3.13347 4.03671 3.13347 4.08861 3.17805C4.2106 3.28282 4.22999 3.28075 4.1904 3.16718C4.15647 3.06984 4.16114 3.05363 4.24359 2.98271L4.33399 2.90495H4.21811C4.11646 2.90495 4.09954 2.89267 4.08027 2.80495C4.05218 2.67705 4.00836 2.67705 3.98027 2.80495C3.96101 2.89267 3.94408 2.90495 3.84243 2.90495H3.72656L3.81695 2.98271C3.89941 3.05363 3.90408 3.06984 3.87015 3.16718C3.83056 3.28075 3.84994 3.28281 3.97194 3.17805ZM5.30527 3.17805C5.35717 3.13347 5.37004 3.13347 5.42194 3.17805C5.54394 3.28282 5.56332 3.28075 5.52373 3.16718C5.4898 3.06984 5.49447 3.05363 5.57693 2.98271L5.66732 2.90495H5.55145C5.4498 2.90495 5.43287 2.89267 5.41361 2.80495C5.38552 2.67705 5.3417 2.67705 5.31361 2.80495C5.29434 2.89267 5.27741 2.90495 5.17577 2.90495H5.05989L5.15029 2.98271C5.23274 3.05363 5.23742 3.06984 5.20348 3.16718C5.16389 3.28075 5.18328 3.28281 5.30527 3.17805ZM6.63861 3.17805C6.69051 3.13347 6.70337 3.13347 6.75527 3.17805C6.87727 3.28282 6.89665 3.28075 6.85707 3.16718C6.82313 3.06984 6.82781 3.05363 6.91026 2.98271L7.00066 2.90495H6.88478C6.78313 2.90495 6.76621 2.89267 6.74694 2.80495C6.71885 2.67705 6.67503 2.67705 6.64694 2.80495C6.62767 2.89267 6.61075 2.90495 6.5091 2.90495H6.39322L6.48362 2.98271C6.56608 3.05363 6.57075 3.06984 6.53681 3.16718C6.49722 3.28075 6.51661 3.28281 6.63861 3.17805ZM0.795607 2.63738L0.894353 2.70769L0.85707 2.60074C0.823037 2.50311 0.827668 2.48701 0.910183 2.41603L1.00058 2.33827H0.884704C0.783053 2.33827 0.76613 2.32599 0.746863 2.23827C0.718773 2.11038 0.674953 2.11038 0.646863 2.23827C0.627596 2.32599 0.610673 2.33827 0.509023 2.33827H0.393147L0.483543 2.41603C0.566057 2.48701 0.57069 2.50311 0.536657 2.60074L0.499373 2.70769L0.59812 2.63738L0.696867 2.56707L0.795607 2.63738ZM2.12894 2.63738L2.22769 2.70769L2.1904 2.60074C2.15637 2.50311 2.161 2.48701 2.24352 2.41603L2.33391 2.33827H2.21804C2.11639 2.33827 2.09946 2.32599 2.0802 2.23827C2.05211 2.11038 2.00829 2.11038 1.9802 2.23827C1.96093 2.32599 1.94401 2.33827 1.84236 2.33827H1.72648L1.81688 2.41603C1.89939 2.48701 1.90402 2.50311 1.86999 2.60074L1.83271 2.70769L1.93145 2.63738L2.0302 2.56707L2.12894 2.63738ZM3.46227 2.63738L3.56102 2.70769L3.52374 2.60074C3.4897 2.50311 3.49433 2.48701 3.57685 2.41603L3.66725 2.33827H3.55137C3.44972 2.33827 3.4328 2.32599 3.41353 2.23827C3.38544 2.11038 3.34162 2.11038 3.31353 2.23827C3.29426 2.32599 3.27734 2.33827 3.17569 2.33827H3.05981L3.15021 2.41603C3.23272 2.48701 3.23736 2.50311 3.20332 2.60074L3.16604 2.70769L3.26479 2.63738L3.36353 2.56707L3.46227 2.63738ZM4.79561 2.63738L4.89435 2.70769L4.85707 2.60074C4.82304 2.50311 4.82767 2.48701 4.91018 2.41603L5.00058 2.33827H4.8847C4.78305 2.33827 4.76613 2.32599 4.74686 2.23827C4.71877 2.11038 4.67495 2.11038 4.64686 2.23827C4.6276 2.32599 4.61067 2.33827 4.50902 2.33827H4.39315L4.48354 2.41603C4.56606 2.48701 4.57069 2.50311 4.53666 2.60074L4.49937 2.70769L4.59812 2.63738L4.69687 2.56707L4.79561 2.63738ZM6.12894 2.63738L6.22769 2.70769L6.1904 2.60074C6.15637 2.50311 6.161 2.48701 6.24352 2.41603L6.33391 2.33827H6.21804C6.11639 2.33827 6.09946 2.32599 6.0802 2.23827C6.05211 2.11038 6.00829 2.11038 5.9802 2.23827C5.96093 2.32599 5.944 2.33827 5.84236 2.33827H5.72648L5.81688 2.41603C5.89939 2.48701 5.90402 2.50311 5.86999 2.60074L5.83271 2.70769L5.93145 2.63738L6.0302 2.56707L6.12894 2.63738ZM7.46227 2.63738L7.56102 2.70769L7.52374 2.60074C7.4897 2.50311 7.49434 2.48701 7.57685 2.41603L7.66725 2.33827H7.55137C7.44972 2.33827 7.4328 2.32599 7.41353 2.23827C7.38544 2.11038 7.34162 2.11038 7.31353 2.23827C7.29426 2.32599 7.27734 2.33827 7.17569 2.33827H7.05981L7.15021 2.41603C7.23272 2.48701 7.23736 2.50311 7.20332 2.60074L7.16604 2.70769L7.26479 2.63738L7.36353 2.56707L7.46227 2.63738ZM1.30261 2.04978C1.35933 1.99845 1.3678 1.99845 1.42452 2.04978C1.51276 2.12964 1.53839 2.11795 1.51441 2.00879C1.49717 1.93029 1.50975 1.89967 1.58292 1.84212L1.67256 1.77161H1.55056C1.44168 1.77161 1.42642 1.76086 1.40856 1.67161C1.39756 1.61661 1.37731 1.57161 1.36356 1.57161C1.34981 1.57161 1.32956 1.61661 1.31856 1.67161C1.30071 1.76087 1.28545 1.77161 1.17657 1.77161H1.05457L1.1442 1.84212C1.21737 1.89967 1.22996 1.93029 1.21272 2.00879C1.18874 2.11795 1.21436 2.12964 1.30261 2.04978ZM2.63594 2.04978C2.69266 1.99845 2.70113 1.99845 2.75786 2.04978C2.8461 2.12964 2.87172 2.11795 2.84774 2.00879C2.8305 1.93029 2.84309 1.89967 2.91626 1.84212L3.00589 1.77161H2.88389C2.77501 1.77161 2.75975 1.76086 2.7419 1.67161C2.7309 1.61661 2.71065 1.57161 2.6969 1.57161C2.68315 1.57161 2.6629 1.61661 2.6519 1.67161C2.63405 1.76087 2.61879 1.77161 2.5099 1.77161H2.3879L2.47754 1.84212C2.55071 1.89967 2.56329 1.93029 2.54605 2.00879C2.52207 2.11795 2.5477 2.12964 2.63594 2.04978ZM3.96927 2.04978C4.026 1.99845 4.03447 1.99845 4.09119 2.04978C4.17943 2.12964 4.20505 2.11795 4.18108 2.00879C4.16384 1.93029 4.17642 1.89967 4.24959 1.84212L4.33922 1.77161H4.21723C4.10834 1.77161 4.09308 1.76086 4.07523 1.67161C4.06423 1.61661 4.04398 1.57161 4.03023 1.57161C4.01648 1.57161 3.99623 1.61661 3.98523 1.67161C3.96738 1.76087 3.95212 1.77161 3.84323 1.77161H3.72124L3.81087 1.84212C3.88404 1.89967 3.89662 1.93029 3.87938 2.00879C3.85541 2.11795 3.88103 2.12964 3.96927 2.04978ZM5.30261 2.04978C5.35933 1.99845 5.3678 1.99845 5.42452 2.04978C5.51276 2.12964 5.53839 2.11795 5.51441 2.00879C5.49717 1.93029 5.50975 1.89967 5.58292 1.84212L5.67256 1.77161H5.55056C5.44168 1.77161 5.42641 1.76086 5.40856 1.67161C5.39756 1.61661 5.37731 1.57161 5.36356 1.57161C5.34981 1.57161 5.32956 1.61661 5.31856 1.67161C5.30071 1.76087 5.28545 1.77161 5.17657 1.77161H5.05457L5.1442 1.84212C5.21737 1.89967 5.22996 1.93029 5.21272 2.00879C5.18874 2.11795 5.21436 2.12964 5.30261 2.04978ZM6.63594 2.04978C6.69266 1.99845 6.70113 1.99845 6.75786 2.04978C6.8461 2.12964 6.87172 2.11795 6.84774 2.00879C6.8305 1.93029 6.84309 1.89967 6.91626 1.84212L7.00589 1.77161H6.88389C6.77501 1.77161 6.75975 1.76086 6.7419 1.67161C6.7309 1.61661 6.71065 1.57161 6.6969 1.57161C6.68315 1.57161 6.6629 1.61661 6.6519 1.67161C6.63405 1.76087 6.61879 1.77161 6.5099 1.77161H6.3879L6.47754 1.84212C6.55071 1.89967 6.56329 1.93029 6.54605 2.00879C6.52207 2.11795 6.5477 2.12964 6.63594 2.04978ZM0.784273 1.49401L0.871683 1.55129L0.849134 1.44862C0.830224 1.36252 0.84105 1.33458 0.916217 1.27545L1.00585 1.20494H0.887337C0.782757 1.20494 0.766241 1.19318 0.746861 1.10494C0.71877 0.977042 0.674951 0.977042 0.646861 1.10494C0.62748 1.19318 0.610961 1.20494 0.506384 1.20494H0.387871L0.477504 1.27545C0.552674 1.33457 0.563497 1.36252 0.544587 1.44862L0.522038 1.55129L0.609448 1.49401C0.692082 1.43987 0.701643 1.43987 0.784273 1.49401ZM2.11761 1.49401L2.20502 1.55129L2.18247 1.44862C2.16356 1.36252 2.17438 1.33458 2.24955 1.27545L2.33918 1.20494H2.22067C2.11609 1.20494 2.09957 1.19318 2.08019 1.10494C2.0521 0.977042 2.00828 0.977042 1.98019 1.10494C1.96081 1.19318 1.94429 1.20494 1.83972 1.20494H1.7212L1.81084 1.27545C1.88601 1.33457 1.89683 1.36252 1.87792 1.44862L1.85537 1.55129L1.94278 1.49401C2.02541 1.43987 2.03498 1.43987 2.11761 1.49401ZM3.45094 1.49401L3.53835 1.55129L3.5158 1.44862C3.49689 1.36252 3.50772 1.33458 3.58288 1.27545L3.67252 1.20494H3.554C3.44942 1.20494 3.43291 1.19318 3.41353 1.10494C3.38544 0.977042 3.34162 0.977042 3.31353 1.10494C3.29415 1.19318 3.27763 1.20494 3.17305 1.20494H3.05454L3.14417 1.27545C3.21934 1.33457 3.23016 1.36252 3.21125 1.44862L3.1887 1.55129L3.27611 1.49401C3.35875 1.43987 3.36831 1.43987 3.45094 1.49401ZM4.78427 1.49401L4.87168 1.55129L4.84913 1.44862C4.83022 1.36252 4.84105 1.33458 4.91622 1.27545L5.00585 1.20494H4.88734C4.78276 1.20494 4.76624 1.19318 4.74686 1.10494C4.71877 0.977042 4.67495 0.977042 4.64686 1.10494C4.62748 1.19318 4.61096 1.20494 4.50638 1.20494H4.38787L4.4775 1.27545C4.55267 1.33457 4.5635 1.36252 4.54459 1.44862L4.52204 1.55129L4.60945 1.49401C4.69208 1.43987 4.70164 1.43987 4.78427 1.49401ZM6.11761 1.49401L6.20502 1.55129L6.18247 1.44862C6.16356 1.36252 6.17438 1.33458 6.24955 1.27545L6.33918 1.20494H6.22067C6.11609 1.20494 6.09957 1.19318 6.08019 1.10494C6.0521 0.977042 6.00828 0.977042 5.98019 1.10494C5.96081 1.19318 5.94429 1.20494 5.83972 1.20494H5.7212L5.81084 1.27545C5.88601 1.33457 5.89683 1.36252 5.87792 1.44862L5.85537 1.55129L5.94278 1.49401C6.02542 1.43987 6.03498 1.43987 6.11761 1.49401ZM7.45094 1.49401L7.53835 1.55129L7.5158 1.44862C7.49689 1.36252 7.50772 1.33458 7.58288 1.27545L7.67252 1.20494H7.554C7.44942 1.20494 7.43291 1.19318 7.41353 1.10494C7.38544 0.977042 7.34162 0.977042 7.31353 1.10494C7.29415 1.19318 7.27763 1.20494 7.17305 1.20494H7.05454L7.14417 1.27545C7.21934 1.33457 7.23016 1.36252 7.21125 1.44862L7.18871 1.55129L7.27612 1.49401C7.35875 1.43987 7.36831 1.43987 7.45094 1.49401Z"
        fill={className?.includes('fill') ? undefined : color}
      />
    </g>
    <defs>
      <clipPath id="clip0_3097_78289">
        <rect width={size} height={height} fill="white" transform="translate(0 0.733337)" />
      </clipPath>
    </defs>
  </svg>
);

export const IconGlobe = ({ size = 16, color = colors.brand[400], className }: Props): JSX.Element => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 15 16"
    fill="none"
    className={className}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      // eslint-disable-next-line max-len
      d="M7.50047 0.5C3.35763 0.5 0 3.85729 0 7.99922C0 12.1424 3.35763 15.5 7.50047 15.5C11.6424 15.5 15 12.1424 15 7.99922C15 3.85729 11.6424 0.5 7.50047 0.5ZM7.88379 1.28949C7.94482 1.27424 7.99548 1.24861 8.03851 1.2553C8.36658 1.31055 8.69953 1.34565 9.00288 1.49762C9.11825 1.55562 9.22322 1.63587 9.33857 1.69293C9.43198 1.73872 9.53786 1.75888 9.63248 1.80372C9.76493 1.86569 9.79271 1.96488 9.69533 2.08907C9.75271 2.13851 9.80521 2.18519 9.8574 2.23007C9.78414 2.37595 9.81162 2.48948 9.93187 2.60483C9.99778 2.66892 10.053 2.7434 10.1266 2.82821C9.90349 2.92465 9.7402 2.84652 9.58364 2.76717C9.4692 2.70889 9.35782 2.63658 9.26138 2.55051C9.17347 2.47239 9.10969 2.3686 9.03341 2.27766C9.00869 2.24716 8.98 2.21573 8.94643 2.19651C8.79687 2.11469 8.64977 2.02194 8.49139 1.96213C8.36839 1.9145 8.23017 1.88857 8.09863 1.88675C7.91735 1.88582 7.83128 1.81685 7.82366 1.63191C7.82181 1.57944 7.79313 1.52815 7.77147 1.45643C7.84103 1.44331 7.88956 1.43262 7.95366 1.42012C7.93079 1.3774 7.91154 1.34108 7.88379 1.28949ZM9.40144 8.24825C9.20491 8.37032 9.1335 8.09384 8.96747 8.12525C9.09809 8.0624 9.081 7.79993 9.31082 7.88752C9.41213 7.92596 9.53694 7.91165 9.58088 8.0575C9.49514 8.0795 9.41488 8.09931 9.31966 8.12253C9.35016 8.17013 9.37307 8.20431 9.40144 8.24825ZM8.88262 2.83767C8.90369 2.86821 8.85578 2.94724 8.83015 3.03818C8.61468 2.97777 8.38764 2.91305 8.13586 2.84255C8.20545 2.76352 8.2732 2.68601 8.36568 2.58101C8.26649 2.57064 8.18805 2.56392 8.08033 2.55354C7.97748 2.3036 7.7016 2.46382 7.49835 2.39913C7.44522 2.38172 7.38206 2.34054 7.361 2.29416C7.34575 2.26057 7.38207 2.16719 7.41532 2.15469C7.49835 2.12631 7.59692 2.10616 7.68176 2.12235C7.84289 2.15285 7.99946 2.20444 8.15477 2.25967C8.36263 2.33201 8.57075 2.40767 8.77004 2.49986C8.8216 2.52305 8.84266 2.61155 8.8921 2.69639C8.79291 2.69639 8.74135 2.69639 8.67941 2.69639C8.7624 2.75008 8.84265 2.77939 8.88262 2.83767ZM7.5911 1.41646C7.71135 1.38958 7.75713 1.45368 7.74857 1.57087C7.72754 1.84675 7.63597 1.92581 7.36222 1.88766C7.38206 1.79794 7.39946 1.71981 7.41928 1.63496C7.36406 1.60718 7.3134 1.5834 7.26212 1.55865C7.2609 1.54524 7.25906 1.5318 7.25815 1.51746C7.36893 1.48299 7.4785 1.44118 7.5911 1.41646ZM12.6773 10.7944C12.5668 11.0614 12.4313 11.3181 12.308 11.5802C12.2406 11.7237 12.1756 11.8674 12.1154 12.0136C11.9924 12.3139 11.6977 12.4351 11.4746 12.6307C11.4248 12.6734 11.3674 12.7164 11.3381 12.7717C11.1318 13.1705 10.7656 13.4186 10.4537 13.7162C10.4195 13.7507 10.3585 13.7534 10.3173 13.7818C10.2343 13.841 10.1552 13.906 10.0768 13.9729C10.0463 13.9976 10.0274 14.0394 9.99381 14.0586C9.76583 14.1834 9.53024 14.2942 9.30989 14.4297C9.15151 14.5271 9.01235 14.6568 8.86557 14.7721C8.79598 14.8274 8.73097 14.8884 8.65741 14.9391C8.57928 14.9946 8.45906 14.9992 8.45447 15.1393C8.45325 15.1612 8.36171 15.1966 8.31043 15.1985C8.22649 15.2024 8.14043 15.185 8.00799 15.1719C8.11571 15.0297 8.20055 14.9134 8.29027 14.8008C8.35712 14.7187 8.44562 14.6635 8.45815 14.5307C8.46668 14.4364 8.5564 14.3427 8.62415 14.2609C8.99616 13.8154 9.22598 13.306 9.3282 12.7366C9.34439 12.6429 9.33492 12.5751 9.23757 12.5409C9.22811 12.5373 9.22016 12.5287 9.21073 12.5257C8.86738 12.4073 8.63177 12.2013 8.53534 11.8274C8.50024 11.691 8.34552 11.5802 8.23595 11.4679C8.08795 11.3153 8.08033 11.2283 8.21002 11.0651C8.23289 11.0367 8.25305 10.9729 8.2378 10.9488C8.10992 10.7678 8.22833 10.6198 8.30064 10.47C8.3333 10.4022 8.38764 10.3412 8.44683 10.2936C8.68518 10.0952 8.63849 9.75646 8.50787 9.55443C8.43615 9.44549 8.35499 9.43967 8.29395 9.55624C8.23289 9.6704 8.16639 9.67343 8.05926 9.62096C7.8142 9.4998 7.56425 9.39786 7.41653 9.14029C7.33172 8.99228 7.1779 8.92575 7.00149 8.92178C6.79458 8.91781 6.65542 8.78353 6.5187 8.65565C6.43507 8.57662 6.35572 8.5574 6.25928 8.61447C6.14881 8.68037 6.04931 8.65353 5.94556 8.59065C5.69652 8.4399 5.44383 8.29402 5.18992 8.15121C5.10326 8.10236 5.08219 8.03827 5.07273 7.94214C5.06297 7.83135 5.03185 7.71235 4.97448 7.61773C4.78769 7.30888 4.5814 7.01103 4.38853 6.70493C4.34581 6.63627 4.3229 6.55417 4.29056 6.47789C4.27134 6.48552 4.25059 6.49405 4.23137 6.50167C4.36471 6.85171 4.4984 7.20084 4.63206 7.5512C4.62047 7.56157 4.60916 7.57195 4.59666 7.58354C4.5405 7.53107 4.47094 7.48713 4.42881 7.42426C4.34396 7.29241 4.26277 7.15691 4.20174 7.01287C4.15415 6.89934 4.14743 6.76871 4.10837 6.6503C4.03755 6.43486 3.95942 6.23161 3.81846 6.0381C3.61214 5.75185 3.54073 5.40455 3.68295 5.04992C3.75161 4.87901 3.82518 4.71117 3.89474 4.54029C3.93258 4.44873 3.96615 4.3535 4.12059 4.39563C4.06746 4.30591 4.00643 4.22107 3.96249 4.12644C3.93381 4.06359 3.90421 3.9824 3.91946 3.91953C3.99362 3.60946 3.87156 3.32811 3.76108 3.06076C3.71896 2.95973 3.60848 2.77573 3.40614 2.8166C3.25448 2.64388 3.17544 2.84651 3.05976 2.87889C2.87391 2.93014 2.68134 2.95793 2.4903 2.99058C2.46374 2.99515 2.4314 2.9674 2.39874 2.91977C2.57269 2.87308 2.74512 2.82729 2.92822 2.77877C2.89375 2.74033 2.86935 2.71164 2.85285 2.69454C2.909 2.59751 2.94748 2.49801 3.01523 2.4266C3.07135 2.36647 3.16658 2.29475 3.23648 2.30269C3.38905 2.32069 3.43208 2.26729 3.42355 2.13087C3.42233 2.11347 3.43758 2.09578 3.45408 2.06037C3.61399 2.15469 3.71224 2.09578 3.79528 1.94503C3.84715 1.85531 4.181 1.83822 4.27044 1.91237C4.37632 1.99812 4.47826 2.09272 4.56311 2.19954C4.61683 2.26548 4.6577 2.28438 4.74071 2.26913C5.03246 2.21388 5.26747 2.33016 5.48475 2.51723C5.55066 2.57429 5.65444 2.58682 5.74538 2.62129C5.93885 2.41804 6.16502 2.69364 6.37864 2.55629C6.38442 2.56576 6.37648 2.5392 6.36061 2.51998C6.31943 2.46932 6.23917 2.42354 6.23549 2.37107C6.21842 2.12903 6.27639 2.08419 6.52056 2.14216C6.62003 2.16719 6.73541 2.15745 6.79094 2.29873C6.82022 2.37595 6.93957 2.41501 7.01128 2.4791C7.04363 2.50779 7.07782 2.56026 7.07413 2.59933C7.07016 2.64511 7.03691 2.71377 6.99969 2.72811C6.78025 2.81205 6.67434 3.0144 6.53337 3.17247C6.38443 3.33757 6.47478 3.57591 6.6945 3.64091C6.84525 3.68669 6.99969 3.72851 7.15532 3.75354C7.28961 3.77429 7.35736 3.83473 7.39367 3.96627C7.44799 4.1628 7.52796 4.20583 7.6949 4.15212C7.67871 4.10543 7.66559 4.05871 7.64637 4.01386C7.60149 3.91102 7.54993 3.81764 7.66346 3.71326C7.72265 3.65895 7.69887 3.57988 7.63202 3.51307C7.56824 3.44835 7.50049 3.36535 7.48248 3.28051C7.44342 3.09619 7.50505 3.02844 7.68849 3.04554C7.82274 3.05804 7.96131 3.08 8.05378 3.20788C8.09009 3.25916 8.16519 3.31651 8.21491 3.30982C8.25794 3.30432 8.28938 3.21275 8.34188 3.13647C8.4371 3.20116 8.54604 3.27929 8.65836 3.35101C8.78926 3.4337 8.91898 3.5216 9.0563 3.59057C9.17655 3.65039 9.31846 3.68273 9.30809 3.85455C9.29952 4.00074 9.19852 4.09289 9.00474 4.11486C8.74136 4.14477 8.58786 4.29158 8.5192 4.547C8.57748 4.48902 8.63454 4.4289 8.69377 4.37184C8.83017 4.23818 8.91805 4.24364 8.99618 4.41274C9.05721 4.547 9.14206 4.62543 9.30809 4.60256C9.309 4.61599 9.309 4.62909 9.3099 4.64162C9.23759 4.72737 9.16496 4.81434 9.07736 4.91719C9.0447 4.84975 9.02183 4.80184 8.98854 4.73409C8.86923 4.85525 8.66322 4.89156 8.7206 5.12991C8.72823 5.16041 8.68704 5.21963 8.65285 5.23976C8.52194 5.31608 8.47066 5.43723 8.42853 5.5718C8.41238 5.6243 8.37147 5.66796 8.32569 5.73967C8.47066 5.97527 8.28631 6.13153 8.12062 6.29387C8.09193 6.32315 8.05743 6.34696 8.02875 6.37565C7.92499 6.47575 7.88012 6.58197 7.95156 6.72966C8.01074 6.85297 8.04402 6.98817 8.07665 7.12273C8.091 7.18073 8.07178 7.24786 8.06812 7.30952C8.0489 7.31927 8.02996 7.32874 8.01074 7.33821C7.91828 7.22467 7.79712 7.12273 7.74189 6.99395C7.67689 6.84229 7.58258 6.7791 7.43548 6.76323C7.36498 6.75532 7.29325 6.75073 7.22185 6.74798C7.10344 6.7422 6.97769 6.72967 7.01675 6.92805C6.86234 6.90243 6.72868 6.87739 6.59499 6.85542C6.41312 6.82611 6.19552 6.95367 6.13358 7.12824C6.10124 7.21918 6.07255 7.31165 6.04017 7.40412C5.92942 7.72641 6.10489 7.96504 6.28433 8.19576C6.38137 8.32089 6.76284 8.27208 6.84985 8.1387C6.86907 8.1082 6.88707 8.07676 6.90416 8.04623C7.03507 7.82651 7.07507 7.80941 7.36102 7.86985C7.3238 7.97667 7.2887 8.08132 7.2487 8.18358C7.10345 8.54978 7.1303 8.58977 7.51758 8.58215C7.81512 8.57724 7.8429 8.60534 7.83803 8.90562C7.83709 8.96178 7.84015 9.01794 7.84106 9.07441C7.84475 9.32341 8.05194 9.45801 8.27107 9.34266C8.37698 9.28832 8.4667 9.30726 8.56223 9.34998C8.65742 9.39392 8.71298 9.36248 8.78255 9.28435C8.9019 9.14794 9.04015 9.02678 9.17837 8.90928C9.21468 8.87878 9.30137 8.85496 9.33006 8.87603C9.49303 8.99413 9.6926 8.98741 9.85376 8.96484C10.2096 8.91722 10.5071 9.01062 10.7916 9.19922C10.9384 9.29687 11.0928 9.34325 11.2704 9.30419C11.3665 9.28222 11.4374 9.3225 11.4822 9.41313C11.5051 9.45891 11.5289 9.50559 11.5622 9.54285C11.6882 9.68779 11.7386 9.8462 11.651 10.0064C11.9323 10.0018 12.1908 9.98048 12.4475 9.99573C12.5687 10.0037 12.6877 10.072 12.8024 10.1257C12.829 10.138 12.8491 10.2087 12.8397 10.2429C12.7926 10.4291 12.7508 10.6177 12.6773 10.7944ZM8.92566 7.86096C8.83687 8.01753 8.58599 8.03768 8.48377 7.93362C8.33423 7.78193 8.12914 7.76758 7.92864 7.76086C7.8151 7.75689 7.70282 7.77155 7.58441 7.75689C7.71897 7.65802 7.8676 7.57014 8.0327 7.59698C8.24452 7.63117 8.46668 7.6397 8.65284 7.78377C8.71968 7.83534 8.82434 7.83443 8.92566 7.86096Z"
      fill={className?.includes('fill') ? undefined : color}
    />
  </svg>
);

export const IconKanban2 = ({ size = 24, height = 20, color = colors.brand[400] }: Props): JSX.Element => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={height} viewBox="0 0 25 20" fill="none">
    <path
      // eslint-disable-next-line max-len
      d="M7.85714 3.51634L1.64913 4.80968C1.46374 4.84829 1.29916 4.93743 1.18155 5.06293C1.06393 5.18844 1.00003 5.3431 1 5.50234V17.6452C1.00002 17.7537 1.02974 17.8608 1.08688 17.9584C1.14402 18.0559 1.22709 18.1413 1.32977 18.2081C1.43246 18.2749 1.55206 18.3213 1.67948 18.3438C1.80691 18.3662 1.9388 18.3642 2.06515 18.3379L7.85714 17.1316V3.51634Z"
      fill={color}
    />
    <path d="M16.4286 5.25663L9.57141 3.54234V17.1766L16.4286 18.8909V5.25663Z" fill={color} />
    <path
      // eslint-disable-next-line max-len
      d="M24.6702 4.22513C24.568 4.15771 24.4484 4.11092 24.3208 4.08842C24.1932 4.06591 24.0611 4.06829 23.9349 4.09539L18.1429 5.30162V18.9169L24.3509 17.6236C24.5363 17.585 24.7009 17.4958 24.8185 17.3703C24.9361 17.2448 25 17.0901 25 16.9309V4.78805C25 4.67951 24.9702 4.57241 24.9131 4.47487C24.8559 4.37733 24.7729 4.29193 24.6702 4.22513Z"
      fill={color}
    />
  </svg>
);

export const IconDockWindow = ({ size = 20, color = colors.steel[400], className }: Props): JSX.Element => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    className={className}
  >
    <path
      // eslint-disable-next-line max-len
      d="M15.0003 14.9997V16.6663H3.33366C2.89163 16.6663 2.46771 16.4907 2.15515 16.1782C1.84259 15.8656 1.66699 15.4417 1.66699 14.9997V6.66634H3.33366V14.9997M18.3337 4.99967V11.6663C18.3337 12.1084 18.1581 12.5323 17.8455 12.8449C17.5329 13.1574 17.109 13.333 16.667 13.333H6.66699C6.22496 13.333 5.80104 13.1574 5.48848 12.8449C5.17592 12.5323 5.00033 12.1084 5.00033 11.6663V4.99967C5.00033 4.55765 5.17592 4.13372 5.48848 3.82116C5.80104 3.5086 6.22496 3.33301 6.66699 3.33301H16.667C17.109 3.33301 17.5329 3.5086 17.8455 3.82116C18.1581 4.13372 18.3337 4.55765 18.3337 4.99967ZM16.667 4.99967H6.66699V11.6663H16.667V4.99967Z"
      fill={className?.includes('fill') ? undefined : color}
    />
  </svg>
);

export function IconContractAssessment({
  size = 20,
  color = colors.steel[400],
  foregroundColor = '#fff',
}: Props): JSX.Element {
  return (
    <svg width={size} height={size} fill="none">
      <defs>
        <mask id="search">
          <rect width="100%" height="100%" fill="white" />
          <path
            // eslint-disable-next-line max-len
            d="m10.5584,14.0898c0.4132,-0.6786 0.6317,-1.4578 0.6317,-2.2524c0,-2.40693 -1.94764,-4.35597 -4.35508,-4.35693c-2.40528,0.00072 -4.35504,1.95 -4.35504,4.35643c0,2.4044 1.95,4.3534 4.35648,4.3534c0.8256,0 1.5948,-0.2333 2.25216,-0.6319l2.96228,2.9621l1.4691,-1.4698l-2.9616,-2.9609zm-3.72242,0.4419c-1.48848,-0.0036 -2.69184,-1.2065 -2.69448,-2.6919c0.00102,-0.7143 0.28521,-1.3991 0.79029,-1.9043c0.50508,-0.50517 1.18984,-0.78949 1.90419,-0.79063c1.48776,0.00384 2.6916,1.20673 2.69472,2.69493c-0.00312,1.4837 -1.20744,2.6883 -2.69472,2.6919z"
            fill="black"
          />
        </mask>
      </defs>
      <path
        // eslint-disable-next-line max-len
        d="m9.5,0.83301l-8.25,0c-0.19891,0 -0.38968,0.08048 -0.53033,0.22374c-0.14065,0.14325 -0.21967,0.33755 -0.21967,0.54015l0,16.8056c0,0.2025 0.07902,0.3968 0.21967,0.5401c0.14065,0.1433 0.33142,0.2237 0.53033,0.2237l13.5,0c0.1989,0 0.3897,-0.0804 0.5303,-0.2237c0.1407,-0.1433 0.2197,-0.3376 0.2197,-0.5401l0,-11.45838l-5.25,0c-0.1989,0 -0.38968,-0.08048 -0.53033,-0.22374c-0.14065,-0.14326 -0.21967,-0.33755 -0.21967,-0.54015l0,-5.34722z"
        fill={color}
        mask="url(#search)"
      />
      <path
        // eslint-disable-next-line max-len
        d="m10.5584,14.0898c0.4132,-0.6786 0.6317,-1.4578 0.6317,-2.2524c0,-2.40693 -1.94764,-4.35597 -4.35508,-4.35693c-2.40528,0.00072 -4.35504,1.95 -4.35504,4.35643c0,2.4044 1.95,4.3534 4.35648,4.3534c0.8256,0 1.5948,-0.2333 2.25216,-0.6319l2.96228,2.9621l1.4691,-1.4698l-2.9616,-2.9609zm-3.72242,0.4419c-1.48848,-0.0036 -2.69184,-1.2065 -2.69448,-2.6919c0.00102,-0.7143 0.28521,-1.3991 0.79029,-1.9043c0.50508,-0.50517 1.18984,-0.78949 1.90419,-0.79063c1.48776,0.00384 2.6916,1.20673 2.69472,2.69493c-0.00312,1.4837 -1.20744,2.6883 -2.69472,2.6919z"
        fill={foregroundColor}
      />
      <path d="m15.0605,5.41792l-4.0605,0l0,-4.13569l4.0605,4.13569z" fill={color} />
    </svg>
  );
}

export function IconRiskPulse({ size = 20, color = colors.steel[400] }: Props): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M16 0H2C0.9 0 0 0.9 0 2V16C0 17.1 0.9 18 2 18H16C17.1 18 18 17.1 18 16V2C18 0.9 17.1 0 16 0ZM6 14H4V9H6V14ZM10 14H8V11H10V14ZM10 9H8V7H10V9ZM14 14H12V4H14V14Z"
        fill={color}
      />
    </svg>
  );
}

export function IconPaperClip({ size = 20, color = colors.steel[400] }: Props): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 250 250" fill={color}>
      <path
        // eslint-disable-next-line max-len
        d="M 114.268 168.871 C 113.607 195.658 89.6612 211.353 66.5709 200.213 C 53.7722 194.039 48.0874 182.707 47.9342 168.913 C 47.6291 141.426 47.6732 113.933 47.8506 86.4442 C 47.9346 73.4262 57.2013 64.3522 69.4883 64.5077 C 80.5619 64.6479 89.4654 74.1144 89.5238 86.4263 C 89.6335 109.552 89.415 132.68 89.4296 155.806 C 89.4318 159.249 88.1222 161.662 84.9908 162.851 C 82.399 163.835 80.0996 162.826 78.1226 161.026 C 76.2507 159.323 76.688 157.052 76.6857 154.929 C 76.662 133.103 76.6714 111.276 76.6642 89.4489 C 76.6635 87.123 76.834 84.7663 75.5755 82.6332 C 73.8154 79.6501 71.3516 77.854 67.7643 78.4655 C 64.1919 79.0745 62.2119 81.4097 61.9368 84.9743 C 61.7069 87.9534 61.6973 90.9556 61.7168 93.9468 C 61.8832 119.431 62.3389 144.914 61.5262 170.398 C 61.1858 181.071 70.3631 190.508 80.8671 190.6 C 90.505 190.684 100.537 181.086 100.592 170.883 C 100.779 136.227 100.857 101.569 100.57 66.9147 C 100.447 52.0936 87.5452 39.6219 71.4217 37.6029 C 57.598 35.8719 42.9567 46.17 38.5383 60.755 C 37.7157 63.4705 37.2918 66.2267 37.2957 69.1036 C 37.3329 96.4293 37.2944 123.755 37.2754 151.081 C 37.2742 152.747 37.3407 154.419 37.224 156.078 C 36.9279 160.284 34.0624 163.197 30.348 163.177 C 26.6438 163.158 23.7287 160.192 23.7012 155.951 C 23.6007 140.461 23.6141 124.971 23.6082 109.481 C 23.6032 96.4882 23.6871 83.4948 23.6328 70.5019 C 23.5249 44.6728 42.5535 24.2964 68.4034 23.9382 C 91.7908 23.614 109.551 40.7749 113.338 62.9952 C 114.213 68.1288 114.25 73.2573 114.251 78.4146 C 114.257 108.406 114.265 138.398 114.268 168.871 Z"
        fill={color}
      />
    </svg>
  );
}

export function IconMedicalSheild({ size = 18, height = 22, color = colors.brand[200] }: Props): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={height} viewBox="0 0 17 22" fill="none">
      <path
        // eslint-disable-next-line max-len
        d="M16.1365 3.24696C10.4248 3.03132 9.3455 0.584568 9.3455 0.584568C9.21957 0.233871 8.88677 0.00900346 8.51806 0C8.14014 0 7.79844 0.215864 7.6635 0.557777C7.6545 0.584568 6.57507 3.03132 0.863566 3.24696C0.377817 3.26497 0 3.66068 0 4.14643V10.029C0 14.6884 2.60848 18.9517 6.63809 20.8947C7.14185 21.1285 7.6725 21.3444 8.21216 21.5243C8.31109 21.5603 8.40102 21.5693 8.50005 21.5693C8.58987 21.5693 8.68891 21.5603 8.77883 21.5333C9.31849 21.3534 9.83125 21.1465 10.3619 20.8947C14.3915 18.9609 17 14.6972 17 10.029V4.14643C17 3.66068 16.6222 3.26497 16.1365 3.24696ZM12.2148 9.6603L11.0365 10.8026L11.3153 12.4217C11.3693 12.7184 11.2434 13.0153 11.0005 13.1861C10.8656 13.2852 10.7037 13.3392 10.5508 13.3392C10.4248 13.3392 10.299 13.3122 10.1819 13.2492L8.72492 12.4845L7.27668 13.2492C7.00691 13.393 6.69211 13.366 6.45824 13.1861C6.21537 13.0153 6.08943 12.7184 6.14334 12.4217L6.41322 10.8026L5.24387 9.6603C5.02801 9.45344 4.95609 9.13853 5.04601 8.85987C5.13605 8.58098 5.37881 8.37412 5.66659 8.3291L7.30369 8.09523L8.02331 6.61997C8.15814 6.35031 8.42803 6.17946 8.72492 6.17946C9.02171 6.17946 9.29148 6.35031 9.42643 6.61997L10.155 8.09523L11.783 8.3291C12.0709 8.37412 12.3138 8.58098 12.4126 8.85987C12.5027 9.13853 12.4216 9.45344 12.2148 9.6603Z"
        fill={color}
      />
    </svg>
  );
}

export function IconMedicalStar({ size = 18, color = colors.brand[200] }: Props): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 17 17" fill="none">
      <path
        // eslint-disable-next-line max-len
        d="M14.4928 2.01245L11.0072 4.0249V0H5.99279V4.0249L2.50721 2.01245L0 6.35507L3.48559 8.36752L0 10.38L2.50721 14.7226L5.99279 12.7101V16.735H11.0072V12.7101L14.4928 14.7226L17 10.38L13.5144 8.36752L17 6.35507L14.4928 2.01245Z"
        fill={color}
      />
    </svg>
  );
}

export function IconPerson({ size = 18, color = colors.brand[200] }: Props): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 17 19" fill="none">
      <path
        d="M8.5002 9.72393C11.0097 9.72393 13.051 7.06049 13.051 4.55119C13.051 2.04189 11.0097 0 8.5002 0C5.99069 0 3.94922 2.04189 3.94922 4.55119C3.94964 7.06028 5.9909 9.72393 8.5002 9.72393Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M11.9164 9.77051C11.0337 10.4431 9.93243 10.8432 8.73954 10.8432H8.26046C7.06736 10.8432 5.96591 10.4429 5.08338 9.77051C2.20103 10.2365 0 12.7347 0 15.7483C0 17.4038 3.8055 18.7462 8.5 18.7462C13.1943 18.7462 17 17.4038 17 15.7483C17 12.7347 14.7988 10.2365 11.9164 9.77051Z"
        fill={color}
      />
    </svg>
  );
}

// TODO Check if regular clock can be used here since this is BS
export function IconClock2({ size = 18, color = colors.brand[200] }: Props): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 17 19" fill="none">
      <path
        d="M8.5002 9.72393C11.0097 9.72393 13.051 7.06049 13.051 4.55119C13.051 2.04189 11.0097 0 8.5002 0C5.99069 0 3.94922 2.04189 3.94922 4.55119C3.94964 7.06028 5.9909 9.72393 8.5002 9.72393Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M11.9164 9.77051C11.0337 10.4431 9.93243 10.8432 8.73954 10.8432H8.26046C7.06736 10.8432 5.96591 10.4429 5.08338 9.77051C2.20103 10.2365 0 12.7347 0 15.7483C0 17.4038 3.8055 18.7462 8.5 18.7462C13.1943 18.7462 17 17.4038 17 15.7483C17 12.7347 14.7988 10.2365 11.9164 9.77051Z"
        fill={color}
      />
    </svg>
  );
}

export function IconWheelchair({ size = 18, height = 20, color = colors.brand[200] }: Props): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={height} viewBox="0 0 17 20" fill="none">
      <path
        // eslint-disable-next-line max-len
        d="M5.85968 1.99565C5.85968 0.893272 6.75295 0 7.85533 0C8.95772 0 9.85099 0.893272 9.85099 1.99565C9.85099 3.09804 8.95772 3.99131 7.85533 3.99131C6.75295 3.99131 5.85968 3.09804 5.85968 1.99565ZM16.6244 14.2651L13.4241 11.0647C13.3945 11.0352 13.3627 11.0147 13.3332 10.9897C13.3059 10.9647 13.2763 10.9397 13.2445 10.917C13.2331 10.9102 13.2241 10.9011 13.2127 10.8943C13.0013 10.7465 12.7445 10.6579 12.4672 10.6579H9.88735V8.78724H12.258C12.7536 8.78724 13.1559 8.38493 13.1559 7.88943C13.1559 7.39392 12.7536 6.99161 12.258 6.99161H9.88735V6.78477C9.88735 5.66193 8.97817 4.75048 7.85306 4.75048C6.72795 4.75048 5.81876 5.65966 5.81876 6.78477V12.1671C5.81876 12.7672 6.30518 13.2536 6.90524 13.2536H11.9376L14.7833 16.0993C15.2902 16.6062 16.113 16.6062 16.6198 16.0993C17.1267 15.5925 17.1267 14.7697 16.6198 14.2628L16.6244 14.2651ZM11.3852 15.3765C10.3556 16.6335 8.83498 17.354 7.21209 17.354C4.23679 17.354 1.81609 14.9333 1.81609 11.958C1.81609 10.0692 2.82528 8.29174 4.45045 7.32346C4.88231 7.06662 5.02323 6.50747 4.76639 6.07788C4.50954 5.64602 3.9504 5.5051 3.52081 5.76194C1.34786 7.05753 0 9.43276 0 11.958C0 15.9357 3.23669 19.1724 7.21436 19.1724C9.38503 19.1724 11.4193 18.2086 12.7945 16.5289C13.1127 16.1403 13.0559 15.5675 12.6672 15.2493C12.2785 14.931 11.7057 14.9879 11.3875 15.3765H11.3852Z"
        fill={color}
      />
    </svg>
  );
}

export function IconCallCenter({ size = 18, height = 20, color = colors.brand[200] }: Props): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={height} viewBox="0 0 17 19" fill="none">
      <path
        // eslint-disable-next-line max-len
        d="M8.5 0C5.29934 0 2.70455 2.5948 2.70455 5.79545V7.72727H2.51136C1.13009 7.72727 0 8.85735 0 10.2386V11.7841C0 13.1654 1.13009 14.2955 2.51136 14.2955H4.44318C4.74662 14.2954 5.0227 14.0193 5.02273 13.7159V8.30682C5.0227 8.00337 4.74662 7.72729 4.44318 7.72727H3.86364V5.79545C3.86364 3.21688 5.92143 1.15909 8.5 1.15909C11.0786 1.15909 13.1364 3.21688 13.1364 5.79545V7.72727H12.5568C12.1871 7.74659 11.9836 8.06258 11.9773 8.30682V13.7159C11.9773 14.0193 12.2534 14.2954 12.5568 14.2955H13.0398C12.9234 14.648 12.6964 14.9741 12.3214 15.2614C11.8152 15.649 11.0312 15.9557 9.92472 16.022C9.68974 15.4656 9.13698 15.0682 8.5 15.0682C7.65333 15.0682 6.95455 15.767 6.95455 16.6136C6.95455 17.4603 7.65333 18.1591 8.5 18.1591C9.14823 18.1591 9.70917 17.7531 9.93679 17.1811C11.2491 17.1122 12.2854 16.7535 13.0277 16.185C13.6828 15.6832 14.0913 15.0119 14.2351 14.2955H14.4886C15.8699 14.2955 17 13.1654 17 11.7841V10.2386C17 8.85735 15.8699 7.72727 14.4886 7.72727H14.2955V5.79545C14.2955 2.5948 11.7007 0 8.5 0Z"
        fill={color}
      />
    </svg>
  );
}

export function IconDollar({ size = 12, height = 17, color = colors.brand[200] }: Props): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={height} viewBox="0 0 10 17" fill="none">
      <path
        // eslint-disable-next-line max-len
        d="M2.53142 10.3411C2.51945 11.0233 2.69898 11.5619 3.07002 11.9569C3.44105 12.3519 3.90185 12.6032 4.45242 12.711V8.95871C4.39258 8.93477 4.3088 8.91083 4.20108 8.88689C4.10533 8.85099 3.99761 8.82106 3.87792 8.79713C3.43507 8.67744 2.9982 8.5398 2.56732 8.3842C2.13645 8.21664 1.74746 8.0012 1.40036 7.73788C1.06523 7.4626 0.795931 7.12747 0.592459 6.7325C0.388989 6.32555 0.287253 5.82286 0.287253 5.22442C0.287253 4.614 0.400957 4.0754 0.628366 3.60862C0.867744 3.14183 1.17893 2.75284 1.56194 2.44165C1.94494 2.11849 2.38779 1.87313 2.89048 1.70556C3.39318 1.52603 3.91382 1.41831 4.45242 1.38241V0H5.51167V1.38241C6.05027 1.44225 6.55296 1.56194 7.01975 1.74147C7.4985 1.90904 7.91741 2.14841 8.27648 2.45961C8.63555 2.75883 8.92879 3.13585 9.15619 3.59066C9.3836 4.04548 9.51526 4.58408 9.55117 5.20646H7.00179C6.98983 4.72771 6.84021 4.32077 6.55296 3.98564C6.26571 3.65051 5.91861 3.48294 5.51167 3.48294V6.66068C5.6553 6.69659 5.79892 6.73249 5.94255 6.7684C6.09814 6.80431 6.25972 6.8462 6.42729 6.89407C7.31299 7.13345 7.99521 7.43267 8.47397 7.79174C8.95272 8.13884 9.3058 8.50389 9.53321 8.88689C9.76062 9.2699 9.89228 9.6529 9.92819 10.0359C9.97606 10.4189 10 10.76 10 11.0592C10 11.3226 9.93417 11.6577 9.80251 12.0646C9.67085 12.4596 9.43148 12.8486 9.08438 13.2316C8.74925 13.6146 8.29443 13.9557 7.71993 14.2549C7.14542 14.5542 6.40934 14.7337 5.51167 14.7935V16.3196H4.45242V14.7935C3.11191 14.6978 2.06463 14.2849 1.31059 13.5548C0.556553 12.8247 0.119689 11.7534 0 10.3411H2.53142ZM5.51167 12.711C5.72711 12.687 5.94853 12.6391 6.17594 12.5673C6.41532 12.4835 6.62478 12.3698 6.80431 12.2262C6.99581 12.0826 7.15141 11.909 7.27109 11.7056C7.39078 11.4901 7.45063 11.2388 7.45063 10.9515C7.45063 10.4847 7.30102 10.1376 7.00179 9.91023C6.71454 9.67086 6.21783 9.45541 5.51167 9.26391V12.711ZM4.45242 3.48294C4.24895 3.48294 4.05147 3.51287 3.85996 3.57271C3.66846 3.63255 3.49491 3.72831 3.33932 3.85996C3.18372 3.97965 3.05805 4.13525 2.9623 4.32675C2.87852 4.50628 2.83662 4.72172 2.83662 4.97307C2.83662 5.36804 2.9623 5.67325 3.21364 5.88869C3.46499 6.10413 3.87792 6.27768 4.45242 6.40933V3.48294Z"
        fill={color}
      />
    </svg>
  );
}

export function IconHappyFace({ size = 18, color = colors.brand[200] }: Props): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 18 18" fill="none">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        // eslint-disable-next-line max-len
        d="M8.75 0.25C4.05879 0.25 0.25 4.05879 0.25 8.75C0.25 13.4412 4.05879 17.25 8.75 17.25C13.4412 17.25 17.25 13.4412 17.25 8.75C17.25 4.05879 13.4412 0.25 8.75 0.25ZM11.4384 11.5562C10.8248 12.373 9.84828 12.9012 8.75 12.9012C7.65172 12.9012 6.67521 12.373 6.06163 11.5562C5.86554 11.2945 5.49312 11.2423 5.2314 11.4384C4.96967 11.6353 4.91749 12.0077 5.11358 12.2694C5.94381 13.3732 7.26428 14.0872 8.75 14.0872C10.2357 14.0872 11.5562 13.3732 12.3864 12.2694C12.5825 12.0077 12.5303 11.6353 12.2686 11.4384C12.0069 11.2423 11.6345 11.2945 11.4384 11.5562ZM11.9128 5.98256C12.5675 5.98256 13.0988 6.51391 13.0988 7.1686C13.0988 7.8233 12.5675 8.35465 11.9128 8.35465C11.2581 8.35465 10.7267 7.8233 10.7267 7.1686C10.7267 6.51391 11.2581 5.98256 11.9128 5.98256ZM5.58721 5.98256C6.24191 5.98256 6.77326 6.51391 6.77326 7.1686C6.77326 7.8233 6.24191 8.35465 5.58721 8.35465C4.93251 8.35465 4.40116 7.8233 4.40116 7.1686C4.40116 6.51391 4.93251 5.98256 5.58721 5.98256Z"
        fill={color}
      />
    </svg>
  );
}

export function IconMuscle({ size = 18, height = 19, color = colors.brand[200] }: Props): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={height} viewBox="0 0 17 18" fill="none">
      <path
        // eslint-disable-next-line max-len
        d="M15.2415 11.3799V11.3787C15.219 11.3657 15.1934 11.3569 15.1703 11.3446C15.0899 11.3022 15.0084 11.2622 14.9242 11.2269C14.8775 11.207 14.8292 11.19 14.782 11.1725C14.7148 11.148 14.6468 11.125 14.5783 11.1052C14.5241 11.0885 14.4697 11.0743 14.4147 11.0611C14.3479 11.0452 14.2804 11.0325 14.212 11.0215C14.1558 11.0116 14.0992 11.0009 14.0416 10.9937C13.9619 10.9846 13.8805 10.9814 13.7993 10.9783C13.7567 10.9767 13.7151 10.97 13.6725 10.97C12.5977 10.97 11.64 11.5006 11.0316 12.323C11.0024 12.3164 10.975 12.3139 10.9458 12.3077C10.296 11.8793 9.45555 11.6192 8.53532 11.6192C7.9832 11.6192 7.43125 11.7111 6.91544 11.9101C6.53821 12.0557 6.17156 12.2403 5.85749 12.4978C5.76858 12.5703 5.68503 12.6508 5.60768 12.7356C5.50259 12.8505 5.42504 12.99 5.28627 13.0704C5.25866 13.0867 5.22517 13.0982 5.19069 13.1062H5.07219C5.02813 13.097 4.98718 13.0772 4.95917 13.0355C4.95752 13.0331 4.95514 13.0327 4.95369 13.0309C4.87027 12.8974 4.88533 12.6968 4.87905 12.5453C4.87033 12.359 4.86386 12.1728 4.84556 11.9869C4.84391 11.971 4.84437 11.9549 4.84272 11.9382C4.81227 11.6659 4.78836 11.3925 4.76703 11.1186C4.73763 10.7627 4.71511 10.4025 4.69741 10.0517L4.69476 9.99144C4.65744 9.27371 4.63915 8.50572 4.75177 7.7533C4.85686 7.05544 5.06564 6.35278 5.35648 5.72128C5.40761 5.60985 5.46626 5.49769 5.52287 5.3895C5.56105 5.3183 5.59976 5.24359 5.63688 5.16915C5.64804 5.14722 5.6594 5.12331 5.67136 5.09815C5.731 4.97212 5.80485 4.8163 5.90347 4.75805C5.94225 4.73592 5.99172 4.72535 6.06015 4.72535C6.10051 4.72535 6.14457 4.72918 6.18658 4.73222C6.21663 4.73506 6.24867 4.7377 6.27892 4.73849C6.34021 4.74133 6.4025 4.74563 6.4644 4.74887C6.57781 4.756 6.69552 4.76287 6.81236 4.76287C6.91811 4.76287 7.01018 4.75758 7.09566 4.74503C7.17341 4.73407 7.24382 4.69814 7.3063 4.6665C7.32638 4.65613 7.34547 4.64642 7.36331 4.63849C7.6192 4.52164 7.88096 4.40658 8.13441 4.29641C8.23204 4.25381 8.33065 4.21081 8.42928 4.16754C8.61556 4.17706 8.80083 4.17871 8.97877 4.10889C9.22733 4.01146 9.30969 3.68438 9.29371 3.44678C9.28988 3.39341 9.27911 3.34797 9.26187 3.3084C9.21154 3.1905 9.10566 3.13006 8.97903 3.10528C9.0324 2.94193 9.09654 2.79662 9.17931 2.62864L9.18644 2.61424C9.28506 2.41437 9.3871 2.15484 9.30025 1.92147L9.29946 1.92108V1.92009C9.16755 1.56949 7.69417 0.548372 7.38818 0.339388C7.34174 0.301671 6.96148 -0.00778076 6.62322 0.000149792C6.3929 0.00543401 5.63686 0.632412 5.20646 1.00581C4.66793 1.47332 4.02796 2.08129 3.89466 2.31173C3.79221 2.48723 3.67754 2.71042 3.66843 2.83097L3.54768 3.11163C3.32204 3.63517 3.02092 4.26584 2.70273 4.93312C2.64936 5.04455 2.59764 5.15631 2.54487 5.26754C2.41864 5.53314 2.29288 5.79794 2.1689 6.0642C2.13561 6.1352 2.10278 6.20383 2.06929 6.27584C1.92074 6.59559 1.77649 6.91767 1.63262 7.24027C1.10948 8.42023 0.615214 9.66069 0.298026 10.8349C0.249345 11.0141 0.210176 11.1883 0.175696 11.3596C0.145047 11.5218 0.112153 11.6847 0.0906865 11.847C0.0854023 11.8831 0.0809767 11.9188 0.0760889 11.9535C0.0383723 12.2622 0.0109603 12.5714 0.00243961 12.8801C0.00243961 12.8943 0.00243961 12.9087 0.00184514 12.9229C-0.00528864 13.2486 0.00772394 13.5752 0.0503281 13.9017C0.097027 14.2597 0.168826 14.6178 0.201061 14.9774V14.9786L0.207534 15.0214C0.201457 15.0522 0.19472 15.092 0.187058 15.1358C0.173055 15.2365 0.15641 15.3376 0.145644 15.4339C0.143002 15.4572 0.141813 15.4818 0.139567 15.5057C0.132037 15.6346 0.132697 15.7656 0.166583 15.8909C0.216716 16.0751 0.357339 16.2488 0.519839 16.3711C0.592893 16.4156 0.696391 16.4738 0.834187 16.5404C0.980097 16.6077 1.12838 16.6727 1.27858 16.7338C1.34008 16.758 1.39669 16.7817 1.46505 16.8066C1.47502 16.8107 1.48612 16.8138 1.49609 16.8186C1.68362 16.8888 1.87694 16.9446 2.0681 17.0056C2.172 17.0377 2.2722 17.0687 2.38786 17.1004C3.75106 17.475 5.33197 17.6806 6.85095 17.7932C7.24944 17.8218 7.6492 17.846 8.04816 17.8642C8.2162 17.8717 8.38317 17.8801 8.54692 17.8853C9.0023 17.8999 9.45758 17.909 9.91316 17.911C10.0023 17.9118 10.0974 17.9135 10.1831 17.9139C10.4194 17.9139 10.6553 17.9088 10.8912 17.9063C11.5523 17.8984 12.2294 17.8834 12.9291 17.8536L13.0543 17.8485C13.0543 17.8485 13.3997 17.8585 13.6229 17.86C13.9455 17.8367 14.2657 17.7993 14.5729 17.7162C14.6058 17.7079 14.7357 17.6697 14.8718 17.6247C15.0467 17.5673 15.2312 17.4962 15.2389 17.453C16.2855 16.8725 17 15.7294 17 14.4156C17.0006 13.1033 16.2872 11.9612 15.2416 11.3798L15.2415 11.3799Z"
        fill={color}
      />
    </svg>
  );
}

export function IconFamily({ size = 18, height = 17, color = colors.brand[200] }: Props): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={height} viewBox="0 0 17 16" fill="none">
      <path
        // eslint-disable-next-line max-len
        d="M9.5087 3.09796C10.3592 3.09796 11.0486 2.40849 11.0486 1.55801C11.0486 0.707519 10.3592 0.0180664 9.5087 0.0180664C8.65821 0.0180664 7.96875 0.707519 7.96875 1.55801C7.96875 2.40849 8.65821 3.09796 9.5087 3.09796Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M1.46888 2.04131C1.64794 1.88016 1.79119 1.50412 1.98816 1.28925C1.97026 1.37878 1.97025 1.45041 1.97025 1.53994C1.97025 2.38154 2.6507 3.07988 3.5102 3.07988C4.36971 3.07988 5.05014 2.39944 5.05014 1.53994C5.05014 0.698343 4.36971 0 3.5102 0C2.90139 0 2.36419 0.35812 2.13141 0.877404C1.79119 0.823685 1.32563 0.805783 1.05703 0.949033C0.770531 1.11019 0.537749 1.66529 0.0721842 1.88016C-0.0352537 1.93388 -0.0173518 2.07713 0.0900862 2.11294C0.770527 2.30991 1.20028 2.292 1.46888 2.04131Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M16.9939 10.7079C16.9939 10.69 16.7074 9.31126 16.6358 8.8636C16.5462 8.25478 16.0628 7.62806 15.3465 7.62806H14.5765C13.7528 7.62806 13.4484 8.77407 13.0008 8.77407C12.8217 8.77407 12.6785 8.66662 12.571 8.54128C12.5889 8.46965 12.6068 8.38012 12.5889 8.30849C12.5889 8.29059 12.2308 5.98067 12.1234 5.26442C11.9622 4.22585 11.1385 3.4917 10.0821 3.4917H8.91814C7.91539 3.4917 7.1275 4.17213 6.89472 5.2286C6.82309 5.55092 6.64403 6.17764 6.48288 6.78646C6.30381 6.26717 6.14266 5.71207 6.05313 5.40767C5.83825 4.54816 5.19362 3.54542 4.10134 3.54542H2.93742C1.84514 3.54542 1.11098 4.53026 0.967728 5.44348C0.86029 6.14183 0.412637 8.25477 0.412637 8.27268C0.358918 8.54128 0.52007 8.79197 0.788665 8.84568C0.824478 8.84568 0.860286 8.8636 0.896099 8.8636C1.12888 8.8636 1.32585 8.70244 1.37957 8.46966C1.39748 8.38012 1.82724 6.30298 1.93467 5.58673C1.93467 5.55092 2.09582 7.91455 2.09582 7.91455L1.18261 9.99169C1.02145 10.3319 1.29005 10.7258 1.66608 10.7258H2.00629L1.66608 14.522C1.63027 14.8801 1.89886 15.2024 2.25699 15.2203C2.61511 15.2562 2.93742 14.9876 2.95533 14.6294L3.31345 10.7079H3.76111L4.11924 14.6294C4.15505 14.9696 4.44156 15.2203 4.76387 15.2203C4.78178 15.2203 4.79968 15.2203 4.81759 15.2203C5.17572 15.1845 5.44431 14.8801 5.4085 14.522L5.06828 10.7258H5.4085C5.78453 10.7258 6.03522 10.3319 5.89197 9.99169L4.97875 7.91455C4.97875 7.91455 5.1399 5.60465 5.1399 5.64046C5.33687 6.41043 6.05313 8.45174 6.07103 8.54128C6.16056 8.75615 6.35754 8.8815 6.46497 8.91731C6.51869 8.93522 6.5724 8.93522 6.62612 8.93522C6.85891 8.93522 7.09169 8.77406 7.16331 8.54128C7.19913 8.45174 7.84376 6.19555 8.00491 5.4614C8.00491 5.44349 8.00492 5.42557 8.02283 5.40767L8.09444 9.07846L7.61097 14.4146C7.57516 14.8085 7.86167 15.1666 8.25561 15.2024C8.64955 15.2383 9.00767 14.9517 9.04348 14.5578L9.52695 9.32916H9.58067L10.0641 14.5578C10.1 14.9338 10.4044 15.2024 10.7804 15.2024C10.7983 15.2024 10.8162 15.2024 10.852 15.2024C11.246 15.1666 11.5325 14.8085 11.4966 14.4146L11.0132 9.07846L11.0848 5.38977V5.40767C11.1922 6.12392 11.5504 8.43384 11.5504 8.45174C11.5862 8.72034 11.8369 8.91731 12.1055 8.91731C12.1413 8.91731 12.1592 8.91731 12.195 8.91731C12.4278 9.168 12.7322 9.41869 13.0724 9.41869C13.5917 9.41869 14.0035 9.02475 14.0035 9.02475C14.0214 9.00684 14.0931 10.2961 14.0931 10.2961C14.0931 10.4035 14.0752 10.511 14.0393 10.6184L13.5022 11.8181C13.3947 12.0509 13.5738 12.3016 13.8245 12.3016H14.0573L13.8424 14.7906C13.8245 15.0234 13.9856 15.2382 14.2184 15.2561C14.4512 15.2741 14.6661 15.1129 14.684 14.8801L14.9168 12.3195H15.2033L15.436 14.8801C15.4539 15.095 15.633 15.2561 15.8658 15.2561C15.8837 15.2561 15.9016 15.2561 15.9016 15.2561C16.1344 15.2382 16.2955 15.0234 16.2776 14.7906L16.0628 12.3195H16.2955C16.5462 12.3195 16.7074 12.0688 16.6179 11.836L16.0807 10.6363C16.0449 10.5289 16.009 10.4214 16.0269 10.314C16.0269 10.314 16.1165 8.93522 16.1165 8.97103C16.1881 9.4366 16.4746 10.7975 16.4746 10.8512C16.5104 10.9944 16.6358 11.1019 16.779 11.1019C16.7969 11.1019 16.8148 11.1019 16.8506 11.1019C16.9044 11.0482 17.0297 10.8691 16.9939 10.7079Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M15.0062 7.27021C15.649 7.27021 16.1701 6.7491 16.1701 6.10629C16.1701 5.46348 15.649 4.94238 15.0062 4.94238C14.3634 4.94238 13.8423 5.46348 13.8423 6.10629C13.8423 6.7491 14.3634 7.27021 15.0062 7.27021Z"
        fill={color}
      />
    </svg>
  );
}

export function IconCrutch({ size = 18, height = 24, color = colors.brand[200] }: Props): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={height} viewBox="0 0 17 24" fill="none">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        // eslint-disable-next-line max-len
        d="M9.07223 0.219418C8.59558 -0.141348 7.91675 -0.0474578 7.55602 0.429137C7.19518 0.905732 7.28912 1.58455 7.76566 1.94532L7.87357 2.02697L3.1868 10.1447C2.75235 10.8972 2.55439 11.763 2.61865 12.6295L2.94684 17.0542L1.2195 20.046L0.269467 21.127C-0.181391 21.6401 -0.050193 22.4373 0.541289 22.7789L0.803923 22.9305L1.06655 23.0821C1.65804 23.4236 2.41407 23.1386 2.63294 22.4916L3.09411 21.1283L4.82147 18.1365L8.81734 16.2084C9.59996 15.8308 10.2508 15.2264 10.6853 14.474L15.372 6.35621L15.4967 6.4088C16.0474 6.64116 16.6823 6.38306 16.9146 5.83233C17.1469 5.2816 16.8889 4.64677 16.3381 4.41442L14.1881 3.5073C13.0292 3.01836 11.9357 2.38704 10.9328 1.62788L9.07223 0.219418ZM13.3622 5.50823L13.3466 5.50169C12.0222 4.9429 10.7724 4.22138 9.62637 3.35378L9.61295 3.34361L7.31715 7.31988L11.0664 9.48453L13.3622 5.50823ZM6.23484 9.19449L9.98407 11.3591L8.81063 13.3917C8.59341 13.7679 8.26796 14.0701 7.87671 14.2588L5.01258 15.6408L4.77734 12.4694C4.74521 12.0362 4.84418 11.6033 5.06142 11.227L6.23484 9.19449Z"
        fill={color}
      />
    </svg>
  );
}

export function IconGroupPeople({ size = 18, height = 13, color = colors.brand[200] }: Props): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={height} viewBox="0 0 18 13" fill="none">
      <path
        // eslint-disable-next-line max-len
        d="M3.94012 4.50742C5.10338 4.50742 6.04959 3.27281 6.04959 2.10966C6.04959 0.946497 5.10338 0 3.94012 0C2.77687 0 1.83057 0.946497 1.83057 2.10966C1.83076 3.27272 2.77697 4.50742 3.94012 4.50742Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M5.52372 4.5293C5.11454 4.84106 4.60407 5.02654 4.05112 5.02654H3.82905C3.276 5.02654 2.76543 4.84097 2.35634 4.5293C1.02026 4.74528 0 5.90332 0 7.30024C0 8.06763 1.764 8.6899 3.94008 8.6899C6.11607 8.6899 7.88017 8.06763 7.88017 7.30024C7.88017 5.90332 6.85981 4.74528 5.52372 4.5293Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M12.7242 7.27675C14.2818 7.27675 15.5488 5.62358 15.5488 4.06609C15.5488 2.50859 14.2818 1.24121 12.7242 1.24121C11.1665 1.24121 9.89941 2.50859 9.89941 4.06609C9.89967 5.62346 11.1667 7.27675 12.7242 7.27675Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M14.8446 7.30566C14.2967 7.72313 13.6132 7.97148 12.8728 7.97148H12.5754C11.8349 7.97148 11.1512 7.723 10.6034 7.30566C8.8144 7.59487 7.44824 9.14552 7.44824 11.016C7.44824 12.0436 9.81028 12.8768 12.7241 12.8768C15.6378 12.8768 18 12.0436 18 11.016C18 9.14552 16.6337 7.59487 14.8446 7.30566Z"
        fill={color}
      />
    </svg>
  );
}

export function IconFlagStaff({ size = 16, height = 21, color = colors.brand[200] }: Props): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={height} viewBox="0 0 15 21" fill="none">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        // eslint-disable-next-line max-len
        d="M0.464831 0C0.341378 0.00190756 0.219447 0.0550425 0.133405 0.144517C0.0473623 0.233988 -0.00172901 0.358687 -3.14713e-05 0.483425L-3.14713e-05 20.513C-0.0026741 20.6395 0.0469942 20.7663 0.134609 20.8567C0.222223 20.9472 0.346646 21 0.471889 21C0.597122 21 0.721545 20.9472 0.809159 20.8567C0.896774 20.7663 0.946442 20.6395 0.9438 20.513V9.44634C1.744 8.96628 2.47831 8.9543 3.32611 9.16726C4.22584 9.39323 5.21788 9.90832 6.311 10.405C8.48089 11.391 11.159 12.248 14.338 10.2746C14.4348 10.2344 14.5176 10.1606 14.5693 10.0686C14.573 10.0622 14.5766 10.0558 14.58 10.0491C14.5814 10.0465 14.5828 10.0436 14.5842 10.041C14.6289 9.953 14.6456 9.85076 14.6312 9.75292L14.6312 2.21233C14.644 1.72708 14.2357 1.51476 13.8564 1.77352C10.964 3.57809 8.80753 2.81469 6.69773 1.85721C5.63644 1.37556 4.61461 0.831371 3.55424 0.564546C2.70977 0.352051 1.81825 0.339471 0.943802 0.705003V0.483483C0.945524 0.35637 0.894425 0.229364 0.805414 0.13951C0.716395 0.0496665 0.590633 -0.00188372 0.464831 0Z"
        fill={color}
      />
    </svg>
  );
}

export function IconAirplane({ size = 18, color = colors.brand[200] }: Props): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 17 17" fill="none">
      <path
        // eslint-disable-next-line max-len
        d="M7.98124 1.90156C8.17143 1.79736 8.37523 2.00423 8.56355 2.1922C8.75152 2.38018 8.95788 2.58399 8.85402 2.77434C8.69721 3.04353 8.4791 3.33554 8.21229 3.62329L11.004 4.22484L14.6483 0.580978C15.2739 -0.0447494 16.2465 -0.195435 16.7208 0.278415C17.1943 0.752776 17.0465 1.72806 16.4211 2.35362L12.7773 5.99833L13.379 8.79103C13.6666 8.52422 13.9591 8.30577 14.2285 8.14895C14.4185 8.04492 14.6225 8.25163 14.8106 8.4396C14.9986 8.62774 15.2051 8.83172 15.1011 9.02191C14.9145 9.34252 14.6403 9.69599 14.3007 10.0355C14.1193 10.217 13.9339 10.3796 13.7514 10.5204L14.3972 13.5186C14.5668 14.4478 14.4878 15.0119 13.4514 15.8772C12.5999 16.5878 12.1521 15.4125 11.6258 14.2094L9.47719 9.28292C8.08306 10.6151 6.75992 11.7584 5.66052 12.6021C5.45313 12.7577 5.40682 13.0044 5.45466 13.2014L5.98198 15.6524C6.07086 16.1395 6.02948 16.4356 5.48599 16.8892C5.03956 17.2618 4.80493 16.6457 4.52893 16.0146L3.65155 13.9337C3.3008 14.1064 3.03416 14.1815 2.8702 14.1452C2.86628 14.1442 2.86407 14.1432 2.86117 14.1406C2.85879 14.1382 2.85777 14.136 2.85692 14.1319C2.82065 13.968 2.89574 13.7013 3.06805 13.3507L0.987398 12.4732C0.356563 12.1969 -0.259629 11.9624 0.112913 11.5158C0.566842 10.9728 0.862593 10.9308 1.34955 11.02L3.8007 11.5475C3.99753 11.5956 4.24424 11.5492 4.39987 11.3416C5.24336 10.2422 6.38687 8.91924 7.71903 7.52527L2.79256 5.376C1.58946 4.84988 0.414454 4.40225 1.12514 3.55075C1.99043 2.51435 2.5547 2.435 3.48367 2.60459L6.4829 3.25058C6.62354 3.06873 6.78614 2.88331 6.96748 2.70198C7.30699 2.36247 7.66029 2.08868 7.98124 1.90156Z"
        fill={color}
      />
    </svg>
  );
}

export function IconGovBuilding({ size = 18, height = 20, color = colors.brand[200] }: Props): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={height} viewBox="0 0 17 19" fill="none">
      <path
        // eslint-disable-next-line max-len
        d="M3.01972 9.12737H13.9802C14.1139 9.12737 14.2284 9.0128 14.2284 8.87914V7.92439C14.2284 7.79073 14.1139 7.67616 13.9802 7.67616H12.9873C12.5863 5.72848 10.925 4.21998 8.90095 4.04812V2.40596H10.9823C11.1351 2.40596 11.2305 2.21501 11.1351 2.10044L10.4667 1.20298L11.1351 0.305519C11.2305 0.171854 11.1351 0 10.9823 0H8.38539C8.21354 0 8.09897 0.133664 8.09897 0.286424V4.06722C6.07491 4.23907 4.41365 5.74757 4.01265 7.69525H3.01972C2.88605 7.69525 2.77148 7.80982 2.77148 7.94349V8.89823C2.77148 9.0128 2.88605 9.12737 3.01972 9.12737Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M16.9781 17.7393C16.6344 16.9564 15.145 16.288 13.0637 15.8679V10.0249H3.93631V15.8679C1.85497 16.288 0.365563 16.9564 0.0218543 17.7393C-0.0545254 17.9111 0.0791391 18.1021 0.270088 18.1021H16.7299C16.9209 18.1021 17.0545 17.9111 16.9781 17.7393ZM10.4859 11.3615C10.4859 11.2661 10.5623 11.2088 10.6386 11.2088H11.517C11.6125 11.2088 11.6698 11.2852 11.6698 11.3615V12.2399C11.6698 12.3354 11.5934 12.3927 11.517 12.3927H10.6386C10.5432 12.3927 10.4859 12.3163 10.4859 12.2399V11.3615ZM10.4859 13.5957C10.4859 13.5002 10.5623 13.4429 10.6386 13.4429H11.517C11.6125 13.4429 11.6698 13.5193 11.6698 13.5957V14.474C11.6698 14.5695 11.5934 14.6268 11.517 14.6268H10.6386C10.5432 14.6268 10.4859 14.5504 10.4859 14.474V13.5957ZM7.88896 11.3615C7.88896 11.2661 7.96534 11.2088 8.04172 11.2088H8.92009C9.01556 11.2088 9.07285 11.2852 9.07285 11.3615V12.2399C9.07285 12.3354 8.99647 12.3927 8.92009 12.3927H8.04172C7.94625 12.3927 7.88896 12.3163 7.88896 12.2399V11.3615ZM5.31115 11.3615C5.31115 11.2661 5.38753 11.2088 5.46391 11.2088H6.34227C6.43775 11.2088 6.49503 11.2852 6.49503 11.3615V12.2399C6.49503 12.3354 6.41865 12.3927 6.34227 12.3927H5.46391C5.36843 12.3927 5.31115 12.3163 5.31115 12.2399V11.3615ZM5.31115 13.5957C5.31115 13.5002 5.38753 13.4429 5.46391 13.4429H6.34227C6.43775 13.4429 6.49503 13.5193 6.49503 13.5957V14.474C6.49503 14.5695 6.41865 14.6268 6.34227 14.6268H5.46391C5.36843 14.6268 5.31115 14.5504 5.31115 14.474V13.5957ZM7.88896 15.4861V13.5957C7.88896 13.5002 7.96534 13.4429 8.04172 13.4429H8.92009C9.01556 13.4429 9.07285 13.5193 9.07285 13.5957V15.4861C8.8819 15.4861 8.67185 15.4861 8.46181 15.4861C8.25177 15.4861 8.09901 15.4861 7.88896 15.4861Z"
        fill={color}
      />
    </svg>
  );
}

export function IconNewHire({ size = 20, height = 16, color = colors.steel[200] }: Props): JSX.Element {
  return (
    <svg width={size} height={height} viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        // eslint-disable-next-line max-len
        d="M18 2H10L8 0H2C0.9 0 0.00999999 0.9 0.00999999 2L0 14C0 15.1 0.9 16 2 16H18C19.1 16 20 15.1 20 14V4C20 2.9 19.1 2 18 2ZM13 5C14.1 5 15 5.9 15 7C15 8.1 14.1 9 13 9C11.9 9 11 8.1 11 7C11 5.9 11.9 5 13 5ZM17 13H9V12C9 10.67 11.67 10 13 10C14.33 10 17 10.67 17 12V13Z"
        fill={color}
      />
    </svg>
  );
}

export function IconOpenEnrollment({ size = 20, height = 16, color = colors.steel[200] }: Props): JSX.Element {
  return (
    <svg width={size} height={height} viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8 1.4992H2C0.89543 1.4992 0 2.39463 0 3.4992V9.4992C0 10.6038 0.89543 11.4992 2 11.4992H8C9.10457 11.4992 10 10.6038 10 9.4992V3.4992C10 2.39463 9.10457 1.4992 8 1.4992Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M22.428 5.1172L18.381 1.0712C18.0142 0.705904 17.5177 0.500809 17 0.500809C16.4823 0.500809 15.9858 0.705904 15.619 1.0712L11.572 5.1172C11.2066 5.48432 11.0014 5.98122 11.0014 6.4992C11.0014 7.01718 11.2066 7.51408 11.572 7.8812L15.619 11.9282C15.9858 12.2935 16.4823 12.4986 17 12.4986C17.5177 12.4986 18.0142 12.2935 18.381 11.9282L22.428 7.8822C22.7937 7.51504 22.9991 7.01793 22.9991 6.4997C22.9991 5.98147 22.7937 5.48436 22.428 5.1172Z"
        fill={color}
      />
      <path
        d="M20 13.4992H14C12.8954 13.4992 12 14.3946 12 15.4992V21.4992C12 22.6038 12.8954 23.4992 14 23.4992H20C21.1046 23.4992 22 22.6038 22 21.4992V15.4992C22 14.3946 21.1046 13.4992 20 13.4992Z"
        fill={color}
      />
      <path
        d="M8 13.4992H2C0.89543 13.4992 0 14.3946 0 15.4992V21.4992C0 22.6038 0.89543 23.4992 2 23.4992H8C9.10457 23.4992 10 22.6038 10 21.4992V15.4992C10 14.3946 9.10457 13.4992 8 13.4992Z"
        fill={color}
      />
    </svg>
  );
}

export function IconFileDoc({ size = 24, height = 24 }: Props): JSX.Element {
  return (
    <svg width={size} height={height} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        // eslint-disable-next-line max-len
        d="M22.3758 5.21012L18.6615 1.60574C18.263 1.2191 17.7229 1 17.1607 1H8.125C6.95182 1.0043 6 1.92794 6 3.06639V20.9379C6 22.0764 6.95182 23 8.125 23H20.875C22.0482 23 23 22.0764 23 20.9379V6.67077C23 6.12517 22.7742 5.59676 22.3758 5.21012ZM20.7023 6.50322H17.3333V3.23394L20.7023 6.50322ZM8.125 20.9379V3.06639H15.2083V7.53427C15.2083 8.10564 15.682 8.56532 16.2708 8.56532H20.875V20.9379H8.125Z"
        fill="#546A83"
      />
      <path
        d="M1 12C1 10.8954 1.89543 10 3 10H14C15.1046 10 16 10.8954 16 12V16C16 17.1046 15.1046 18 14 18H3C1.89543 18 1 17.1046 1 16V12Z"
        fill="#546A83"
      />
      <path
        // eslint-disable-next-line max-len
        d="M6.07715 13.8174C6.07715 14.2998 5.9873 14.7031 5.80762 15.0273C5.62793 15.3496 5.36719 15.5928 5.02539 15.7568C4.68359 15.9189 4.27148 16 3.78906 16H2.59961V11.7168H3.91797C4.36133 11.7168 4.74414 11.7969 5.06641 11.957C5.38867 12.1152 5.6377 12.3506 5.81348 12.6631C5.98926 12.9736 6.07715 13.3584 6.07715 13.8174ZM5.34473 13.8379C5.34473 13.4863 5.28906 13.1973 5.17773 12.9707C5.06836 12.7441 4.90625 12.5762 4.69141 12.4668C4.47852 12.3555 4.21582 12.2998 3.90332 12.2998H3.30273V15.4111H3.80078C4.31836 15.4111 4.70508 15.2793 4.96094 15.0156C5.2168 14.752 5.34473 14.3594 5.34473 13.8379ZM10.8027 13.8525C10.8027 14.1826 10.7607 14.4834 10.6768 14.7549C10.5947 15.0244 10.4707 15.2568 10.3047 15.4521C10.1387 15.6455 9.93066 15.7949 9.68066 15.9004C9.43066 16.0059 9.1377 16.0586 8.80176 16.0586C8.45996 16.0586 8.16309 16.0059 7.91113 15.9004C7.66113 15.7949 7.45312 15.6445 7.28711 15.4492C7.12305 15.2539 7 15.0205 6.91797 14.749C6.83594 14.4775 6.79492 14.1768 6.79492 13.8467C6.79492 13.4053 6.86719 13.0205 7.01172 12.6924C7.1582 12.3623 7.37988 12.1064 7.67676 11.9248C7.97559 11.7412 8.35254 11.6494 8.80762 11.6494C9.25293 11.6494 9.62305 11.7402 9.91797 11.9219C10.2129 12.1035 10.4336 12.3594 10.5801 12.6895C10.7285 13.0176 10.8027 13.4053 10.8027 13.8525ZM7.5332 13.8525C7.5332 14.1826 7.57812 14.4688 7.66797 14.7109C7.75781 14.9512 7.89551 15.1367 8.08105 15.2676C8.26855 15.3965 8.50879 15.4609 8.80176 15.4609C9.09668 15.4609 9.33691 15.3965 9.52246 15.2676C9.70801 15.1367 9.84473 14.9512 9.93262 14.7109C10.0205 14.4688 10.0645 14.1826 10.0645 13.8525C10.0645 13.3506 9.96387 12.958 9.7627 12.6748C9.56348 12.3896 9.24512 12.2471 8.80762 12.2471C8.51465 12.2471 8.27344 12.3115 8.08398 12.4404C7.89648 12.5693 7.75781 12.7539 7.66797 12.9941C7.57812 13.2324 7.5332 13.5186 7.5332 13.8525ZM13.5479 12.2529C13.3428 12.2529 13.1592 12.29 12.9971 12.3643C12.8369 12.4385 12.7012 12.5459 12.5898 12.6865C12.4785 12.8252 12.3936 12.9941 12.335 13.1934C12.2764 13.3906 12.2471 13.6133 12.2471 13.8613C12.2471 14.1934 12.2939 14.4795 12.3877 14.7197C12.4814 14.958 12.624 15.1416 12.8154 15.2705C13.0068 15.3975 13.249 15.4609 13.542 15.4609C13.7236 15.4609 13.9004 15.4434 14.0723 15.4082C14.2441 15.373 14.4219 15.3252 14.6055 15.2646V15.8623C14.4316 15.9307 14.2559 15.9805 14.0781 16.0117C13.9004 16.043 13.6953 16.0586 13.4629 16.0586C13.0254 16.0586 12.6621 15.9678 12.373 15.7861C12.0859 15.6045 11.8711 15.3486 11.7285 15.0186C11.5879 14.6885 11.5176 14.3018 11.5176 13.8584C11.5176 13.5342 11.5625 13.2373 11.6523 12.9678C11.7422 12.6982 11.873 12.4658 12.0449 12.2705C12.2168 12.0732 12.4287 11.9219 12.6807 11.8164C12.9346 11.709 13.2246 11.6553 13.5508 11.6553C13.7656 11.6553 13.9766 11.6797 14.1836 11.7285C14.3926 11.7754 14.5859 11.8428 14.7637 11.9307L14.5176 12.5107C14.3691 12.4404 14.2139 12.3799 14.0518 12.3291C13.8896 12.2783 13.7217 12.2529 13.5479 12.2529Z"
        fill="white"
      />
    </svg>
  );
}

export function IconAmbulance({ size = 24, height = 24, color = colors.steel[200] }: Props): JSX.Element {
  return (
    <svg width={size} height={height} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        // eslint-disable-next-line max-len
        d="M0 2.1999C0 1.20615 0.80625 0.399902 1.8 0.399902H13.8C14.7938 0.399902 15.6 1.20615 15.6 2.1999V3.9999H17.5013C18.1388 3.9999 18.75 4.25115 19.2 4.70115L22.0988 7.5999C22.5487 8.0499 22.8 8.66115 22.8 9.29865V13.5999C23.4638 13.5999 24 14.1362 24 14.7999C24 15.4637 23.4638 15.9999 22.8 15.9999H21.6C21.6 17.9874 19.9875 19.5999 18 19.5999C16.0125 19.5999 14.4 17.9874 14.4 15.9999H9.6C9.6 17.9874 7.9875 19.5999 6 19.5999C4.0125 19.5999 2.4 17.9874 2.4 15.9999H1.8C0.80625 15.9999 0 15.1937 0 14.1999V2.1999ZM15.6 9.9999H20.4V9.29865L17.5013 6.3999H15.6V9.9999ZM6 17.7999C6.47739 17.7999 6.93523 17.6103 7.27279 17.2727C7.61036 16.9351 7.8 16.4773 7.8 15.9999C7.8 15.5225 7.61036 15.0647 7.27279 14.7271C6.93523 14.3895 6.47739 14.1999 6 14.1999C5.52261 14.1999 5.06477 14.3895 4.72721 14.7271C4.38964 15.0647 4.2 15.5225 4.2 15.9999C4.2 16.4773 4.38964 16.9351 4.72721 17.2727C5.06477 17.6103 5.52261 17.7999 6 17.7999ZM19.8 15.9999C19.8 15.5225 19.6104 15.0647 19.2728 14.7271C18.9352 14.3895 18.4774 14.1999 18 14.1999C17.5226 14.1999 17.0648 14.3895 16.7272 14.7271C16.3896 15.0647 16.2 15.5225 16.2 15.9999C16.2 16.4773 16.3896 16.9351 16.7272 17.2727C17.0648 17.6103 17.5226 17.7999 18 17.7999C18.4774 17.7999 18.9352 17.6103 19.2728 17.2727C19.6104 16.9351 19.8 16.4773 19.8 15.9999ZM6.6 3.3999V5.1999H4.8C4.47 5.1999 4.2 5.4699 4.2 5.7999V6.9999C4.2 7.3299 4.47 7.5999 4.8 7.5999H6.6V9.3999C6.6 9.7299 6.87 9.9999 7.2 9.9999H8.4C8.73 9.9999 9 9.7299 9 9.3999V7.5999H10.8C11.13 7.5999 11.4 7.3299 11.4 6.9999V5.7999C11.4 5.4699 11.13 5.1999 10.8 5.1999H9V3.3999C9 3.0699 8.73 2.7999 8.4 2.7999H7.2C6.87 2.7999 6.6 3.0699 6.6 3.3999Z"
        fill={color}
      />
    </svg>
  );
}

export function IconTeamSupport({ size = 20, height = 16, color = colors.steel[200] }: Props): JSX.Element {
  return (
    <svg width={size} height={height} viewBox="0 0 24 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M5 6.5C6.65685 6.5 8 5.15685 8 3.5C8 1.84315 6.65685 0.5 5 0.5C3.34315 0.5 2 1.84315 2 3.5C2 5.15685 3.34315 6.5 5 6.5Z"
        fill={color}
      />
      <path
        d="M19 6.5C20.6569 6.5 22 5.15685 22 3.5C22 1.84315 20.6569 0.5 19 0.5C17.3431 0.5 16 1.84315 16 3.5C16 5.15685 17.3431 6.5 19 6.5Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M20 8C17.125 8 15.641 10.065 14.75 11.324C14.2377 12.1172 13.6193 12.8365 12.912 13.462C13.3018 13.7101 13.6055 14.0726 13.7817 14.4998C13.9578 14.927 13.9977 15.3982 13.896 15.849C14.6958 15.2895 15.4053 14.611 16 13.837V21C16 21.2652 16.1053 21.5196 16.2929 21.7071C16.4804 21.8946 16.7348 22 17 22H23C23.2652 22 23.5196 21.8946 23.7071 21.7071C23.8946 21.5196 24 21.2652 24 21V12C24 10.9391 23.5786 9.92172 22.8284 9.17157C22.0783 8.42143 21.0609 8 20 8Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M12.268 14.231C11.0187 13.5513 9.97598 12.547 9.25 11.324C8.359 10.065 6.875 8 4 8C2.93913 8 1.92172 8.42143 1.17157 9.17157C0.421427 9.92172 0 10.9391 0 12L0 21C0 21.2652 0.105357 21.5196 0.292893 21.7071C0.48043 21.8946 0.734784 22 1 22H7C7.26522 22 7.51957 21.8946 7.70711 21.7071C7.89464 21.5196 8 21.2652 8 21V13.837C8.83633 14.9417 9.91322 15.8414 11.149 16.468C11.4445 16.607 11.7826 16.6249 12.0911 16.5179C12.3997 16.411 12.6541 16.1876 12.8002 15.8956C12.9463 15.6036 12.9724 15.266 12.873 14.955C12.7735 14.6439 12.5564 14.3841 12.268 14.231Z"
        fill={color}
      />
    </svg>
  );
}

export function IconProtection({ size = 24, height = 24, color = colors.steel[200] }: Props): JSX.Element {
  return (
    <svg width={size} height={height} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        // eslint-disable-next-line max-len
        d="M8.02332 17.3168C7.93999 17.3168 7.83999 17.3168 7.75666 17.2668C0.358323 14.8168 0.458323 9.2001 0.50999 5.11676V3.98343C0.50999 3.51676 0.876657 3.1501 1.34332 3.1501C1.37666 3.1501 5.20166 3.11676 7.40499 0.900098C7.72166 0.583431 8.27332 0.583431 8.58999 0.900098C10.8117 3.11676 14.6183 3.1501 14.6517 3.1501C15.1183 3.1501 15.485 3.51676 15.485 3.98343V5.11676C15.5533 9.2001 15.6533 14.8001 8.25666 17.2834C8.17332 17.3168 8.08999 17.3334 7.98999 17.3334L8.02332 17.3168Z"
        fill={color}
      />
    </svg>
  );
}

export function IconLinkedin({ color = colors.brand[200] }: Props): JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32">
      <g clipPath="url(#clip0_1324_16867)">
        <path
          // eslint-disable-next-line max-len
          d="M3.86317 0.276367C5.95965 0.276367 7.72508 2.0418 7.72508 4.13828C7.72508 6.34507 5.84928 8.05538 3.86317 8.00019C1.82188 8.05538 0.00125955 6.34507 0.00125955 4.13828C-0.0539258 1.98661 1.71157 0.276367 3.86317 0.276367Z"
          fill={color}
        />
        <path
          // eslint-disable-next-line max-len
          d="M6.01459 31.7234H1.60095C0.994102 31.7234 0.497559 31.2268 0.497559 30.62V11.3104C0.497559 10.7036 0.938916 10.207 1.54577 10.207H6.01453C6.62138 10.207 7.11792 10.7036 7.11792 11.3104V30.6752C7.11799 31.2268 6.62144 31.7234 6.01459 31.7234Z"
          fill={color}
        />
        <path
          // eslint-disable-next-line max-len
          d="M31.9448 16.9377C31.9448 12.8551 28.9104 9.65527 24.8278 9.65527H23.6692C21.4624 9.65527 19.3108 10.7035 18.1522 12.4138C17.766 12.8 17.6005 12.9655 17.6005 12.9655V10.7587C17.6005 10.5381 17.2694 10.207 17.0487 10.207H11.5317C11.311 10.207 10.98 10.4277 10.98 10.7035V31.2821C10.98 31.5027 11.311 31.7234 11.5317 31.7234H17.6004C17.8211 31.7234 18.1521 31.5027 18.1521 31.2821V19.3652C18.1521 17.3239 19.6969 15.6136 21.7382 15.5585C22.7864 15.5585 23.7243 15.9447 24.4415 16.6619C25.1036 17.3239 25.3794 18.2618 25.3794 19.31V31.1716C25.3794 31.3923 25.7105 31.7234 25.9311 31.7234H31.4482C31.6689 31.7234 31.9999 31.3923 31.9999 31.1716V16.9377H31.9448Z"
          fill={color}
        />
      </g>
      <defs>
        <clipPath id="clip0_1324_16867">
          <rect width="32" height="32" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

export function IconTwitter({ color = colors.brand[200] }: Props): JSX.Element {
  return (
    <svg width="25" height="24" viewBox="0 0 25 24" fill="none">
      <path
        d="M14.9492 10.1623L23.8832 0H21.7657L14.0104 8.82343L7.81322 0H0.666687L10.0366 13.344L0.666687 24H2.78418L10.9753 14.6811L17.5202 24H24.6667L14.9492 10.1623Z"
        fill={color}
      />
    </svg>
  );
}

export function IconUmbrella({ size = 24, height = 24, color = colors.steel[200] }: Props): JSX.Element {
  return (
    <svg width={size} height={height} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        // eslint-disable-next-line max-len
        d="M10 0C12.6522 0 15.1957 1.0534 17.0711 2.92846C18.9464 4.80352 20 7.34666 20 9.99839C20 10.2705 19.9 10.5331 19.7192 10.7365C19.5384 10.9398 19.2892 11.0697 19.0189 11.1016L18.8889 11.1093H11.1111V16.664C11.1114 16.9471 11.2199 17.2195 11.4143 17.4254C11.6087 17.6313 11.8744 17.7552 12.1571 17.7718C12.4398 17.7884 12.7182 17.6964 12.9354 17.5147C13.1525 17.3329 13.2921 17.0751 13.3256 16.794L13.3333 16.664C13.3333 16.3694 13.4504 16.0868 13.6588 15.8784C13.8671 15.6701 14.1498 15.5531 14.4444 15.5531C14.7391 15.5531 15.0217 15.6701 15.2301 15.8784C15.4385 16.0868 15.5556 16.3694 15.5556 16.664C15.5564 17.5315 15.2189 18.3652 14.6147 18.9879C14.0105 19.6105 13.1873 19.9731 12.32 19.9986C11.4527 20.024 10.6097 19.7104 9.97003 19.1242C9.33037 18.538 8.94451 17.7256 8.89445 16.8595L8.88889 16.664V11.1093H1.11111C0.838963 11.1093 0.576292 11.0094 0.37292 10.8286C0.169548 10.6478 0.0396191 10.3986 0.00777774 10.1284L0 9.99839C0 7.34666 1.05357 4.80352 2.92893 2.92846C4.8043 1.0534 7.34784 0 10 0Z"
        fill={color}
      />
    </svg>
  );
}
export function IconFacebook({ color = colors.brand[200] }: Props): JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <g clipPath="url(#clip0_1324_16856)">
        <path
          d="M29.7931 32H2.20688C0.993125 32 0 31.0069 0 29.7931V2.20688C0 0.993125 0.993125 0 2.20688 0H29.7931C31.0069 0 32 0.993125 32 2.20688V29.7931C32 31.0069 31.0069 32 29.7931 32Z"
          fill={color}
        />
        <path
          // eslint-disable-next-line max-len
          d="M27.0347 5.51764V11.0349H22.0692C21.4623 11.0349 20.9657 11.5315 20.9657 12.1383V15.4487H26.483V20.966H20.9657V32.0004H15.4485V20.9659H11.0347V15.4486H15.4485V12.1383C15.4485 8.66239 18.0967 5.79345 21.5174 5.51758H27.0347V5.51764Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_1324_16856">
          <rect width="32" height="32" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

export function IconCurrency({ size = 24, height = 24, color = colors.steel[200] }: Props): JSX.Element {
  return (
    <svg width={size} height={height} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        // eslint-disable-next-line max-len
        d="M0 0H21V14H0V0ZM10.5 3.5C11.4283 3.5 12.3185 3.86875 12.9749 4.52513C13.6313 5.1815 14 6.07174 14 7C14 7.92826 13.6313 8.8185 12.9749 9.47487C12.3185 10.1313 11.4283 10.5 10.5 10.5C9.57174 10.5 8.6815 10.1313 8.02513 9.47487C7.36875 8.8185 7 7.92826 7 7C7 6.07174 7.36875 5.1815 8.02513 4.52513C8.6815 3.86875 9.57174 3.5 10.5 3.5ZM4.66667 2.33333C4.66667 2.95217 4.42083 3.54566 3.98325 3.98325C3.54566 4.42083 2.95217 4.66667 2.33333 4.66667V9.33333C2.95217 9.33333 3.54566 9.57917 3.98325 10.0168C4.42083 10.4543 4.66667 11.0478 4.66667 11.6667H16.3333C16.3333 11.0478 16.5792 10.4543 17.0168 10.0168C17.4543 9.57917 18.0478 9.33333 18.6667 9.33333V4.66667C18.0478 4.66667 17.4543 4.42083 17.0168 3.98325C16.5792 3.54566 16.3333 2.95217 16.3333 2.33333H4.66667Z"
        fill={color}
      />
    </svg>
  );
}

export function IconWellbeing({ size = 24, height = 24, color = colors.steel[200] }: Props): JSX.Element {
  return (
    <svg width={size} height={height} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        // eslint-disable-next-line max-len
        d="M9.99 0C4.47 0 0 4.48 0 10C0 15.52 4.47 20 9.99 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 9.99 0ZM6.5 6C7.33 6 8 6.67 8 7.5C8 8.33 7.33 9 6.5 9C5.67 9 5 8.33 5 7.5C5 6.67 5.67 6 6.5 6ZM10 16C7.72 16 5.78 14.34 5 12H15C14.22 14.34 12.28 16 10 16ZM13.5 9C12.67 9 12 8.33 12 7.5C12 6.67 12.67 6 13.5 6C14.33 6 15 6.67 15 7.5C15 8.33 14.33 9 13.5 9Z"
        fill={color}
      />
    </svg>
  );
}

export function IconMultiUser({ size = 24, height = 24, color = colors.steel[200] }: Props): JSX.Element {
  return (
    <svg width={size} height={height} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        // eslint-disable-next-line max-len
        d="M8.66662 11.6567H8.03829C7.18084 11.6257 6.32616 11.7712 5.52731 12.0843C4.72846 12.3974 4.00247 12.8713 3.3944 13.4767L3.22107 13.6789V19.6589H6.16774V16.2645L6.56496 15.8167L6.74551 15.6072C7.68579 14.6413 8.85641 13.9304 10.1472 13.5417C9.50093 13.0499 8.99125 12.401 8.66662 11.6567Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M22.6345 13.4549C22.0264 12.8496 21.3004 12.3757 20.5016 12.0626C19.7027 11.7495 18.848 11.604 17.9906 11.6349C17.7276 11.6357 17.4648 11.6502 17.2034 11.6783C16.8726 12.3766 16.3769 12.9839 15.7589 13.4477C17.1369 13.8289 18.3852 14.5779 19.37 15.6144L19.5506 15.8166L19.9406 16.2644V19.6661H22.7861V13.6572L22.6345 13.4549Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M8.01666 10.2483H8.24055C8.13652 9.35506 8.29325 8.45083 8.69181 7.64473C9.09038 6.83863 9.71374 6.16512 10.4867 5.7055C10.2065 5.27747 9.81995 4.92955 9.36491 4.69579C8.90986 4.46204 8.40192 4.35047 7.8908 4.372C7.37968 4.39354 6.88292 4.54745 6.44916 4.81866C6.0154 5.08987 5.65951 5.46908 5.41633 5.91916C5.17316 6.36924 5.05105 6.87476 5.06195 7.38622C5.07285 7.89767 5.21639 8.39753 5.47852 8.83684C5.74065 9.27615 6.11237 9.63985 6.55729 9.89233C7.00222 10.1448 7.50509 10.2774 8.01666 10.2772V10.2483Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M17.6439 9.70654C17.6527 9.87254 17.6527 10.0389 17.6439 10.2049C17.7825 10.2269 17.9225 10.2389 18.0628 10.241H18.2C18.7093 10.2138 19.2029 10.0553 19.6328 9.7807C20.0626 9.50615 20.414 9.12497 20.6528 8.67428C20.8916 8.2236 21.0097 7.71875 20.9955 7.20891C20.9812 6.69906 20.8352 6.20159 20.5717 5.76491C20.3081 5.32824 19.936 4.96725 19.4915 4.71709C19.047 4.46693 18.5453 4.33613 18.0353 4.33741C17.5252 4.33869 17.0242 4.47202 16.581 4.72442C16.1378 4.97681 15.7675 5.33966 15.5061 5.77766C16.1598 6.20444 16.6973 6.78682 17.0704 7.47255C17.4435 8.15827 17.6406 8.92589 17.6439 9.70654Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M12.9061 12.9423C14.689 12.9423 16.1344 11.4969 16.1344 9.71393C16.1344 7.93097 14.689 6.4856 12.9061 6.4856C11.1231 6.4856 9.67773 7.93097 9.67773 9.71393C9.67773 11.4969 11.1231 12.9423 12.9061 12.9423Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M13.0795 14.661C12.1363 14.6229 11.1951 14.7761 10.3127 15.1112C9.43019 15.4463 8.62464 15.9565 7.94447 16.611L7.76392 16.8132V21.3849C7.76673 21.5338 7.79886 21.6807 7.85846 21.8172C7.91807 21.9537 8.00398 22.0771 8.11129 22.1804C8.21859 22.2837 8.3452 22.3648 8.48387 22.4192C8.62254 22.4735 8.77056 22.5 8.91947 22.4971H17.2178C17.3667 22.5 17.5147 22.4735 17.6534 22.4192C17.7921 22.3648 17.9187 22.2837 18.026 22.1804C18.1333 22.0771 18.2192 21.9537 18.2788 21.8172C18.3384 21.6807 18.3705 21.5338 18.3734 21.3849V16.8277L18.2 16.611C17.5243 15.9545 16.7215 15.4428 15.841 15.1075C14.9605 14.7722 14.0208 14.6203 13.0795 14.661Z"
        fill={color}
      />
    </svg>
  );
}

export function IconFitness({ size = 24, height = 24, color = colors.steel[200] }: Props): JSX.Element {
  return (
    <svg width={size} height={height} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        // eslint-disable-next-line max-len
        d="M20.6461 7.02905L16.8412 1.32998L18.653 0L22.4569 5.69907L20.6461 7.02905ZM18.5635 9.97425L16.6621 7.12297L7.60624 13.774L9.50761 16.623L7.69689 17.9541L2.62508 10.3546L4.43581 9.02459L6.33718 11.8747L15.393 5.22367L13.4917 2.37355L15.3024 1.04357L20.3742 8.64311L18.5635 9.97425ZM21.1889 3.79861L22.9996 2.46863L21.7317 0.569327L19.921 1.8993L21.1889 3.79861ZM6.1581 17.67L2.35314 11.9709L0.54241 13.3009L4.34626 19L6.1581 17.67ZM3.07831 17.1007L1.26759 18.4307L-0.000366211 16.5314L1.81036 15.2014L3.07831 17.1007Z"
        fill={color}
      />
    </svg>
  );
}

export function IconTravel({ size = 24, height = 24, color = colors.steel[200] }: Props): JSX.Element {
  return (
    <svg width={size} height={height} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        // eslint-disable-next-line max-len
        d="M3.85005 14.15L0.650049 12.4L1.70005 11.35L4.20005 11.7L8.10005 7.80005L0.300049 3.55005L1.70005 2.15005L11.25 4.60005L15.175 0.725049C15.4584 0.441715 15.8127 0.300049 16.238 0.300049C16.6634 0.300049 17.0174 0.441715 17.3 0.725049C17.5834 1.00838 17.725 1.36272 17.725 1.78805C17.725 2.21338 17.5834 2.56738 17.3 2.85005L13.4 6.75005L15.85 16.3L14.45 17.7001L10.2 9.90005L6.30005 13.8L6.65005 16.3L5.60005 17.35L3.85005 14.15Z"
        fill={color}
      />
    </svg>
  );
}

export function IconInstagram({ color = colors.brand[200] }: Props): JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <g clipPath="url(#clip0_1324_16833)">
        <path
          d="M23.1173 32H8.88275C3.97244 32 0 28.0276 0 23.1173V8.88275C0 3.97244 3.97244 0 8.88275 0H23.1173C28.0276 0 32 3.97244 32 8.88275V23.1173C32 28.0276 28.0276 32 23.1173 32Z"
          fill={color}
        />
        <path
          // eslint-disable-next-line max-len
          d="M24.2762 8.82805C24.2762 8.22117 23.7796 7.72461 23.1728 7.72461C22.5659 7.72461 22.0693 8.22117 22.0693 8.82805C22.0693 9.43492 22.5659 9.93148 23.1728 9.93148C23.7796 9.93155 24.2762 9.43498 24.2762 8.82805Z"
          fill="white"
        />
        <path
          // eslint-disable-next-line max-len
          d="M16.0001 20.9661C13.3518 20.9661 11.0346 18.6488 11.0346 16.0006C11.0346 13.3523 13.3518 11.0351 16.0001 11.0351C18.6483 11.0351 20.9656 13.3523 20.9656 16.0006C20.9656 18.6488 18.6483 20.9661 16.0001 20.9661ZM16.0001 8.82812C12.0276 8.82812 8.82764 12.0281 8.82764 16.0006C8.82764 19.973 12.0276 23.173 16.0001 23.173C19.9725 23.173 23.1725 19.973 23.1725 16.0006C23.1725 12.0281 19.9725 8.82812 16.0001 8.82812Z"
          fill="white"
        />
        <path
          // eslint-disable-next-line max-len
          d="M26.483 23.3933C26.483 25.1037 25.1037 26.4829 23.3934 26.4829H8.60711C6.89674 26.4829 5.51748 25.1036 5.51748 23.3933V8.60705C5.51748 6.89667 6.8968 5.51742 8.60711 5.51742H23.3934C25.1037 5.51742 26.483 6.89673 26.483 8.60705V23.3933ZM23.1727 3.31055H8.8278C5.7933 3.31055 3.31055 5.7933 3.31055 8.8278V23.1726C3.31055 26.2071 5.7933 28.6899 8.8278 28.6899H23.1726C26.2071 28.6899 28.6899 26.2071 28.6899 23.1726V8.82773C28.6899 5.7933 26.2071 3.31055 23.1727 3.31055Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_1324_16833">
          <rect width="32" height="32" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

export function IconTag({ className, color = 'currentColor', size = 12 }: Props): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        // eslint-disable-next-line max-len
        d="M12.4563 6.71087L6.76669 1.02124C6.43045 0.684996 5.98371 0.5 5.50846 0.5H1.56605C1.15456 0.5 0.820312 0.834493 0.820312 1.24573V5.18815C0.820312 5.66364 1.00531 6.11038 1.34155 6.44663L7.03118 12.1363C7.26568 12.3708 7.57767 12.5 7.90916 12.5C8.24066 12.5 8.55265 12.3708 8.78715 12.1363L12.4566 8.46683C12.6911 8.23234 12.8203 7.92035 12.8203 7.58885C12.8203 7.25736 12.6911 6.94537 12.4566 6.71087H12.4563ZM3.621 4.49992C2.95977 4.49992 2.42203 3.96218 2.42203 3.30094C2.42203 2.63971 2.95977 2.10197 3.621 2.10197C4.28224 2.10197 4.82023 2.63996 4.82023 3.30094C4.82023 3.96193 4.28224 4.49992 3.621 4.49992Z"
        fill={color}
      />
    </svg>
  );
}

export function IconCarrierContact({ size = 24, height = 24, color = colors.steel[200] }: Props): JSX.Element {
  return (
    <svg width={size} height={height} viewBox={`0 0 ${size} ${size}`} fill={color} xmlns="http://www.w3.org/2000/svg">
      <path // eslint-disable-next-line max-len
        d="M18.833 0H2.83301V2H18.833V0ZM2.83301 24H18.833V22H2.83301V24ZM18.833 4H2.83301C1.73301 4 0.833008 4.9 0.833008 6V18C0.833008 19.1 1.73301 20 2.83301 20H18.833C19.933 20 20.833 19.1 20.833 18V6C20.833 4.9 19.933 4 18.833 4ZM10.833 6.75C12.073 6.75 13.083 7.76 13.083 9C13.083 10.24 12.073 11.25 10.833 11.25C9.59301 11.25 8.58301 10.24 8.58301 9C8.58301 7.76 9.59301 6.75 10.833 6.75ZM15.833 17H5.83301V15.5C5.83301 13.83 9.16301 13 10.833 13C12.503 13 15.833 13.83 15.833 15.5V17Z"
        fill={color}
      />
    </svg>
  );
}

export function IconLeaveOfAbsence({ size = 24, height = 24, color = colors.steel[200] }: Props): JSX.Element {
  return (
    <svg width={size} height={height} viewBox={`0 0 ${size} ${size}`} fill={color} xmlns="http://www.w3.org/2000/svg">
      <path // eslint-disable-next-line max-len
        d="M16.3324 2.66667C16.3324 1.37167 17.3707 0.333336 18.6657 0.333336C19.9607 0.333336 20.999 1.37167 20.999 2.66667C20.999 3.96167 19.9607 5 18.6657 5C17.3707 5 16.3324 3.96167 16.3324 2.66667ZM20.999 23.6667V16.6667H23.9157L20.9524 7.765C20.7963 7.30033 20.4985 6.89625 20.1009 6.60956C19.7033 6.32287 19.2259 6.16798 18.7357 6.16667H18.5957C18.1051 6.16622 17.6268 6.32043 17.2289 6.60737C16.8309 6.8943 16.5336 7.29938 16.379 7.765L15.3757 10.775C16.6357 11.475 17.499 12.7933 17.499 14.3333V23.6667H20.999ZM12.249 11.4167C13.2174 11.4167 13.999 10.635 13.999 9.66667C13.999 8.69834 13.2174 7.91667 12.249 7.91667C11.2807 7.91667 10.499 8.69834 10.499 9.66667C10.499 10.635 11.2807 11.4167 12.249 11.4167ZM4.08236 5C5.37736 5 6.41569 3.96167 6.41569 2.66667C6.41569 1.37167 5.37736 0.333336 4.08236 0.333336C2.78736 0.333336 1.74902 1.37167 1.74902 2.66667C1.74902 3.96167 2.78736 5 4.08236 5ZM6.41569 23.6667V15.5H8.16569V8.5C8.16569 7.21667 7.11569 6.16667 5.83236 6.16667H2.33236C1.04902 6.16667 -0.000976562 7.21667 -0.000976562 8.5V15.5H1.74902V23.6667H6.41569ZM13.999 23.6667V19H15.1657V14.3333C15.1657 13.3767 14.3724 12.5833 13.4157 12.5833H11.0824C10.1257 12.5833 9.33236 13.3767 9.33236 14.3333V19H10.499V23.6667H13.999Z"
        fill={color}
      />
    </svg>
  );
}

export function IconCopyPlus(): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
      <path
        // eslint-disable-next-line max-len
        d="M16.0278 8H14.0278V11H11.0278V13H14.0278V16H16.0278V13H19.0278V11H16.0278V8ZM2.02783 12C2.02783 9.21 3.66783 6.8 6.03783 5.68V3.52C2.54783 4.76 0.027832 8.09 0.027832 12C0.027832 15.91 2.54783 19.24 6.03783 20.48V18.32C4.83908 17.755 3.82563 16.8609 3.11564 15.742C2.40566 14.623 2.02839 13.3252 2.02783 12ZM15.0278 3C10.0678 3 6.02783 7.04 6.02783 12C6.02783 16.96 10.0678 21 15.0278 21C19.9878 21 24.0278 16.96 24.0278 12C24.0278 7.04 19.9878 3 15.0278 3ZM15.0278 19C11.1678 19 8.02783 15.86 8.02783 12C8.02783 8.14 11.1678 5 15.0278 5C18.8878 5 22.0278 8.14 22.0278 12C22.0278 15.86 18.8878 19 15.0278 19Z"
        fill="#546A83"
      />
    </svg>
  );
}

export function IconDragHandle(): JSX.Element {
  return (
    <svg width="17" height="20" viewBox="0 0 17 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="0.5" width="3" height="3" fill="#BEC7D0" stroke="white" />
      <rect x="7" y="0.5" width="3" height="3" fill="#BEC7D0" stroke="white" />
      <rect x="3" y="4.5" width="3" height="3" fill="#BEC7D0" stroke="white" />
      <rect x="7" y="4.5" width="3" height="3" fill="#BEC7D0" stroke="white" />
      <rect x="3" y="8.5" width="3" height="3" fill="#BEC7D0" stroke="white" />
      <rect x="7" y="8.5" width="3" height="3" fill="#BEC7D0" stroke="white" />
      <rect x="3" y="12.5" width="3" height="3" fill="#BEC7D0" stroke="white" />
      <rect x="7" y="12.5" width="3" height="3" fill="#BEC7D0" stroke="white" />
      <rect x="3" y="16.5" width="3" height="3" fill="#BEC7D0" stroke="white" />
      <rect x="7" y="16.5" width="3" height="3" fill="#BEC7D0" stroke="white" />
      <rect x="11" y="0.5" width="3" height="3" fill="#BEC7D0" stroke="white" />
      <rect x="11" y="4.5" width="3" height="3" fill="#BEC7D0" stroke="white" />
      <rect x="11" y="8.5" width="3" height="3" fill="#BEC7D0" stroke="white" />
      <rect x="11" y="12.5" width="3" height="3" fill="#BEC7D0" stroke="white" />
      <rect x="11" y="16.5" width="3" height="3" fill="#BEC7D0" stroke="white" />
    </svg>
  );
}

export const IconDragHandle2 = ({ size = 8, height = 14 }: Props): JSX.Element => (
  <svg width={size} height={height} viewBox="0 0 8 14" xmlns="http://www.w3.org/2000/svg">
    <rect width="3" height="3" transform="translate(0 0.5)" fill="#546A83" />
    <rect width="3" height="3" transform="translate(0 5.5)" fill="#546A83" />
    <rect width="3" height="3" transform="translate(0 10.5)" fill="#546A83" />
    <rect width="3" height="3" transform="translate(5 0.5)" fill="#546A83" />
    <rect width="3" height="3" transform="translate(5 5.5)" fill="#546A83" />
    <rect width="3" height="3" transform="translate(5 10.5)" fill="#546A83" />
  </svg>
);

export function IconFileGeneric({ size = 24, height = 24, color = colors.steel[200] }: Props): JSX.Element {
  return (
    <svg width={size} height={height} viewBox="0 0 20 20" fill={color} xmlns="http://www.w3.org/2000/svg">
      <path
        // eslint-disable-next-line max-len
        d="M15.276 3.82738L11.9993 0.550674C11.6478 0.19918 11.1713 0 10.6753 0H2.70422C1.66927 0.00390549 0.82959 0.843585 0.82959 1.87854V18.1254C0.82959 19.1603 1.66927 20 2.70422 20H13.952C14.987 20 15.8267 19.1603 15.8267 18.1254V5.15524C15.8267 4.65925 15.6275 4.17887 15.276 3.82738ZM13.7997 5.00293H10.8276V2.03085L13.7997 5.00293ZM2.70422 18.1254V1.87854H8.953V5.94025C8.953 6.45968 9.37089 6.87756 9.89032 6.87756H13.952V18.1254H2.70422Z"
        fill={color}
      />
    </svg>
  );
}

export const Chevron = (): JSX.Element => (
  <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      // eslint-disable-next-line max-len
      d="M1.73154 7.6945L6.02453 3.4015L10.3175 7.6945C10.5061 7.87665 10.7587 7.97745 11.0209 7.97517C11.2831 7.97289 11.5339 7.86772 11.7193 7.68231C11.9048 7.49691 12.0099 7.24609 12.0122 6.9839C12.0145 6.7217 11.9137 6.4691 11.7315 6.2805L6.73153 1.2805C6.544 1.09302 6.2897 0.987712 6.02453 0.987712C5.75937 0.987712 5.50506 1.09302 5.31753 1.2805L0.317545 6.2805C0.222045 6.37274 0.145845 6.48309 0.0934449 6.60509C0.0410449 6.72709 0.0134443 6.85831 0.0122443 6.99109C0.0111443 7.12387 0.0364444 7.25555 0.0867444 7.37845C0.136944 7.50135 0.211244 7.613 0.305144 7.70689C0.399044 7.80078 0.510645 7.87504 0.633545 7.92532C0.756445 7.9756 0.888145 8.0009 1.02094 7.99975C1.15374 7.99859 1.28494 7.97101 1.40694 7.9186C1.52894 7.86619 1.63924 7.79001 1.73154 7.6945Z"
      fill="currentColor"
    />
  </svg>
);

export function IconSave({ className, color = colors.steel[400] }: Props): JSX.Element {
  return (
    <svg
      className={className}
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        // eslint-disable-next-line max-len
        d="M4.74219 3H17.7422L21.4492 6.707C21.6367 6.89449 21.7421 7.14881 21.7422 7.414V20C21.7422 20.2652 21.6368 20.5196 21.4493 20.7071C21.2618 20.8946 21.0074 21 20.7422 21H4.74219C4.47697 21 4.22262 20.8946 4.03508 20.7071C3.84754 20.5196 3.74219 20.2652 3.74219 20V4C3.74219 3.73478 3.84754 3.48043 4.03508 3.29289C4.22262 3.10536 4.47697 3 4.74219 3ZM12.7422 18C13.5378 18 14.3009 17.6839 14.8635 17.1213C15.4261 16.5587 15.7422 15.7956 15.7422 15C15.7422 14.2044 15.4261 13.4413 14.8635 12.8787C14.3009 12.3161 13.5378 12 12.7422 12C11.9465 12 11.1835 12.3161 10.6209 12.8787C10.0583 13.4413 9.74219 14.2044 9.74219 15C9.74219 15.7956 10.0583 16.5587 10.6209 17.1213C11.1835 17.6839 11.9465 18 12.7422 18ZM5.74219 5V9H15.7422V5H5.74219Z"
        fill={color}
      />
    </svg>
  );
}

export function IconSaveAs({ className, color = colors.steel[400] }: Props): JSX.Element {
  return (
    <svg
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        // eslint-disable-next-line max-len
        d="M13.7422 23V19.925L19.2672 14.425C19.4172 14.275 19.5839 14.1667 19.7672 14.1C19.9505 14.0333 20.1339 14 20.3172 14C20.5172 14 20.7089 14.0377 20.8922 14.113C21.0755 14.1883 21.2422 14.3007 21.3922 14.45L22.3172 15.375C22.4505 15.525 22.5549 15.6917 22.6302 15.875C22.7055 16.0583 22.7429 16.2417 22.7422 16.425C22.7415 16.6083 22.7082 16.796 22.6422 16.988C22.5762 17.18 22.4679 17.3507 22.3172 17.5L16.8172 23H13.7422ZM6.74219 10H15.7422V6H6.74219V10ZM20.3172 17.4L21.2422 16.425L20.3172 15.5L19.3672 16.45L20.3172 17.4ZM11.7422 21H5.74219C5.19219 21 4.72152 20.8043 4.33019 20.413C3.93885 20.0217 3.74285 19.5507 3.74219 19V5C3.74219 4.45 3.93819 3.97933 4.33019 3.588C4.72219 3.19667 5.19285 3.00067 5.74219 3H17.7422L21.7422 7V12.3C21.4255 12.1667 21.0965 12.0793 20.7552 12.038C20.4139 11.9967 20.0762 12.0007 19.7422 12.05C19.4082 12.0993 19.0832 12.2037 18.7672 12.363C18.4512 12.5223 18.1595 12.7263 17.8922 12.975L15.7422 15.125V15C15.7422 14.1667 15.4505 13.4583 14.8672 12.875C14.2839 12.2917 13.5755 12 12.7422 12C11.9089 12 11.2005 12.2917 10.6172 12.875C10.0339 13.4583 9.74219 14.1667 9.74219 15C9.74219 15.8333 10.0339 16.5417 10.6172 17.125C11.2005 17.7083 11.9089 18 12.7422 18H12.8422L11.7422 19.1V21Z"
        fill={color}
      />
    </svg>
  );
}

export function IconNote({ className }: Props): JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        // eslint-disable-next-line max-len
        d="M13 9C13 9.35362 12.8595 9.69276 12.6095 9.94281C12.3594 10.1929 12.0203 10.3333 11.6667 10.3333H3.66667L1 13V2.33333C1 1.97971 1.14048 1.64057 1.39052 1.39052C1.64057 1.14048 1.97971 1 2.33333 1H11.6667C12.0203 1 12.3594 1.14048 12.6095 1.39052C12.8595 1.64057 13 1.97971 13 2.33333V9Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function IconUpload({ className, color = colors.steel[200] }: Props): JSX.Element {
  return (
    <svg
      width="24"
      height="25"
      viewBox="0 0 24 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <g clipPath="url(#clip0_90_1648)">
        <path
          // eslint-disable-next-line max-len
          d="M11.9996 3.74121C11.6996 3.74121 11.4996 3.84121 11.2996 4.04121L5.59961 9.74121L6.99961 11.1412L10.9996 7.14121L10.9996 15.7412L12.9996 15.7412L12.9996 7.14121L16.9996 11.1412L18.3996 9.74121L12.6996 4.04121C12.4996 3.84121 12.2996 3.74121 11.9996 3.74121Z"
          fill={color}
        />
        <path
          d="M18 17.7412H6V14.7412H4V18.7412C4 19.3412 4.4 19.7412 5 19.7412H19C19.6 19.7412 20 19.3412 20 18.7412V14.7412H18V17.7412Z"
          fill={color}
        />
      </g>
      <defs>
        <clipPath id="clip0_90_1648">
          <rect width="16" height="16" fill="white" transform="translate(4 3.74121)" />
        </clipPath>
      </defs>
    </svg>
  );
}

export function IconContractComparisonProgress() {
  return (
    <svg width="144" height="144" viewBox="0 0 144 144" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M143.502 143.973H0.498535V0.969604H143.502V143.973Z" fill="white" />
      <path
        // eslint-disable-next-line max-len
        d="M119.68 101.396H20.2876C22.9252 101.396 25.3141 100.366 27.0428 98.7001C28.7714 97.0341 29.8412 94.7325 29.8412 92.1906C29.8412 87.1061 25.5636 82.9847 20.2876 82.9847H119.68C117.042 82.9847 114.653 84.0144 112.924 85.681C111.195 87.3471 110.126 89.6479 110.126 92.1906C110.126 97.2744 114.404 101.396 119.68 101.396Z"
        fill="#F2F2F2"
      />
      <path
        // eslint-disable-next-line max-len
        d="M119.979 117.243H19.9353C15.5594 117.243 12.0122 113.696 12.0122 109.32C12.0122 104.944 15.5594 101.397 19.9353 101.397H119.979C124.355 101.397 127.902 104.944 127.902 109.32C127.902 113.696 124.355 117.243 119.979 117.243Z"
        fill="#F2F2F2"
      />
      <path
        // eslint-disable-next-line max-len
        d="M125.661 50.9521H28.2988C23.6553 50.9521 19.8911 47.1876 19.8911 42.5446C19.8911 37.9008 23.6553 34.1363 28.2988 34.1363H125.661C130.305 34.1363 134.068 37.9008 134.068 42.5446C134.068 47.1876 130.305 50.9521 125.661 50.9521Z"
        fill="#F2F2F2"
      />
      <path
        // eslint-disable-next-line max-len
        d="M122.886 82.9854H19.9226C14.405 82.9854 9.93213 78.5122 9.93213 72.9944C9.93213 67.4768 14.405 63.0039 19.9226 63.0039H122.886C128.403 63.0039 132.876 67.4768 132.876 72.9944C132.876 78.5122 128.403 82.9854 122.886 82.9854Z"
        fill="#F2F2F2"
      />
      <path
        // eslint-disable-next-line max-len
        d="M96.0238 72.3431C96.0238 88.4095 82.9518 101.481 66.8856 101.481C50.8185 101.481 37.7476 88.4095 37.7476 72.3431C37.7476 56.2759 50.8185 43.2047 66.8856 43.2047C82.9518 43.2047 96.0238 56.2759 96.0238 72.3431Z"
        fill="white"
      />
      <mask id="mask0_65_1488" maskUnits="userSpaceOnUse" x="37" y="43" width="60" height="59">
        <path
          // eslint-disable-next-line max-len
          d="M96.0238 72.3431C96.0238 88.4095 82.9518 101.481 66.8856 101.481C50.8185 101.481 37.7476 88.4095 37.7476 72.3431C37.7476 56.2759 50.8185 43.2047 66.8856 43.2047C82.9518 43.2047 96.0238 56.2759 96.0238 72.3431Z"
          fill="white"
        />
      </mask>
      <g mask="url(#mask0_65_1488)">
        <path
          // eslint-disable-next-line max-len
          d="M87.1452 68.2181V103.945C87.1452 104.866 86.3994 105.611 85.4792 105.611H48.291C47.3712 105.611 46.6255 104.866 46.6255 103.945V60.6768C46.6255 59.7565 47.3712 59.0115 48.291 59.0115H77.9379L87.1452 68.2181Z"
          fill="#F2F2F2"
        />
        <path
          // eslint-disable-next-line max-len
          d="M78.63 60.0322C78.5456 60.0322 78.4613 60.0001 78.3969 59.9357L77.8013 59.3408H76.959C76.7774 59.3408 76.6294 59.1928 76.6294 59.0112C76.6294 58.8288 76.7774 58.6816 76.959 58.6816H77.9379C78.0251 58.6816 78.1088 58.7159 78.171 58.7781L78.8631 59.4695C78.9918 59.5982 78.9918 59.807 78.8631 59.9357C78.7988 60.0001 78.7144 60.0322 78.63 60.0322Z"
          fill="#B3B3B3"
        />
        <path
          // eslint-disable-next-line max-len
          d="M51.7579 59.3406H49.8198C49.6377 59.3406 49.4901 59.1926 49.4901 59.011C49.4901 58.8286 49.6377 58.6814 49.8198 58.6814H51.7579C51.94 58.6814 52.0876 58.8286 52.0876 59.011C52.0876 59.1926 51.94 59.3406 51.7579 59.3406ZM55.6343 59.3406H53.6961C53.514 59.3406 53.3664 59.1926 53.3664 59.011C53.3664 58.8286 53.514 58.6814 53.6961 58.6814H55.6343C55.8162 58.6814 55.9639 58.8286 55.9639 59.011C55.9639 59.1926 55.8162 59.3406 55.6343 59.3406ZM59.5104 59.3406H57.5723C57.3903 59.3406 57.2427 59.1926 57.2427 59.011C57.2427 58.8286 57.3903 58.6814 57.5723 58.6814H59.5104C59.6925 58.6814 59.8401 58.8286 59.8401 59.011C59.8401 59.1926 59.6925 59.3406 59.5104 59.3406ZM63.3868 59.3406H61.4486C61.2666 59.3406 61.119 59.1926 61.119 59.011C61.119 58.8286 61.2666 58.6814 61.4486 58.6814H63.3868C63.5688 58.6814 63.7164 58.8286 63.7164 59.011C63.7164 59.1926 63.5688 59.3406 63.3868 59.3406ZM67.263 59.3406H65.3249C65.1428 59.3406 64.9953 59.1926 64.9953 59.011C64.9953 58.8286 65.1428 58.6814 65.3249 58.6814H67.263C67.4451 58.6814 67.5927 58.8286 67.5927 59.011C67.5927 59.1926 67.4451 59.3406 67.263 59.3406ZM71.1393 59.3406H69.2012C69.0192 59.3406 68.8715 59.1926 68.8715 59.011C68.8715 58.8286 69.0192 58.6814 69.2012 58.6814H71.1393C71.3214 58.6814 71.469 58.8286 71.469 59.011C71.469 59.1926 71.3214 59.3406 71.1393 59.3406ZM75.0159 59.3406H73.0775C72.8951 59.3406 72.7478 59.1926 72.7478 59.011C72.7478 58.8286 72.8951 58.6814 73.0775 58.6814H75.0159C75.1975 58.6814 75.3455 58.8286 75.3455 59.011C75.3455 59.1926 75.1975 59.3406 75.0159 59.3406ZM46.6471 60.737C46.6295 60.737 46.6117 60.7356 46.5939 60.7327C46.4141 60.7034 46.2921 60.534 46.3212 60.3545C46.4491 59.5673 47.0318 58.9338 47.806 58.7407C47.9827 58.6964 48.1615 58.8036 48.2056 58.9802C48.2497 59.1568 48.1423 59.3356 47.9657 59.3799C47.4475 59.5093 47.0575 59.9333 46.972 60.4603C46.9458 60.6219 46.8059 60.737 46.6471 60.737ZM81.3702 62.7734C81.2859 62.7734 81.2015 62.7412 81.1371 62.6769L79.7664 61.3062C79.6377 61.1775 79.6377 60.9687 79.7664 60.84C79.8952 60.7113 80.1039 60.7113 80.2326 60.84L81.6033 62.2107C81.732 62.3394 81.732 62.5482 81.6033 62.6769C81.539 62.7412 81.4546 62.7734 81.3702 62.7734ZM46.625 64.6117C46.4431 64.6117 46.2954 64.4644 46.2954 64.2821V62.3444C46.2954 62.1621 46.4431 62.0148 46.625 62.0148C46.8071 62.0148 46.9547 62.1621 46.9547 62.3444V64.2821C46.9547 64.4644 46.8071 64.6117 46.625 64.6117ZM84.1109 65.5141C84.0265 65.5141 83.9429 65.4819 83.8778 65.4175L82.5078 64.0468C82.3791 63.9181 82.3791 63.7094 82.5078 63.5807C82.6365 63.452 82.8453 63.452 82.974 63.5807L84.344 64.9513C84.4734 65.08 84.4734 65.2888 84.344 65.4175C84.2796 65.4819 84.1953 65.5141 84.1109 65.5141ZM86.8523 68.2547C86.7679 68.2547 86.6835 68.2225 86.6192 68.1582L85.2485 66.7882C85.1198 66.6595 85.1198 66.4507 85.2485 66.322C85.3772 66.1933 85.586 66.1933 85.7147 66.322L87.0854 67.692C87.2141 67.8207 87.2141 68.0295 87.0854 68.1582C87.021 68.2225 86.9366 68.2547 86.8523 68.2547ZM46.625 68.4885C46.4431 68.4885 46.2954 68.3405 46.2954 68.1582V66.2205C46.2954 66.0382 46.4431 65.8909 46.625 65.8909C46.8071 65.8909 46.9547 66.0382 46.9547 66.2205V68.1582C46.9547 68.3405 46.8071 68.4885 46.625 68.4885ZM87.1447 72.01C86.9624 72.01 86.8151 71.8627 86.8151 71.6804V69.7427C86.8151 69.5603 86.9624 69.4131 87.1447 69.4131C87.3263 69.4131 87.4743 69.5603 87.4743 69.7427V71.6804C87.4743 71.8627 87.3263 72.01 87.1447 72.01ZM46.625 72.3646C46.4431 72.3646 46.2954 72.2166 46.2954 72.035V70.0966C46.2954 69.915 46.4431 69.767 46.625 69.767C46.8071 69.767 46.9547 69.915 46.9547 70.0966V72.035C46.9547 72.2166 46.8071 72.3646 46.625 72.3646ZM87.1447 75.8868C86.9624 75.8868 86.8151 75.7388 86.8151 75.5572V73.6188C86.8151 73.4364 86.9624 73.2892 87.1447 73.2892C87.3263 73.2892 87.4743 73.4364 87.4743 73.6188V75.5572C87.4743 75.7388 87.3263 75.8868 87.1447 75.8868ZM46.625 76.2407C46.4431 76.2407 46.2954 76.0935 46.2954 75.9111V73.9727C46.2954 73.7911 46.4431 73.6431 46.625 73.6431C46.8071 73.6431 46.9547 73.7911 46.9547 73.9727V75.9111C46.9547 76.0935 46.8071 76.2407 46.625 76.2407ZM87.1447 79.7629C86.9624 79.7629 86.8151 79.6149 86.8151 79.4333V77.4949C86.8151 77.3133 86.9624 77.1653 87.1447 77.1653C87.3263 77.1653 87.4743 77.3133 87.4743 77.4949V79.4333C87.4743 79.6149 87.3263 79.7629 87.1447 79.7629ZM46.625 80.1169C46.4431 80.1169 46.2954 79.9696 46.2954 79.7872V77.8495C46.2954 77.6672 46.4431 77.5199 46.625 77.5199C46.8071 77.5199 46.9547 77.6672 46.9547 77.8495V79.7872C46.9547 79.9696 46.8071 80.1169 46.625 80.1169ZM87.1447 83.639C86.9624 83.639 86.8151 83.4917 86.8151 83.3094V81.371C86.8151 81.1894 86.9624 81.0414 87.1447 81.0414C87.3263 81.0414 87.4743 81.1894 87.4743 81.371V83.3094C87.4743 83.4917 87.3263 83.639 87.1447 83.639ZM46.625 83.9937C46.4431 83.9937 46.2954 83.8457 46.2954 83.6633V81.7257C46.2954 81.5433 46.4431 81.396 46.625 81.396C46.8071 81.396 46.9547 81.5433 46.9547 81.7257V83.6633C46.9547 83.8457 46.8071 83.9937 46.625 83.9937ZM87.1447 87.5151C86.9624 87.5151 86.8151 87.3679 86.8151 87.1855V85.2478C86.8151 85.0655 86.9624 84.9182 87.1447 84.9182C87.3263 84.9182 87.4743 85.0655 87.4743 85.2478V87.1855C87.4743 87.3679 87.3263 87.5151 87.1447 87.5151ZM46.625 87.8698C46.4431 87.8698 46.2954 87.7218 46.2954 87.5402V85.6018C46.2954 85.4201 46.4431 85.2721 46.625 85.2721C46.8071 85.2721 46.9547 85.4201 46.9547 85.6018V87.5402C46.9547 87.7218 46.8071 87.8698 46.625 87.8698ZM87.1447 91.392C86.9624 91.392 86.8151 91.244 86.8151 91.0616V89.1239C86.8151 88.9416 86.9624 88.7943 87.1447 88.7943C87.3263 88.7943 87.4743 88.9416 87.4743 89.1239V91.0616C87.4743 91.244 87.3263 91.392 87.1447 91.392ZM46.625 91.7459C46.4431 91.7459 46.2954 91.5986 46.2954 91.4163V89.4779C46.2954 89.2963 46.4431 89.1482 46.625 89.1482C46.8071 89.1482 46.9547 89.2963 46.9547 89.4779V91.4163C46.9547 91.5986 46.8071 91.7459 46.625 91.7459ZM87.1447 95.2681C86.9624 95.2681 86.8151 95.1201 86.8151 94.9385V93C86.8151 92.8184 86.9624 92.6704 87.1447 92.6704C87.3263 92.6704 87.4743 92.8184 87.4743 93V94.9385C87.4743 95.1201 87.3263 95.2681 87.1447 95.2681ZM46.625 95.622C46.4431 95.622 46.2954 95.4747 46.2954 95.2924V93.3547C46.2954 93.1724 46.4431 93.0251 46.625 93.0251C46.8071 93.0251 46.9547 93.1724 46.9547 93.3547V95.2924C46.9547 95.4747 46.8071 95.622 46.625 95.622ZM87.1447 99.1442C86.9624 99.1442 86.8151 98.9969 86.8151 98.8146V96.8762C86.8151 96.6945 86.9624 96.5465 87.1447 96.5465C87.3263 96.5465 87.4743 96.6945 87.4743 96.8762V98.8146C87.4743 98.9969 87.3263 99.1442 87.1447 99.1442ZM46.625 99.4981C46.4431 99.4981 46.2954 99.3508 46.2954 99.1685V97.2308C46.2954 97.0485 46.4431 96.9012 46.625 96.9012C46.8071 96.9012 46.9547 97.0485 46.9547 97.2308V99.1685C46.9547 99.3508 46.8071 99.4981 46.625 99.4981ZM87.1447 103.02C86.9624 103.02 86.8151 102.873 86.8151 102.691V100.753C86.8151 100.571 86.9624 100.423 87.1447 100.423C87.3263 100.423 87.4743 100.571 87.4743 100.753V102.691C87.4743 102.873 87.3263 103.02 87.1447 103.02ZM46.625 103.375C46.4431 103.375 46.2954 103.227 46.2954 103.045V101.107C46.2954 100.925 46.4431 100.777 46.625 100.777C46.8071 100.777 46.9547 100.925 46.9547 101.107V103.045C46.9547 103.227 46.8071 103.375 46.625 103.375ZM48.6495 105.94H48.2905C47.6503 105.94 47.0447 105.629 46.6703 105.11C46.564 104.962 46.5976 104.756 46.7453 104.649C46.8931 104.543 47.099 104.577 47.2054 104.724C47.4562 105.073 47.8619 105.281 48.2905 105.281H48.6495C48.8317 105.281 48.9792 105.428 48.9792 105.611C48.9792 105.792 48.8317 105.94 48.6495 105.94ZM52.5259 105.94H50.5877C50.4057 105.94 50.2581 105.792 50.2581 105.611C50.2581 105.428 50.4057 105.281 50.5877 105.281H52.5259C52.7079 105.281 52.8555 105.428 52.8555 105.611C52.8555 105.792 52.7079 105.94 52.5259 105.94ZM56.4021 105.94H54.464C54.2819 105.94 54.1343 105.792 54.1343 105.611C54.1343 105.428 54.2819 105.281 54.464 105.281H56.4021C56.5842 105.281 56.7318 105.428 56.7318 105.611C56.7318 105.792 56.5842 105.94 56.4021 105.94ZM60.2784 105.94H58.3402C58.1582 105.94 58.0106 105.792 58.0106 105.611C58.0106 105.428 58.1582 105.281 58.3402 105.281H60.2784C60.4605 105.281 60.6081 105.428 60.6081 105.611C60.6081 105.792 60.4605 105.94 60.2784 105.94ZM64.1547 105.94H62.2166C62.0345 105.94 61.8869 105.792 61.8869 105.611C61.8869 105.428 62.0345 105.281 62.2166 105.281H64.1547C64.3367 105.281 64.4843 105.428 64.4843 105.611C64.4843 105.792 64.3367 105.94 64.1547 105.94ZM68.0309 105.94H66.0928C65.9108 105.94 65.7632 105.792 65.7632 105.611C65.7632 105.428 65.9108 105.281 66.0928 105.281H68.0309C68.213 105.281 68.3606 105.428 68.3606 105.611C68.3606 105.792 68.213 105.94 68.0309 105.94ZM71.9072 105.94H69.9691C69.7871 105.94 69.6394 105.792 69.6394 105.611C69.6394 105.428 69.7871 105.281 69.9691 105.281H71.9072C72.0893 105.281 72.2366 105.428 72.2366 105.611C72.2366 105.792 72.0893 105.94 71.9072 105.94ZM75.7838 105.94H73.8454C73.6631 105.94 73.5158 105.792 73.5158 105.611C73.5158 105.428 73.6631 105.281 73.8454 105.281H75.7838C75.9654 105.281 76.1134 105.428 76.1134 105.611C76.1134 105.792 75.9654 105.94 75.7838 105.94ZM79.6599 105.94H77.7215C77.5399 105.94 77.3919 105.792 77.3919 105.611C77.3919 105.428 77.5399 105.281 77.7215 105.281H79.6599C79.8415 105.281 79.9895 105.428 79.9895 105.611C79.9895 105.792 79.8415 105.94 79.6599 105.94ZM83.536 105.94H81.5976C81.416 105.94 81.268 105.792 81.268 105.611C81.268 105.428 81.416 105.281 81.5976 105.281H83.536C83.7184 105.281 83.8656 105.428 83.8656 105.611C83.8656 105.792 83.7184 105.94 83.536 105.94ZM85.4787 105.94L85.4744 105.611V105.281C86.0107 105.281 86.4919 104.966 86.7043 104.478C86.7772 104.311 86.9717 104.235 87.1383 104.307C87.3049 104.38 87.3814 104.575 87.3092 104.741C86.9917 105.469 86.2731 105.94 85.4787 105.94Z"
          fill="#B3B3B3"
        />
      </g>
      <path
        // eslint-disable-next-line max-len
        d="M93.8112 95.9108C93.7254 95.9695 93.6431 96.0381 93.5659 96.1146L90.6572 99.0233C90.5807 99.0998 90.5121 99.1828 90.4534 99.2686C89.742 98.1288 89.1671 96.9011 88.8461 95.5762C88.6702 94.8304 89.3759 94.1276 90.1188 94.3035C91.443 94.6238 92.6714 95.1987 93.8112 95.9108Z"
        fill="#E6E6E6"
      />
      <path
        // eslint-disable-next-line max-len
        d="M89.8876 94.6291C89.7067 94.6291 89.5273 94.7049 89.3871 94.8443C89.2098 95.0216 89.1354 95.2648 89.1898 95.495C89.4465 96.5525 89.879 97.5971 90.5068 98.6732L93.216 95.964C92.1406 95.3355 91.096 94.903 90.0356 94.6463C89.9863 94.6348 89.937 94.6291 89.8876 94.6291ZM90.4532 99.6221C90.4511 99.6221 90.4482 99.6214 90.4461 99.6214C90.3266 99.6192 90.2165 99.557 90.1536 99.4555C89.3499 98.1684 88.8101 96.9272 88.5019 95.6594C88.3911 95.1868 88.5355 94.6963 88.8873 94.3438C89.2398 93.992 89.731 93.8476 90.2001 93.9591C91.4707 94.2666 92.7127 94.8064 93.9983 95.6108C94.0998 95.6737 94.162 95.7839 94.1649 95.9033C94.167 96.0227 94.1091 96.1349 94.0104 96.2029C93.9418 96.2493 93.876 96.3037 93.8159 96.3645L90.9072 99.2731C90.8465 99.3339 90.7921 99.399 90.7457 99.4676C90.6792 99.5642 90.5698 99.6221 90.4532 99.6221Z"
        fill="#B3B3B3"
      />
      <path
        // eslint-disable-next-line max-len
        d="M108.368 109.506C108.069 109.107 107.758 108.722 107.439 108.344C106.485 107.212 105.477 106.128 104.439 105.072C102.013 102.613 99.4218 100.315 96.7377 98.1367C96.6504 98.0673 96.5618 97.9944 96.4731 97.9179C96.5353 97.7363 96.5653 97.5461 96.5653 97.3566C96.5653 96.9061 96.3937 96.4564 96.0505 96.1139C95.4413 95.504 94.4954 95.4361 93.8111 95.9101C93.7253 95.9688 93.6431 96.0374 93.5658 96.1139L90.6572 99.0226C90.5807 99.0991 90.512 99.182 90.4534 99.2679C90.246 99.5674 90.1431 99.9164 90.1431 100.265C90.1431 100.715 90.314 101.164 90.6572 101.507C91.1462 101.996 91.8505 102.136 92.4633 101.929C92.5384 102.018 92.6113 102.106 92.6814 102.194C95.1704 105.26 97.8109 108.208 100.68 110.921C101.4 111.597 102.131 112.26 102.888 112.895C103.266 113.214 103.65 113.526 104.049 113.824C105.117 114.621 106.624 114.663 107.744 113.824C109.109 112.803 109.388 110.87 108.368 109.506Z"
        fill="#E6E6E6"
      />
      <path
        // eslint-disable-next-line max-len
        d="M92.463 101.576C92.5645 101.576 92.6639 101.62 92.7318 101.7C92.8126 101.795 92.8892 101.888 92.9585 101.976C95.6834 105.332 98.2897 108.175 100.922 110.665C101.741 111.434 102.438 112.056 103.115 112.625C103.534 112.979 103.908 113.278 104.261 113.541C105.225 114.261 106.57 114.261 107.532 113.542C108.738 112.639 108.986 110.925 108.085 109.718C107.819 109.364 107.52 108.989 107.168 108.573C106.3 107.543 105.325 106.479 104.186 105.32C101.91 103.013 99.4001 100.753 96.515 98.4118C96.4228 98.3388 96.3305 98.2623 96.2426 98.1873C96.1332 98.0929 96.0917 97.9413 96.1382 97.8047C96.1868 97.6617 96.2118 97.5109 96.2118 97.3564C96.2118 96.9817 96.066 96.6292 95.8007 96.3647C95.3209 95.8842 94.5687 95.8155 94.0124 96.2009C93.9417 96.2496 93.8759 96.3039 93.8158 96.3647L90.9071 99.2734C90.8463 99.3342 90.792 99.3992 90.7455 99.4679C90.5818 99.7031 90.496 99.9784 90.496 100.265C90.496 100.64 90.6419 100.993 90.9071 101.258C91.2875 101.637 91.8402 101.767 92.35 101.594C92.3872 101.582 92.4251 101.576 92.463 101.576ZM105.897 114.787C105.171 114.787 104.445 114.56 103.838 114.108C103.474 113.836 103.089 113.528 102.659 113.165C101.973 112.589 101.267 111.958 100.438 111.18C97.7813 108.668 95.1543 105.802 92.4065 102.418C92.3836 102.389 92.36 102.359 92.3364 102.33C91.6407 102.479 90.92 102.27 90.4073 101.758C90.0083 101.36 89.7896 100.829 89.7896 100.265C89.7896 99.8339 89.9183 99.4192 90.1628 99.0667C90.2321 98.9645 90.3151 98.8651 90.4073 98.7729L93.316 95.8642C93.4075 95.7726 93.5069 95.6904 93.612 95.6189C94.4479 95.0398 95.579 95.1427 96.3005 95.8649C96.6988 96.2624 96.919 96.7923 96.919 97.3564C96.919 97.5044 96.9033 97.6503 96.8732 97.7926C96.9011 97.8155 96.929 97.8383 96.9576 97.8605C99.8656 100.22 102.395 102.497 104.691 104.824C105.842 105.996 106.829 107.073 107.709 108.117C108.069 108.544 108.377 108.929 108.651 109.294V109.295C109.786 110.813 109.474 112.972 107.955 114.107C107.35 114.56 106.624 114.787 105.897 114.787Z"
        fill="#B3B3B3"
      />
      <path
        // eslint-disable-next-line max-len
        d="M66.886 101.481C50.8189 101.481 37.7483 88.4094 37.7483 72.343C37.7483 56.2758 50.8189 43.2046 66.886 43.2046C82.9525 43.2046 96.0244 56.2758 96.0244 72.343C96.0244 88.4094 82.9525 101.481 66.886 101.481ZM97.5388 59.3919C95.8635 55.43 93.4646 51.8728 90.4101 48.8182C87.357 45.7644 83.799 43.3662 79.8371 41.6902C75.7329 39.9541 71.3756 39.0747 66.886 39.0747C62.3971 39.0747 58.0391 39.9541 53.9356 41.6902C49.9737 43.3662 46.4158 45.7644 43.3619 48.8182C40.3074 51.8728 37.9092 55.43 36.2332 59.3919C34.4979 63.4961 33.6177 67.8527 33.6177 72.343C33.6177 76.8326 34.4979 81.1892 36.2332 85.2934C37.9092 89.2553 40.3074 92.8125 43.3619 95.8671C46.4158 98.9216 49.9737 101.319 53.9356 102.995C58.0391 104.731 62.3971 105.611 66.886 105.611C71.3756 105.611 75.7329 104.731 79.8371 102.995C83.799 101.319 87.357 98.9216 90.4101 95.8671C93.4646 92.8125 95.8635 89.2553 97.5388 85.2934C99.2742 81.1892 100.154 76.8326 100.154 72.343C100.154 67.8527 99.2742 63.4961 97.5388 59.3919Z"
        fill="#E6E6E6"
      />
      <path
        // eslint-disable-next-line max-len
        d="M66.886 43.5581C51.0143 43.5581 38.1018 56.4713 38.1018 72.3433C38.1018 88.2152 51.0143 101.128 66.886 101.128C82.758 101.128 95.6712 88.2152 95.6712 72.3433C95.6712 56.4713 82.758 43.5581 66.886 43.5581ZM66.886 101.835C50.6245 101.835 37.3948 88.6049 37.3948 72.3433C37.3948 56.0816 50.6245 42.8517 66.886 42.8517C83.1476 42.8517 96.3776 56.0816 96.3776 72.3433C96.3776 88.6049 83.1476 101.835 66.886 101.835ZM66.886 39.4282C62.4426 39.4282 58.1318 40.2991 54.0733 42.0158C50.1527 43.6747 46.633 46.0471 43.6119 49.0688C40.5899 52.0904 38.2169 55.6105 36.5587 59.5302C34.8418 63.5908 33.9712 67.9016 33.9712 72.3433C33.9712 76.7843 34.8418 81.0951 36.5588 85.1557C38.2169 89.0754 40.5899 92.5954 43.6119 95.6171C46.6318 98.6381 50.1516 101.01 54.0733 102.67C58.133 104.388 62.4438 105.258 66.886 105.258C71.328 105.258 75.6392 104.388 79.6991 102.67C83.6217 101.01 87.141 98.6381 90.1598 95.6171C93.1836 92.594 95.5561 89.0747 97.2135 85.1557C98.9302 81.0951 99.8011 76.7843 99.8011 72.3433C99.8011 67.9023 98.9302 63.5908 97.2135 59.5302C95.5561 55.6112 93.1836 52.0919 90.1598 49.0688C87.1396 46.0478 83.6203 43.6747 79.6991 42.0158C75.64 40.2991 71.3292 39.4282 66.886 39.4282ZM66.886 105.965C62.3486 105.965 57.9452 105.075 53.7979 103.321C49.7918 101.626 46.1965 99.2029 43.112 96.1176C40.0253 93.0309 37.6015 89.4351 35.9077 85.4317C34.1536 81.2832 33.2642 76.8794 33.2642 72.3433C33.2642 67.8065 34.1536 63.4027 35.9076 59.2549C37.6015 55.2508 40.0253 51.655 43.112 48.5682C46.1978 45.4829 49.7931 43.059 53.7979 41.3652C57.9439 39.6105 62.3474 38.7217 66.886 38.7217C71.4244 38.7217 75.828 39.6105 79.9751 41.3652C83.9799 43.059 87.575 45.4829 90.6603 48.569C93.7478 51.6564 96.1717 55.2515 97.8641 59.2549C99.6181 63.4027 100.508 67.8065 100.508 72.3433C100.508 76.8794 99.6181 81.2824 97.8641 85.4317C96.1717 89.4344 93.7478 93.0295 90.6603 96.1176C87.5765 99.2022 83.9813 101.626 79.9751 103.321C75.8266 105.075 71.4231 105.965 66.886 105.965Z"
        fill="#B3B3B3"
      />
      <path
        // eslint-disable-next-line max-len
        d="M95.3934 27.6987C87.1142 27.6987 80.4031 34.4099 80.4031 42.6884C80.4031 48.2054 83.3833 53.0247 87.8214 55.6273C86.1239 57.0502 84.3836 57.6787 84.3836 57.6787H95.3934C103.672 57.6787 110.383 50.9676 110.383 42.6884C110.383 34.4099 103.672 27.6987 95.3934 27.6987Z"
        fill="white"
      />
      <path
        // eslint-disable-next-line max-len
        d="M85.9579 57.3253H95.3926C103.463 57.3253 110.029 50.7599 110.029 42.689C110.029 34.6188 103.463 28.0526 95.3926 28.0526C87.3217 28.0526 80.7562 34.6188 80.7562 42.689C80.7562 47.8613 83.5317 52.7024 88 55.3226C88.0987 55.3806 88.1631 55.4836 88.1731 55.5981C88.1824 55.7126 88.1359 55.8249 88.0479 55.8986C87.311 56.5161 86.5711 56.9869 85.9579 57.3253ZM95.3926 58.0322H84.3831C84.2114 58.0322 84.064 57.9092 84.0347 57.7396C84.0053 57.5707 84.1012 57.4047 84.2629 57.3468C84.2787 57.3411 85.7111 56.8144 87.2065 55.6675C85.1294 54.3545 83.3857 52.5522 82.1429 50.4293C80.7727 48.091 80.0493 45.4143 80.0493 42.689C80.0493 38.5906 81.6456 34.7376 84.5434 31.8398C87.4412 28.942 91.2942 27.3457 95.3926 27.3457C99.4917 27.3457 103.344 28.942 106.242 31.8398C109.14 34.7376 110.737 38.5906 110.737 42.689C110.737 46.7874 109.14 50.6404 106.242 53.5382C103.344 56.4367 99.4917 58.0322 95.3926 58.0322Z"
        fill="#B3B3B3"
      />
      <path
        d="M93.5862 46.4402L89.7931 42.7087L88.5015 43.9704L93.5862 48.9727L104.501 38.2344L103.219 36.9727L93.5862 46.4402Z"
        fill="#1C917C"
      />
      <path d="M53.5015 69.9727H80.5015" stroke="#98A6B5" strokeLinecap="round" />
      <path d="M53.5015 78.9727H80.5015" stroke="#98A6B5" strokeLinecap="round" />
      <path d="M53.5015 87.9727H80.5015" stroke="#98A6B5" strokeLinecap="round" />
      <path d="M53.5015 95.9727H80.5015" stroke="#98A6B5" strokeLinecap="round" />
    </svg>
  );
}

export function IconImport({ className, color = colors.steel[400], size = 24 }: Props): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        // eslint-disable-next-line max-len
        d="M13.586 2C14.0556 2.00011 14.5101 2.16543 14.87 2.467L15 2.586L19.414 7C19.746 7.33202 19.9506 7.77028 19.992 8.238L20 8.414V12H18V10H13.5C13.1271 9.99998 12.7676 9.86108 12.4916 9.61038C12.2156 9.35968 12.0428 9.01516 12.007 8.644L12 8.5V4H6V20H12V22H6C5.49542 22.0002 5.00943 21.8096 4.63945 21.4665C4.26947 21.1234 4.04284 20.6532 4.005 20.15L4 20V4C3.99984 3.49542 4.19041 3.00943 4.5335 2.63945C4.87659 2.26947 5.34684 2.04284 5.85 2.005L6 2H13.586ZM14 4.414V8H17.586L14 4.414Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M12.2928 18.5473L15.1208 21.3753L15.1208 21.3763C15.3084 21.5638 15.5628 21.6691 15.8281 21.669C16.0934 21.6689 16.3478 21.5634 16.5353 21.3758C16.7228 21.1882 16.8281 20.9337 16.828 20.6684C16.8279 20.4032 16.7224 20.1488 16.5348 19.9613L15.4138 18.8403L19.8278 18.8403C20.093 18.8403 20.3473 18.7349 20.5349 18.5474C20.7224 18.3599 20.8278 18.1055 20.8278 17.8403C20.8278 17.5751 20.7224 17.3207 20.5349 17.1332C20.3473 16.9457 20.093 16.8403 19.8278 16.8403L15.4138 16.8403L16.5348 15.7193C16.6303 15.6271 16.7065 15.5167 16.7589 15.3947C16.8113 15.2727 16.8389 15.1415 16.84 15.0087C16.8412 14.8759 16.8159 14.7442 16.7656 14.6213C16.7153 14.4985 16.641 14.3868 16.5472 14.2929C16.4533 14.199 16.3416 14.1248 16.2187 14.0745C16.0958 14.0242 15.9641 13.9989 15.8314 14.0001C15.6986 14.0012 15.5674 14.0288 15.4454 14.0812C15.3234 14.1336 15.213 14.2098 15.1208 14.3053L12.2928 17.1333C12.1053 17.3208 12 17.5751 12 17.8403C12 18.1055 12.1053 18.3598 12.2928 18.5473Z"
        fill={color}
      />
    </svg>
  );
}

export function IconStars({ className, color = '#29CBCB', size = 24 }: Props): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 16 16"
      className={className}
      fill="none"
    >
      <path
        // eslint-disable-next-line max-len
        d="M6.50803 0.772705C6.5367 0.693332 6.59193 0.624261 6.66587 0.575305C6.73982 0.52635 6.82871 0.5 6.91992 0.5C7.01113 0.5 7.10003 0.52635 7.17397 0.575305C7.24791 0.624261 7.30314 0.693332 7.33181 0.772705L8.10561 2.90248C8.45152 3.8519 9.26331 4.59604 10.299 4.91312L12.6224 5.62243C12.709 5.64872 12.7844 5.69934 12.8378 5.76712C12.8912 5.8349 12.9199 5.91639 12.9199 6C12.9199 6.08361 12.8912 6.1651 12.8378 6.23288C12.7844 6.30066 12.709 6.35128 12.6224 6.37757L10.299 7.08688C9.78822 7.24289 9.32406 7.50581 8.94334 7.8548C8.56262 8.20379 8.2758 8.62927 8.10561 9.09752L7.33181 11.2273C7.30314 11.3067 7.24791 11.3757 7.17397 11.4247C7.10003 11.4736 7.01113 11.5 6.91992 11.5C6.82871 11.5 6.73982 11.4736 6.66587 11.4247C6.59193 11.3757 6.5367 11.3067 6.50803 11.2273L5.73423 9.09752C5.56404 8.62927 5.27723 8.20379 4.8965 7.8548C4.51578 7.50581 4.05162 7.24289 3.54081 7.08688L1.21742 6.37757C1.13083 6.35128 1.05548 6.30066 1.00207 6.23288C0.948667 6.1651 0.919922 6.08361 0.919922 6C0.919922 5.91639 0.948667 5.8349 1.00207 5.76712C1.05548 5.69934 1.13083 5.64872 1.21742 5.62243L3.54081 4.91312C4.05162 4.75711 4.51578 4.4942 4.8965 4.1452C5.27723 3.79621 5.56404 3.37073 5.73423 2.90248L6.50803 0.772705Z"
        fill={color}
      />
      <path
        // eslint-disable-next-line max-len
        d="M12.7135 9.64636C12.7283 9.60362 12.7561 9.56656 12.7929 9.54033C12.8298 9.5141 12.8739 9.5 12.9192 9.5C12.9644 9.5 13.0085 9.5141 13.0454 9.54033C13.0823 9.56656 13.1101 9.60362 13.1249 9.64636L13.5123 10.8082C13.6849 11.3276 14.0918 11.7344 14.6114 11.9071L15.7735 12.2943C15.8163 12.3092 15.8533 12.3369 15.8796 12.3738C15.9058 12.4106 15.9199 12.4548 15.9199 12.5C15.9199 12.5452 15.9058 12.5894 15.8796 12.6262C15.8533 12.6631 15.8163 12.6908 15.7735 12.7057L14.6114 13.0929C14.3553 13.1781 14.1227 13.3217 13.9319 13.5125C13.7411 13.7032 13.5974 13.9358 13.5123 14.1918L13.1249 15.3536C13.1101 15.3964 13.0823 15.4334 13.0454 15.4597C13.0085 15.4859 12.9644 15.5 12.9192 15.5C12.8739 15.5 12.8298 15.4859 12.7929 15.4597C12.7561 15.4334 12.7283 15.3964 12.7135 15.3536L12.3261 14.1918C12.2409 13.9358 12.0972 13.7032 11.9064 13.5125C11.7156 13.3217 11.483 13.1781 11.227 13.0929L10.0663 12.7057C10.0236 12.6908 9.9865 12.6631 9.96026 12.6262C9.93402 12.5894 9.91992 12.5452 9.91992 12.5C9.91992 12.4548 9.93402 12.4106 9.96026 12.3738C9.9865 12.3369 10.0236 12.3092 10.0663 12.2943L11.2285 11.9071C11.748 11.7344 12.1549 11.3276 12.3276 10.8082L12.7135 9.64636Z"
        fill={color}
      />
    </svg>
  );
}

export function IconAttachToChat({ className, size = 24 }: Props): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        // eslint-disable-next-line max-len
        d="M15.1665 0.266968C15.521 0.174622 15.895 0.176948 16.2485 0.272827C16.6018 0.368882 16.9251 0.556081 17.1841 0.814819C17.443 1.07373 17.6298 1.3971 17.7261 1.75037C17.8221 2.10395 17.8242 2.47781 17.7319 2.8324C17.6395 3.18705 17.4551 3.51194 17.1987 3.7738C16.9423 4.03549 16.6216 4.22582 16.269 4.32556L16.1235 4.36658V13.6322L16.269 13.6732C16.6213 13.7729 16.9424 13.9637 17.1987 14.225C17.455 14.4866 17.6394 14.812 17.7319 15.1664C17.8243 15.5209 17.822 15.8948 17.7261 16.2484C17.6299 16.602 17.4431 16.9249 17.1841 17.184C16.9249 17.4431 16.6021 17.6298 16.2485 17.726C15.8949 17.8219 15.5211 17.8242 15.1665 17.7318C14.8121 17.6393 14.4868 17.4549 14.2251 17.1986C13.9638 16.9423 13.773 16.6212 13.6733 16.2689L13.6323 16.1234H4.3667L4.32568 16.2689C4.22594 16.6214 4.03558 16.9422 3.77393 17.1986C3.51204 17.4551 3.18722 17.6394 2.83252 17.7318C2.47788 17.8241 2.10412 17.822 1.75049 17.726C1.3972 17.6297 1.07387 17.4429 0.814941 17.184C0.556203 16.9249 0.369004 16.6017 0.272949 16.2484C0.177091 15.8949 0.174765 15.5208 0.26709 15.1664C0.359571 14.8119 0.543996 14.4867 0.800293 14.225C1.05662 13.9634 1.37763 13.773 1.72998 13.6732L1.87549 13.6322V4.36658L1.72998 4.32556C1.37774 4.22574 1.05653 4.03531 0.800293 3.7738C0.544201 3.51204 0.359431 3.18678 0.26709 2.8324C0.174924 2.47792 0.176933 2.10383 0.272949 1.75037C0.369143 1.39701 0.555995 1.07379 0.814941 0.814819C1.07395 0.555856 1.39709 0.369002 1.75049 0.272827C2.10399 0.176788 2.478 0.174781 2.83252 0.266968C3.1869 0.359309 3.51216 0.544079 3.77393 0.800171C4.03541 1.0564 4.22586 1.37764 4.32568 1.72986L4.3667 1.87537H13.6323L13.6733 1.72986C13.7731 1.37755 13.9636 1.05646 14.2251 0.800171C14.4869 0.543853 14.812 0.35943 15.1665 0.266968ZM14.4722 17.6459C14.4601 17.6383 14.447 17.6312 14.4351 17.6234L14.313 17.5375C14.3642 17.576 14.418 17.6119 14.4722 17.6459ZM16.979 17.6097C16.972 17.6145 16.9646 17.6188 16.9575 17.6234C16.9643 17.619 16.9723 17.6142 16.979 17.6097ZM17.3247 17.3256C17.2305 17.4197 17.1282 17.5045 17.02 17.5804C17.0466 17.5615 17.0732 17.5427 17.0991 17.5228C17.1783 17.462 17.2537 17.3956 17.3247 17.3246V17.3256ZM0.674316 17.3246C0.694369 17.3446 0.716069 17.3639 0.736816 17.3832C0.716243 17.3641 0.694221 17.3455 0.674316 17.3256L0.571777 17.2162C0.604673 17.2534 0.639117 17.2894 0.674316 17.3246ZM2.30322 14.4496C1.97304 14.4498 1.6559 14.5814 1.42236 14.8148C1.18891 15.0483 1.05738 15.3655 1.05713 15.6957C1.05714 16.0258 1.18913 16.3439 1.42236 16.5775C1.65587 16.8108 1.97318 16.9416 2.30322 16.9418C2.6335 16.9418 2.95042 16.8109 3.18408 16.5775C3.41772 16.3438 3.54931 16.0261 3.54932 15.6957C3.54907 15.3655 3.41759 15.0483 3.18408 14.8148C2.95045 14.5816 2.63337 14.4496 2.30322 14.4496ZM15.6958 14.4496C15.3656 14.4498 15.0485 14.5814 14.8149 14.8148C14.5815 15.0483 14.45 15.3655 14.4497 15.6957C14.4497 16.0258 14.5817 16.3439 14.8149 16.5775C15.0484 16.8108 15.3657 16.9416 15.6958 16.9418C16.0263 16.9418 16.3439 16.8112 16.5776 16.5775C16.8113 16.3438 16.9419 16.0261 16.9419 15.6957C16.9416 15.3657 16.8109 15.0483 16.5776 14.8148C16.344 14.5816 16.026 14.4496 15.6958 14.4496ZM4.32568 2.87634C4.22716 3.22102 4.04204 3.53494 3.78857 3.78845C3.53504 4.04196 3.22119 4.22704 2.87646 4.32556L2.73096 4.36658V13.6322L2.87646 13.6732C3.22087 13.7716 3.53516 13.9572 3.78857 14.2103C4.04189 14.4636 4.22708 14.7781 4.32568 15.1224L4.3667 15.2679H13.6323L13.6733 15.1224C13.7719 14.778 13.9571 14.4637 14.2104 14.2103C14.4639 13.957 14.778 13.7718 15.1226 13.6732L15.2681 13.6322V4.36658L15.1226 4.32556C14.4231 4.12745 13.8711 3.57591 13.6733 2.87634L13.6323 2.73083H4.3667L4.32568 2.87634ZM3.04346 14.9554C3.06815 14.9801 3.09184 15.0067 3.11377 15.0336C3.09189 15.007 3.068 14.981 3.04346 14.9564C3.00282 14.9158 2.95887 14.879 2.9126 14.8461C2.95876 14.8788 3.00287 14.9149 3.04346 14.9554ZM16.436 14.9554C16.4607 14.9801 16.4844 15.0067 16.5063 15.0336C16.4845 15.007 16.4606 14.981 16.436 14.9564C16.4117 14.9321 16.3862 14.9077 16.3599 14.8861C16.3862 14.9076 16.4118 14.9312 16.436 14.9554ZM15.0776 14.8529C15.09 14.8439 15.102 14.834 15.1147 14.8256C15.1431 14.8066 15.1726 14.7889 15.2026 14.7728H15.2036C15.1599 14.7961 15.1179 14.8234 15.0776 14.8529ZM17.6235 14.4359C17.6264 14.4403 17.6295 14.4452 17.6323 14.4496C17.6024 14.4032 17.5708 14.3571 17.5376 14.3129L17.6235 14.4359ZM9.3501 5.90076C9.59806 5.90097 9.80002 6.10159 9.80029 6.34998V8.55017H12.0015C12.2492 8.55091 12.4504 8.75253 12.4507 9.00037C12.4502 9.24784 12.2493 9.44884 12.0015 9.44958H9.80029V11.6508C9.79984 11.899 9.59795 12.0998 9.3501 12.1C9.10208 12.0997 8.90133 11.8987 8.90088 11.6508V9.44958H6.70068C6.45228 9.44931 6.25165 9.24738 6.25146 8.99939C6.252 8.75148 6.45271 8.55044 6.70068 8.55017H8.90088V6.34998C8.90115 6.10184 9.10196 5.90103 9.3501 5.90076ZM2.30322 1.05701C1.97301 1.0572 1.6559 1.18877 1.42236 1.42224C1.18889 1.65576 1.05736 1.97291 1.05713 2.3031C1.05716 2.63323 1.18915 2.95033 1.42236 3.18396C1.65591 3.41747 1.97299 3.549 2.30322 3.54919C2.6337 3.54917 2.95038 3.41762 3.18408 3.18396C3.41771 2.95027 3.54929 2.63354 3.54932 2.3031C3.54909 1.97291 3.41756 1.65576 3.18408 1.42224C2.95045 1.18902 2.63337 1.05703 2.30322 1.05701ZM15.6958 1.05701C15.3656 1.05722 15.0485 1.18878 14.8149 1.42224C14.5815 1.65575 14.4499 1.97294 14.4497 2.3031C14.4497 2.63323 14.5817 2.95033 14.8149 3.18396C15.0485 3.41749 15.3656 3.54898 15.6958 3.54919C16.0263 3.54919 16.3439 3.41764 16.5776 3.18396C16.811 2.95031 16.9419 2.63335 16.9419 2.3031C16.9417 1.9731 16.8109 1.65572 16.5776 1.42224C16.344 1.189 16.026 1.05701 15.6958 1.05701ZM17.9585 2.73669C17.9491 2.78553 17.9379 2.83487 17.9253 2.88318C17.9247 2.88534 17.9239 2.88786 17.9233 2.89001C17.924 2.88763 17.9247 2.88457 17.9253 2.8822C17.9379 2.83377 17.9491 2.78468 17.9585 2.73572V2.73669ZM3.27002 2.70447C3.24404 2.7674 3.2123 2.8283 3.17432 2.88513V2.88416C3.19332 2.85569 3.21101 2.82637 3.22705 2.79626C3.24303 2.76624 3.25703 2.73487 3.27002 2.70349V2.70447ZM3.04346 1.56287C3.06815 1.58756 3.09184 1.61408 3.11377 1.64099C3.09189 1.6144 3.068 1.58842 3.04346 1.56384C3.00283 1.52327 2.95888 1.48647 2.9126 1.45349C2.95876 1.48621 3.00287 1.52237 3.04346 1.56287ZM16.3599 1.49353C16.3862 1.51504 16.4118 1.53866 16.436 1.56287C16.4607 1.58756 16.4844 1.61408 16.5063 1.64099C16.4845 1.6144 16.4606 1.58842 16.436 1.56384C16.4006 1.52843 16.3625 1.49598 16.3228 1.46619C16.3351 1.47523 16.348 1.4838 16.3599 1.49353ZM16.2769 1.43298C16.2894 1.44137 16.3016 1.4515 16.314 1.46033C16.274 1.43101 16.2323 1.40339 16.189 1.38025C16.2189 1.39617 16.2485 1.41416 16.2769 1.43298ZM0.674316 0.674194C0.603492 0.745026 0.537782 0.820879 0.477051 0.89978L0.38916 1.02087C0.444491 0.93828 0.504975 0.858622 0.570801 0.783569L0.674316 0.674194Z"
        fill="currentColor"
      />
    </svg>
  );
}
